import {
  Activity,
  ArrowRight,
  BarChart3,
  Blocks,
  BookOpen,
  Bot,
  Braces,
  Building2,
  Check,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  Command,
  Cpu,
  CreditCard,
  Copy,
  Database,
  Download,
  Eye,
  ExternalLink,
  FileCode2,
  FileText,
  FolderKanban,
  Gauge,
  Github,
  KeyRound,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  Menu,
  MessageSquareText,
  Moon,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  Play,
  Plus,
  Pencil,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  TerminalSquare,
  Trash2,
  Users,
  UserPlus,
  WandSparkles,
  Wrench,
  X
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Redirect, Route, Switch, useLocation } from "wouter";
import type { AkResponse } from "@ak/contracts";
import { api, apiStream, ApiError, formatDate, formatLatency, formatNumber } from "./api";
import { MarkdownContent } from "./MarkdownContent";
import { PublicDocumentation } from "./PublicDocumentation";
import {
  ConfirmModal,
  ErrorState,
  FormModal,
  Modal,
  ToastProvider,
  useToast
} from "./ui";

type User = {
  userId: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: "user" | "support" | "admin";
  planId: string;
  planModes: string[];
  planFeatures: string[];
};

type MeResponse = { user: User };

type ProviderKeyUsage = {
  requests_total: number;
  requests_today: number;
  tokens_total: number;
  errors_total: number;
};

type ProviderKeyItem = {
  id: string;
  provider: string;
  label: string;
  hint: string;
  status: "active" | "disabled";
  last_used_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  over_limit: boolean;
  usage: ProviderKeyUsage;
};

type ProviderKeySettingsResponse = {
  plan_id: string;
  limit_per_provider: number | null;
  providers: Array<{
    id: string;
    name: string;
    description: string;
    configured: number;
    model_count: number;
    can_add: boolean;
  }>;
  keys: ProviderKeyItem[];
};

type UserModelEndpointItem = {
  id: string;
  model_alias: string;
  slug: string;
  name: string;
  description: string;
  tier: "fast" | "balanced" | "reasoning";
  priority: number;
  status: "active" | "disabled";
  config: {
    url?: string;
    method?: "GET" | "POST";
    header_names?: string[];
    headers?: Record<string, string>;
    request_template?: string;
    response_path?: string;
    timeout_ms?: number;
    allow_private_network?: boolean;
  };
  usage: {
    requests_total: number;
    input_tokens_total: number;
    output_tokens_total: number;
    errors_total: number;
  };
  last_used_at: string | null;
  last_test_at: string | null;
  last_test_latency_ms: number | null;
  last_error: string | null;
};

type UserModelEndpointResponse = {
  plan_id: string;
  limit: number | null;
  endpoints: UserModelEndpointItem[];
};

const dynamicTemplateFakeValues: Record<string, string> = {
  messages: "[]",
  history: "[]",
  prompt: '"test"',
  system: '"test"',
  model: '"test"',
  temperature: "0",
  max_tokens: "0"
};

function validateDynamicJsonTemplate(content: string): void {
  if (!content.trim()) return;
  const names = Object.keys(dynamicTemplateFakeValues).join("|");
  const quoted = new RegExp(`"\\s*\\{\\{\\s*(${names})\\s*\\}\\}\\s*"`, "g");
  const quotedNormalized = content.replace(quoted, (_match, name: string) => dynamicTemplateFakeValues[name] || "null");
  const unquoted = new RegExp(`\\{\\{\\s*(${names})\\s*\\}\\}`, "g");
  const normalized = quotedNormalized.replace(unquoted, (match, name: string, offset: number, complete: string) => {
    const before = complete.slice(0, offset).trimEnd().at(-1);
    const after = complete.slice(offset + match.length).trimStart().at(0);
    const standalone = [":", "[", ","].includes(before || "") && [",", "]", "}"].includes(after || "");
    return standalone ? dynamicTemplateFakeValues[name] || "null" : match;
  });
  JSON.parse(normalized);
}

const productNav = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/studio", label: "AK Studio", icon: WandSparkles },
  { to: "/memory", label: "Memoria", icon: Database },
  { to: "/artifacts", label: "Artefactos", icon: Blocks },
  { to: "/jobs", label: "Generaciones", icon: Play }
];

const platformNav = [
  { to: "/models", label: "Orquestación", icon: Cpu },
  { to: "/connectors", label: "Herramientas", icon: Wrench },
  { to: "/skills", label: "Skills", icon: Sparkles },
  { to: "/api-keys", label: "API keys", icon: KeyRound }
];

const accountNav = [
  { to: "/settings", label: "Configuración", icon: Settings },
  { to: "/operations", label: "Operaciones", icon: Network },
  { to: "/usage", label: "Uso y analítica", icon: BarChart3 },
  { to: "/plans", label: "Plan y facturación", icon: CreditCard },
  { to: "/docs", label: "Documentación", icon: BookOpen }
];

function userHasFeature(user: User, feature: string): boolean {
  return user.role === "admin" || user.planFeatures.includes("all") || user.planFeatures.includes(feature);
}

function NavLink({
  to,
  className,
  children,
  onClick,
  title
}: {
  to: string;
  className?: string | ((state: { isActive: boolean }) => string);
  children: ReactNode;
  onClick?: () => void;
  title?: string;
}) {
  const [location] = useLocation();
  const isActive = location === to || (to !== "/dashboard" && location.startsWith(`${to}/`));
  const resolvedClassName = typeof className === "function" ? className({ isActive }) : className;
  return <Link href={to} className={resolvedClassName} onClick={onClick} title={title}>{children}</Link>;
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="brand">
      <div className="brand-mark">AK</div>
      {!compact && (
        <div className="brand-copy">
          <strong>AK-v5.0</strong>
          <span>AI Platform</span>
        </div>
      )}
    </div>
  );
}

function StatusPage() {
  const status = useQuery({
    queryKey: ["public-status"],
    queryFn: () => api<any>("/api/v1/status"),
    refetchInterval: 30_000,
    retry: 2
  });
  const components = status.data?.components || [];
  const groups: string[] = [...new Set<string>(components.map((item: any) => String(item.group)))];
  const overall = status.error ? "outage" : status.data?.status || "degraded";
  const labels: Record<string, string> = {
    operational: "Todos los sistemas operativos",
    degraded: "Algunos sistemas presentan incidencias",
    outage: "Hay servicios no disponibles"
  };
  const statusLabels: Record<string, string> = {
    operational: "Operativo",
    degraded: "Degradado",
    outage: "Interrupción"
  };
  return (
    <div className="status-page">
      <header className="status-header">
        <a href="/" aria-label="Inicio"><Brand /></a>
        <div className="inline-actions">
          <a className="button button-ghost" href="/docs">Documentación</a>
          <a className="button button-secondary" href="/dashboard">Dashboard</a>
        </div>
      </header>
      <main className="status-main">
        <div className="status-kicker">Estado de AK-v5.0</div>
        <section className={`status-hero status-${overall}`}>
          <div className="status-hero-icon"><Activity size={24} /></div>
          <div>
            <h1>{labels[overall]}</h1>
            <p>{status.error ? (status.error as Error).message : "Supervisión en tiempo real de la API, aplicaciones y servicios esenciales."}</p>
          </div>
          <button className="button button-secondary" disabled={status.isFetching} onClick={() => status.refetch()}>
            <RefreshCw className={status.isFetching ? "spin" : ""} size={15} />
            Actualizar
          </button>
        </section>
        {status.isLoading && <LoadingBlock />}
        {!status.isLoading && groups.map((group) => (
          <section className="status-group" key={group}>
            <div className="status-group-title"><h2>{group}</h2><span>{components.filter((item: any) => item.group === group).length} componentes</span></div>
            <div className="status-component-list">
              {components.filter((item: any) => item.group === group).map((item: any) => (
                <article className="status-component" key={item.id}>
                  <span className={`status-service-dot status-${item.status}`} />
                  <div><strong>{item.name}</strong><p>{item.detail}</p></div>
                  {item.latency_ms !== undefined && <code>{formatLatency(item.latency_ms)}</code>}
                  <span className={`status-label status-${item.status}`}>{statusLabels[item.status]}</span>
                </article>
              ))}
            </div>
          </section>
        ))}
        <footer className="status-footer">
          <span>Última comprobación: {status.data?.updated_at ? formatDate(status.data.updated_at) : "—"}</span>
          <span>Versión {status.data?.version || "5.0.0"}</span>
        </footer>
      </main>
    </div>
  );
}

function Landing() {
  const authFailed = new URLSearchParams(window.location.search).get("auth") === "failed";
  return (
    <div className="landing">
      <header className="landing-header">
        <Brand />
        <div className="inline-actions">
          <a className="button button-ghost" href="/docs"><BookOpen size={15} /> Documentación</a>
          <a className="button button-ghost" href="/status"><Activity size={15} /> Estado</a>
          <a className="button button-secondary" href="/auth/google">Iniciar sesión</a>
        </div>
      </header>
      <main className="landing-main">
        {authFailed && <div className="security-callout"><ShieldCheck size={20} /><div><strong>No se pudo completar el inicio de sesión.</strong><p>Vuelve a intentarlo; si continúa, verifica la configuración OAuth del dominio.</p></div></div>}
        <div className="landing-proof">
          <span className="status-dot" />
          Orquestación distribuida lista para producción
        </div>
        <h1>Una plataforma de IA.<br />Dos formas de trabajar.</h1>
        <p className="landing-lede">
          Empieza con el contrato profesional compatible con OpenAI o utiliza el modo
          fácil cuando necesites personalidad, contexto e identidad guiada.
        </p>
        <div className="landing-actions">
          <a className="button button-primary button-large" href="/auth/google">
            Continuar con Google <ArrowRight size={17} />
          </a>
          <a className="button button-ghost button-large" href="#contracts">
            Explorar contratos
          </a>
        </div>
        <div className="contract-grid" id="contracts">
          <article className="contract-card featured">
            <div className="contract-meta">
              <span className="icon-box"><Braces size={19} /></span>
              <span className="badge badge-accent">Predeterminado</span>
            </div>
            <h2>Professional</h2>
            <p>Mensajes, tools y streaming con el contrato que ya conoce tu equipo.</p>
            <pre><code>{`{
  "model": "ak-auto",
  "messages": [
    {"role": "user", "content": "Hola"}
  ],
  "stream": true
}`}</code></pre>
          </article>
          <article className="contract-card">
            <div className="contract-meta">
              <span className="icon-box"><Command size={19} /></span>
              <span className="badge">Guiado</span>
            </div>
            <h2>Easy</h2>
            <p>Una entrada simple con identidad, contexto, personalidad e idioma.</p>
            <pre><code>{`{
  "prompt": "Hola",
  "context": "Asistente comercial",
  "personality": "Directo y cordial",
  "genero": "neutral"
}`}</code></pre>
          </article>
        </div>
        <div className="landing-features">
          <span><ShieldCheck size={17} /> API keys hasheadas</span>
          <span><Gauge size={17} /> Ruta rápida real</span>
          <span><Network size={17} /> Fallback por salud</span>
          <span><Database size={17} /> MySQL nativo</span>
        </div>
      </main>
    </div>
  );
}

function Sidebar({
  user,
  collapsed,
  mobileOpen,
  onCollapse,
  onClose
}: {
  user: User;
  collapsed: boolean;
  mobileOpen: boolean;
  onCollapse: () => void;
  onClose: () => void;
}) {
  const groups = [
    { label: "Producto", items: productNav },
    { label: "Plataforma", items: platformNav },
    { label: "Cuenta", items: accountNav }
  ];
  return (
    <>
      {mobileOpen && <button className="mobile-scrim" onClick={onClose} aria-label="Cerrar menú" />}
      <aside className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-top">
          <Brand compact={collapsed} />
          <button className="icon-button collapse-button" onClick={onCollapse} aria-label={collapsed ? "Expandir navegación" : "Contraer navegación"}>
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
          <button className="icon-button mobile-close" onClick={onClose} aria-label="Cerrar navegación">
            <X size={18} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {groups.map((group) => (
            <div className="nav-group" key={group.label}>
              {!collapsed && <div className="nav-label">{group.label}</div>}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                >
                  <item.icon size={18} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
          {user.role === "admin" && (
            <div className="nav-group">
              {!collapsed && <div className="nav-label">Administración</div>}
              <NavLink
                to="/admin"
                onClick={onClose}
                title={collapsed ? "Panel admin" : undefined}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <ShieldCheck size={18} />
                {!collapsed && <span>Panel admin</span>}
              </NavLink>
            </div>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="plan-chip">
            <Sparkles size={16} />
            {!collapsed && <div><strong>{user.planId}</strong><span>Plan actual</span></div>}
          </div>
        </div>
      </aside>
    </>
  );
}

function Header({
  user,
  onMobileMenu,
  onTheme
}: {
  user: User;
  onMobileMenu: () => void;
  onTheme: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [userOpen, setUserOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(() => localStorage.getItem("ak_active_workspace") || "");
  const [activeProjectId, setActiveProjectId] = useState(() => localStorage.getItem("ak_active_project") || "");
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const workspaceMenuRef = useRef<HTMLDivElement | null>(null);
  const workspaces = useQuery({ queryKey: ["workspaces"], queryFn: () => api<any[]>("/api/v1/workspaces") });
  const activeWorkspace = workspaces.data?.find((workspace) => workspace.id === activeWorkspaceId);
  const workspaceDetail = useQuery({
    queryKey: ["workspace-detail", activeWorkspaceId],
    queryFn: () => api<any>(`/api/v1/workspaces/${activeWorkspaceId}`),
    enabled: Boolean(activeWorkspaceId && activeWorkspace)
  });
  const activeProject = workspaceDetail.data?.projects?.find((project: any) => project.id === activeProjectId);
  const changeContext = useCallback((workspaceId: string, projectId = "") => {
    if (workspaceId) localStorage.setItem("ak_active_workspace", workspaceId);
    else localStorage.removeItem("ak_active_workspace");
    if (workspaceId && projectId) localStorage.setItem("ak_active_project", projectId);
    else localStorage.removeItem("ak_active_project");
    setActiveWorkspaceId(workspaceId);
    setActiveProjectId(projectId);
    setWorkspaceOpen(false);
    window.dispatchEvent(new CustomEvent("ak:workspace-changed", { detail: { workspaceId, projectId } }));
    queryClient.invalidateQueries({ predicate: (query) => !["me", "workspaces", "workspace-detail"].includes(String(query.queryKey[0])) });
  }, [queryClient]);
  useEffect(() => {
    const syncContext = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId?: string; projectId?: string }>).detail || {};
      setActiveWorkspaceId(detail.workspaceId || "");
      setActiveProjectId(detail.projectId || "");
    };
    window.addEventListener("ak:workspace-changed", syncContext);
    return () => window.removeEventListener("ak:workspace-changed", syncContext);
  }, []);
  useEffect(() => {
    if (!workspaces.data) return;
    if (activeWorkspaceId && !activeWorkspace) changeContext("");
  }, [workspaces.data, activeWorkspaceId, activeWorkspace, changeContext]);
  useEffect(() => {
    if (!activeWorkspaceId || !workspaceDetail.data?.projects) return;
    const projects = workspaceDetail.data.projects.filter((project: any) => project.status === "active");
    if (!projects.length) {
      if (activeProjectId) changeContext(activeWorkspaceId, "");
      return;
    }
    if (!projects.some((project: any) => project.id === activeProjectId)) changeContext(activeWorkspaceId, projects[0].id);
  }, [activeWorkspaceId, activeProjectId, workspaceDetail.data, changeContext]);
  const logout = useMutation({
    mutationFn: () => api("/api/v1/auth/logout", { method: "POST" }),
    onSuccess: () => {
      setUserOpen(false);
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  useEffect(() => {
    if (!userOpen && !workspaceOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !userMenuRef.current?.contains(event.target)) {
        setUserOpen(false);
      }
      if (event.target instanceof Node && !workspaceMenuRef.current?.contains(event.target)) {
        setWorkspaceOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setUserOpen(false);
        setWorkspaceOpen(false);
        userMenuRef.current?.querySelector<HTMLButtonElement>(".user-menu-trigger")?.focus();
      }
    };
    window.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [userOpen, workspaceOpen]);
  return (
    <header className="app-header">
      <button className="icon-button mobile-menu" onClick={onMobileMenu} aria-label="Abrir navegación">
        <Menu size={19} />
      </button>
      <div className="header-context">
        <div className="workspace-switcher" ref={workspaceMenuRef}>
          <button className="workspace-trigger" type="button" onClick={() => { setWorkspaceOpen((value) => !value); setUserOpen(false); }} aria-expanded={workspaceOpen}>
            <span className="workspace-avatar" style={activeWorkspace?.brand_color ? { background: activeWorkspace.brand_color } : undefined}>{activeWorkspace ? <Building2 size={16} /> : user.name.slice(0, 1)}</span>
            <span><small>{activeWorkspace ? "Workspace" : "Perfil personal"}</small><strong>{activeWorkspace?.name || user.name}</strong></span>
            <ChevronDown size={14} />
          </button>
          {workspaceOpen && <div className="workspace-dropdown">
            <div className="workspace-dropdown-title"><span>Contexto de trabajo</span><NavLink to="/operations" onClick={() => setWorkspaceOpen(false)}>Administrar</NavLink></div>
            <button className={!activeWorkspaceId ? "active" : ""} onClick={() => changeContext("")}><span className="workspace-list-icon personal">{user.name.slice(0, 1)}</span><span><strong>Perfil personal</strong><small>Datos y recursos privados</small></span>{!activeWorkspaceId && <Check size={15} />}</button>
            {workspaces.data?.map((workspace) => <button key={workspace.id} className={workspace.id === activeWorkspaceId ? "active" : ""} onClick={() => changeContext(workspace.id)}><span className="workspace-list-icon" style={{ background: workspace.brand_color || undefined }}><Building2 size={14} /></span><span><strong>{workspace.name}</strong><small>{workspace.role} · {workspace.member_count} miembros</small></span>{workspace.id === activeWorkspaceId && <Check size={15} />}</button>)}
            {workspaces.isLoading && <span className="workspace-dropdown-loading">Cargando organizaciones…</span>}
          </div>}
        </div>
        {activeWorkspace && <>
          <span className="header-separator" />
          <label className="project-switcher"><FolderKanban size={14} /><select aria-label="Proyecto activo" value={activeProjectId} onChange={(event) => changeContext(activeWorkspaceId, event.target.value)}>{workspaceDetail.data?.projects?.filter((project: any) => project.status === "active").map((project: any) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></label>
        </>}
      </div>
      <div className="header-actions">
        <button className="icon-button" onClick={onTheme} aria-label="Cambiar tema">
          <Sun className="theme-sun" size={18} />
          <Moon className="theme-moon" size={18} />
        </button>
        <div className="user-menu" ref={userMenuRef}>
          <button type="button" className="user-menu-trigger" onClick={() => setUserOpen((value) => !value)} aria-expanded={userOpen} aria-haspopup="menu">
            <span className="avatar">
              {user.avatarUrl ? <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" /> : user.name.slice(0, 1)}
            </span>
            <span className="user-copy"><strong>{user.name}</strong><span>{user.email}</span></span>
            <ChevronDown size={15} />
          </button>
          {userOpen && (
            <div className="user-dropdown" role="menu">
              <div className="user-dropdown-meta"><span>Plan actual</span><strong>{user.planId}</strong></div>
              <button type="button" role="menuitem" onClick={() => logout.mutate()} disabled={logout.isPending}>
                <LogOut size={16} /> {logout.isPending ? "Cerrando…" : "Cerrar sesión"}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function PageHeader({
  eyebrow,
  title,
  description,
  actions
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  detail,
  tone = "neutral"
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
  tone?: "neutral" | "positive" | "accent";
}) {
  return (
    <article className={`metric-card tone-${tone}`}>
      <div className="metric-top"><span className="metric-icon">{icon}</span></div>
      <span className="metric-label">{label}</span>
      <strong className="metric-value">{value}</strong>
      <span className="metric-detail">{detail}</span>
    </article>
  );
}

function EmptyState({ icon, title, description, action }: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">{icon}</span>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

function LoadingBlock() {
  return <div className="loading-block"><span /><span /><span /></div>;
}

type ToastWriter = (message: string, tone?: "success" | "error" | "info") => void;

async function copyToClipboard(text: string, toast: ToastWriter, successMessage = "Contenido copiado."): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(successMessage, "success");
  } catch {
    toast("No se pudo acceder al portapapeles.", "error");
  }
}

function UiBlockRenderer({ block }: { block: AkResponse["ui_blocks"][number] }) {
  const payload = block.payload || {};
  const items = Array.isArray(payload.items)
    ? payload.items
    : Array.isArray(payload.tasks)
      ? payload.tasks
      : Array.isArray(payload.options)
        ? payload.options
        : Array.isArray(payload.questions)
          ? payload.questions
          : Array.isArray(payload.slots)
            ? payload.slots
            : [];
  const labelFor = (item: unknown, index: number) => {
    if (typeof item === "string" || typeof item === "number") return String(item);
    if (!item || typeof item !== "object") return `Opción ${index + 1}`;
    const value = item as Record<string, unknown>;
    return String(value.label || value.title || value.text || value.question || value.name || `Opción ${index + 1}`);
  };
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [consent, setConsent] = useState<"accepted" | "rejected" | null>(null);
  const toggleChecked = (index: number) => {
    setCheckedItems((current) => current.includes(index)
      ? current.filter((item) => item !== index)
      : [...current, index]);
  };
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  const explicitColumns = Array.isArray(payload.columns) ? payload.columns.map(String) : [];
  const columns = explicitColumns.length
    ? explicitColumns
    : Array.isArray(rows[0])
      ? rows[0].map((_value, index) => `Columna ${index + 1}`)
      : rows[0] && typeof rows[0] === "object"
        ? Object.keys(rows[0] as Record<string, unknown>)
        : [];
  if (block.type === "table" && rows.length) {
    const gridStyle = { gridTemplateColumns: `repeat(${Math.max(1, columns.length)}, minmax(120px, 1fr))` };
    return <article className="structured-card"><strong>{block.title || "Tabla"}</strong><div className="table"><div className="table-head" style={gridStyle}>{columns.map((column) => <span key={column}>{column}</span>)}</div>{rows.map((row, index) => <div className="table-row" style={gridStyle} key={index}>{(Array.isArray(row) ? row : columns.map((column) => (row as Record<string, unknown>)?.[column])).map((value, cell) => <span key={cell}>{String(value ?? "—")}</span>)}</div>)}</div></article>;
  }
  if (block.type === "code") {
    return <article className="structured-card"><strong>{block.title || String(payload.language || "Código")}</strong><pre>{String(payload.code || payload.content || "")}</pre></article>;
  }
  if (block.type === "chart") {
    const values = (Array.isArray(payload.data) ? payload.data : items).map((item: any) => Number(item?.value ?? item ?? 0));
    const max = Math.max(1, ...values);
    return <article className="structured-card"><strong>{block.title || "Gráfico"}</strong><div className="chart-placeholder">{values.map((value, index) => <span key={index} title={String(value)} style={{ height: `${Math.max(4, value / max * 100)}%` }} />)}</div></article>;
  }
  if (block.type === "media" && typeof payload.url === "string" && /^https:\/\//.test(payload.url)) {
    return <article className="structured-card"><strong>{block.title || "Contenido multimedia"}</strong><a href={payload.url} target="_blank" rel="noreferrer"><img src={payload.url} alt={String(payload.alt || block.title || "Contenido generado")} /></a></article>;
  }
  if ((block.type === "choices" || block.type === "poll" || block.type === "schedule") && items.length) {
    return (
      <article className="structured-card">
        <strong>{block.title || (block.type === "schedule" ? "Selecciona un horario" : "Selecciona una opción")}</strong>
        <div className="widget-options" role="group" aria-label={block.title || block.type}>
          {items.map((item, index) => (
            <button
              type="button"
              key={index}
              className={selectedIndex === index ? "active" : ""}
              aria-pressed={selectedIndex === index}
              onClick={() => setSelectedIndex(index)}
            >
              {labelFor(item, index)}
            </button>
          ))}
        </div>
        {selectedIndex !== null && <span className="widget-feedback" role="status">Seleccionado: {labelFor(items[selectedIndex], selectedIndex)}</span>}
      </article>
    );
  }
  if (block.type === "checklist" && items.length) {
    return (
      <article className="structured-card">
        <strong>{block.title || "Checklist"}</strong>
        <div className="widget-checklist">
          {items.map((item, index) => (
            <label key={index}>
              <input type="checkbox" checked={checkedItems.includes(index)} onChange={() => toggleChecked(index)} />
              <span>{labelFor(item, index)}</span>
            </label>
          ))}
        </div>
        <span className="widget-feedback" role="status">{checkedItems.length} de {items.length} completados</span>
      </article>
    );
  }
  if (block.type === "consent") {
    const acceptLabel = String(payload.accept_label || payload.acceptLabel || "Aceptar");
    const rejectLabel = String(payload.reject_label || payload.rejectLabel || "Rechazar");
    return (
      <article className="structured-card">
        <strong>{block.title || "Confirmación requerida"}</strong>
        {payload.description !== undefined && <p>{String(payload.description)}</p>}
        <div className="widget-options" role="group" aria-label={block.title || "Consentimiento"}>
          <button type="button" className={consent === "accepted" ? "active" : ""} aria-pressed={consent === "accepted"} onClick={() => setConsent("accepted")}>{acceptLabel}</button>
          <button type="button" className={consent === "rejected" ? "active danger" : "danger"} aria-pressed={consent === "rejected"} onClick={() => setConsent("rejected")}>{rejectLabel}</button>
        </div>
        {consent && <span className="widget-feedback" role="status">{consent === "accepted" ? "Aceptado" : "Rechazado"}</span>}
      </article>
    );
  }
  if (block.type === "kanban") {
    const rawColumns = Array.isArray(payload.columns) ? payload.columns : [];
    const columnsData = rawColumns.length && rawColumns.some((column) => column && typeof column === "object")
      ? rawColumns
      : [{ title: block.title || "Tareas", items }];
    let taskOffset = 0;
    return (
      <article className="structured-card">
        <strong>{block.title || "Kanban"}</strong>
        <div className="widget-kanban">
          {columnsData.map((column, columnIndex) => {
            const data: Record<string, unknown> = column && typeof column === "object" ? column as Record<string, unknown> : { title: column };
            const tasks: unknown[] = Array.isArray(data.items) ? data.items : Array.isArray(data.tasks) ? data.tasks : Array.isArray(data.cards) ? data.cards : [];
            const base = taskOffset;
            taskOffset += tasks.length;
            return (
              <section key={columnIndex} aria-label={String(data.title || data.name || `Columna ${columnIndex + 1}`)}>
                <h4>{String(data.title || data.name || `Columna ${columnIndex + 1}`)}</h4>
                {tasks.map((task, taskIndex) => {
                  const key = base + taskIndex;
                  return <label key={taskIndex}><input type="checkbox" checked={checkedItems.includes(key)} onChange={() => toggleChecked(key)} /><span>{labelFor(task, taskIndex)}</span></label>;
                })}
              </section>
            );
          })}
        </div>
      </article>
    );
  }
  if (items.length) {
    return <article className="structured-card"><strong>{block.title || block.type}</strong><ul>{items.map((item: any, index) => <li key={index}><Check size={14} /> {typeof item === "string" ? item : String(item.label || item.title || item.text || item.question || JSON.stringify(item))}</li>)}</ul></article>;
  }
  return <article className="structured-card"><strong>{block.title || block.type}</strong><pre>{JSON.stringify(payload, null, 2)}</pre></article>;
}

function Overview() {
  const [, navigate] = useLocation();
  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: () => api<any>("/api/v1/dashboard/overview")
  });
  const usage = useQuery({
    queryKey: ["usage"],
    queryFn: () => api<any>("/api/v1/usage")
  });
  const readiness = useQuery({
    queryKey: ["readiness"],
    queryFn: () => api<any>("/health/ready"),
    refetchInterval: 30_000
  });
  if (overview.isLoading) return <LoadingBlock />;
  if (overview.error) return <ErrorState error={overview.error} onRetry={() => overview.refetch()} />;
  const metrics = overview.data?.metrics || {};
  const daily = usage.data?.daily || [];
  const maxRequests = Math.max(1, ...daily.map((item: any) => Number(item.requests || 0)));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Workspace"
        title={`Buenos días, ${overview.data?.user?.name?.split(" ")[0] || "equipo"}`}
        description="Rendimiento, consumo y actividad reciente de tu plataforma."
        actions={<button className="button button-primary" onClick={() => navigate("/studio")}><Play size={16} /> Abrir Studio</button>}
      />
      <section className="metric-grid">
        <MetricCard icon={<Activity size={19} />} label="Solicitudes" value={formatNumber(metrics.requests || 0)} detail="Últimos 30 días" />
        <MetricCard icon={<Clock3 size={19} />} label="Latencia media" value={formatLatency(metrics.avg_latency_ms)} detail="Todas las rutas" tone="accent" />
        <MetricCard icon={<Gauge size={19} />} label="Tasa de éxito" value={`${metrics.success_rate ?? 100}%`} detail="Respuestas completadas" tone="positive" />
        <MetricCard icon={<Braces size={19} />} label="Tokens" value={formatNumber((metrics.input_tokens || 0) + (metrics.output_tokens || 0))} detail={`${formatNumber(metrics.credits || 0)} créditos`} />
      </section>
      <section className="overview-grid">
        <article className="panel chart-panel">
          <div className="panel-header">
            <div><h2>Actividad de API</h2><p>Solicitudes procesadas durante el periodo.</p></div>
            <span className="badge">30 días</span>
          </div>
          {usage.error ? <ErrorState error={usage.error} onRetry={() => usage.refetch()} /> : usage.isLoading ? <LoadingBlock /> : daily.length ? <>
            <div className="chart-placeholder">
              {daily.map((item: any) => (
                <span key={String(item.day)} title={`${item.day}: ${item.requests} solicitudes`} style={{ height: `${Math.max(5, Number(item.requests || 0) / maxRequests * 100)}%` }} />
              ))}
            </div>
            <div className="chart-axis"><span>{String(daily[0]?.day || "").slice(0, 10)}</span><span>{String(daily.at(-1)?.day || "").slice(0, 10)}</span></div>
          </> : <EmptyState icon={<BarChart3 />} title="Sin actividad todavía" description="Las métricas aparecerán después de la primera solicitud." />}
        </article>
        <article className="panel health-panel">
          <div className="panel-header"><div><h2>Estado del sistema</h2><p>Servicios críticos</p></div><span className={`badge ${readiness.data?.status === "ready" ? "badge-success" : ""}`}>{readiness.error ? "No disponible" : readiness.data?.status === "ready" ? "Operativo" : "Verificando"}</span></div>
          {[
            ["API principal", readiness.error ? "Sin respuesta" : readiness.data ? "Activa" : "Verificando", readiness.error ? "pending" : readiness.data ? "healthy" : "pending"],
            ["Base de datos", readiness.error ? "Sin confirmar" : readiness.data?.checks?.database ? "Conectada" : "Verificando", readiness.data?.checks?.database ? "healthy" : "pending"],
            ["Memorias", formatNumber(metrics.threads || 0), "healthy"],
            ["API keys", formatNumber(metrics.api_keys || 0), "healthy"]
          ].map(([label, value, status]) => (
            <div className="health-row" key={label}>
              <span className={`health-indicator ${status}`} />
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </article>
      </section>
      <section className="panel">
        <div className="panel-header">
          <div><h2>Memorias recientes</h2><p>Contexto persistente generado desde Studio y API.</p></div>
          <button className="button button-ghost" onClick={() => navigate("/memory")}>Ver todas <ArrowRight size={15} /></button>
        </div>
        {overview.data?.recent_threads?.length ? (
          <div className="table">
            <div className="table-head"><span>Memoria</span><span>Contrato</span><span>Ruta</span><span>Actualizada</span></div>
            {overview.data.recent_threads.map((thread: any) => (
              <button className="table-row" key={thread.id} onClick={() => navigate(`/memory/${thread.id}`)}>
                <span className="cell-primary"><MessageSquareText size={16} /> {thread.title}</span>
                <span><span className="badge">{thread.request_mode}</span></span>
                <span className="mono">{thread.model_alias || "auto"}</span>
                <span>{formatDate(thread.updated_at)}</span>
              </button>
            ))}
          </div>
        ) : <EmptyState icon={<Database />} title="Aún no hay memoria" description="Abre Studio y guarda tu primera sesión." />}
      </section>
    </div>
  );
}

function Studio({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [contract, setContract] = useState<"professional" | "easy">("professional");
  const [prompt, setPrompt] = useState("");
  const [context, setContext] = useState("Eres un asistente experto, preciso y orientado a resultados.");
  const [personality, setPersonality] = useState("Profesional, claro y cordial");
  const [name, setName] = useState("AK");
  const [gender, setGender] = useState("neutral");
  const [model, setModel] = useState("ak-auto");
  const [mode, setMode] = useState("auto");
  const [messages, setMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  const [responseMeta, setResponseMeta] = useState<AkResponse | null>(null);
  const [streamingText, setStreamingText] = useState("");
  const [error, setError] = useState("");
  const [threadId, setThreadId] = useState<string | null>(null);
  const [toolsEnabled, setToolsEnabled] = useState(true);
  const [memoryEnabled, setMemoryEnabled] = useState(true);
  const [structured, setStructured] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<"inspector" | "json">("inspector");
  const [toolModal, setToolModal] = useState(false);
  const [skillModal, setSkillModal] = useState(false);
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>(() => {
    const skill = new URLSearchParams(window.location.search).get("skill");
    return skill && /^[a-z0-9][a-z0-9_-]{1,119}$/i.test(skill) ? [skill] : [];
  });
  const [imageUrl, setImageUrl] = useState("");
  const [imageFileId, setImageFileId] = useState("");
  const [imageFileName, setImageFileName] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [artifactIds, setArtifactIds] = useState<string[]>(() => {
    const value = new URLSearchParams(window.location.search).get("artifact");
    return value && /^[0-9a-f-]{36}$/i.test(value) ? [value] : [];
  });
  const [attachmentDraft, setAttachmentDraft] = useState("");
  const [lastRequest, setLastRequest] = useState<Record<string, unknown> | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const [resumeId, setResumeId] = useState<string | null>(() => new URLSearchParams(window.location.search).get("thread"));
  const [resumeLoadedId, setResumeLoadedId] = useState<string | null>(null);
  const resumeApplied = useRef(false);
  const requestAbortRef = useRef<AbortController | null>(null);
  const resumedThread = useQuery({
    queryKey: ["thread", resumeId],
    queryFn: () => api<any>(`/api/v1/threads/${resumeId}`),
    enabled: Boolean(resumeId)
  });
  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api<any[]>("/api/v1/connectors"),
    enabled: toolModal
  });
  const studioBasicSkills = useQuery({
    queryKey: ["skills"],
    queryFn: () => api<any[]>("/api/v1/skills"),
    enabled: skillModal
  });
  const studioAgentSkills = useQuery({
    queryKey: ["agent-skills"],
    queryFn: () => api<any[]>("/api/v1/agent-skills"),
    enabled: skillModal
  });
  const customModels = useQuery({
    queryKey: ["user-model-endpoints"],
    queryFn: () => api<UserModelEndpointResponse>("/api/v1/user-model-endpoints")
  });

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, responseMeta, streamingText, error]);
  useEffect(() => {
    if (!prompt && composerRef.current) composerRef.current.style.height = "";
  }, [prompt]);
  useEffect(() => {
    if (mode !== "auto" && !user.planModes.includes(mode)) setMode("auto");
  }, [mode, user.planModes]);
  useEffect(() => {
    resumeApplied.current = false;
    setResumeLoadedId(null);
    if (resumeId) {
      setMessages([]);
      setResponseMeta(null);
      setStreamingText("");
      setThreadId(null);
      setError("");
      setLastRequest(null);
    }
  }, [resumeId]);
  useEffect(() => {
    if (!resumeId || !resumedThread.data || resumeApplied.current) return;
    resumeApplied.current = true;
    const restored = (resumedThread.data.messages || []).flatMap((message: any) => {
      if (message.role !== "user" && message.role !== "assistant") return [];
      const content = typeof message.content === "string" ? message.content : JSON.stringify(message.content);
      return [{ role: message.role, content }];
    });
    setMessages(restored);
    setThreadId(resumeId);
    setMemoryEnabled(true);
    setContract(resumedThread.data.thread?.request_mode === "easy" ? "easy" : "professional");
    if (resumedThread.data.thread?.model_alias) setModel(resumedThread.data.thread.model_alias);
    setResumeLoadedId(resumeId);
  }, [resumeId, resumedThread.data]);
  useEffect(() => () => requestAbortRef.current?.abort(), []);

  const resumeBlocked = Boolean(resumeId) && resumeLoadedId !== resumeId;

  const mutation = useMutation({
    mutationFn: async ({ body }: { text: string; body: Record<string, unknown> }) => {
      const controller = new AbortController();
      requestAbortRef.current = controller;
      try {
        return await apiStream<AkResponse>(
          contract === "professional" ? "/api/v1/responses" : "/api/v1/easy",
          body,
          (delta) => setStreamingText((current) => current + delta),
          controller.signal
        );
      } finally {
        if (requestAbortRef.current === controller) requestAbortRef.current = null;
      }
    },
    onMutate: ({ text, body }) => {
      setError("");
      setResponseMeta(null);
      setStreamingText("");
      setLastRequest(body);
      setMessages((current) => [...current, { role: "user", content: text }]);
      setPrompt("");
    },
    onSuccess: (response) => {
      setMessages((current) => [...current, { role: "assistant", content: response.output_text }]);
      setStreamingText("");
      setResponseMeta(response);
      if (memoryEnabled && response.thread_id) {
        setThreadId(response.thread_id);
        window.history.replaceState({}, "", `/studio?thread=${encodeURIComponent(response.thread_id)}`);
      }
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      queryClient.invalidateQueries({ queryKey: ["threads"] });
      queryClient.invalidateQueries({ queryKey: ["usage"] });
      if (response.artifacts.length) queryClient.invalidateQueries({ queryKey: ["artifacts"] });
      if (response.generations?.length) queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (requestError: Error, variables) => {
      const requestId = requestError instanceof ApiError && requestError.requestId ? ` · ID ${requestError.requestId}` : "";
      const cancelled = requestError instanceof ApiError && requestError.code === "request_cancelled";
      setError(cancelled ? "" : `${requestError.message}${requestId}`);
      setPrompt(variables.text);
      setMessages((current) => current.slice(0, -1));
      setStreamingText("");
      toast(requestError.message, cancelled ? "info" : "error");
    }
  });
  const studioLocked = resumeBlocked || mutation.isPending;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!prompt.trim() || mutation.isPending || resumeBlocked) return;
    const text = prompt.trim();
    const tools = toolsEnabled ? selectedTools : [];
    const professionalMessages: Array<Record<string, unknown>> = [
      ...(memoryEnabled ? messages : []).map((message) => ({ role: message.role, content: message.content })),
      {
        role: "user",
        content: imageUrl || imageFileId
          ? [{ type: "text", text }, imageFileId ? { type: "input_image", file_id: imageFileId } : { type: "image_url", image_url: imageUrl }]
          : text
      }
    ];
    let body: Record<string, unknown>;
    if (contract === "professional") {
      const defaults: Record<string, unknown> = {
        model,
        mode,
        tools,
        tool_choice: toolsEnabled ? "auto" : "none",
        skills: selectedSkills,
        metadata: { memory: memoryEnabled, ui_blocks: true },
        ...(memoryEnabled && threadId ? { thread_id: threadId } : {}),
        ...(structured ? { response_format: { type: "json_object" } } : {}),
        stream: true,
        artifact_ids: artifactIds
      };
      const looksJson = text.startsWith("{") || text.startsWith("[");
      if (looksJson) {
        let parsed: unknown;
        try {
          parsed = JSON.parse(text);
        } catch {
          toast("El JSON profesional no es válido.", "error");
          return;
        }
        if (Array.isArray(parsed) && parsed.every((item) => item && typeof item === "object" && "role" in item)) {
          body = { ...defaults, messages: parsed };
        } else if (parsed && !Array.isArray(parsed) && typeof parsed === "object" && ["messages", "input", "prompt"].some((key) => key in (parsed as Record<string, unknown>))) {
          const raw = parsed as Record<string, unknown>;
          body = {
            ...defaults,
            ...raw,
            metadata: {
              ...(defaults.metadata as Record<string, unknown>),
              ...(raw.metadata && typeof raw.metadata === "object" ? raw.metadata as Record<string, unknown> : {})
            },
            stream: true
          };
        } else {
          body = { ...defaults, input: parsed };
        }
      } else {
        body = { ...defaults, messages: professionalMessages };
      }
    } else {
      body = {
          request_mode: "easy",
          prompt: text,
          context,
          personality,
          name,
          genero: gender,
          mode,
          model,
          lang: "Español",
          images: imageUrl ? [imageUrl] : [],
          image_file_ids: imageFileId ? [imageFileId] : [],
          artifact_ids: artifactIds,
          tools,
          skills: selectedSkills,
          stream: true,
          ...(memoryEnabled && threadId ? { id: threadId } : {}),
          settings: {
            memory: memoryEnabled,
            tool_choice: toolsEnabled ? "auto" : "none",
            ui_blocks: !structured,
            ...(structured ? { response_format: { type: "json_object" } } : {})
          }
        };
    }
    mutation.mutate({ text, body });
  }

  const resetSession = useCallback(() => {
    if (resumeId || new URLSearchParams(window.location.search).has("thread")) {
      window.history.replaceState({}, "", "/studio");
      resumeApplied.current = false;
      setResumeId(null);
      setResumeLoadedId(null);
    }
    setMessages([]);
    setResponseMeta(null);
    setStreamingText("");
    setThreadId(null);
    setPrompt("");
    setError("");
    setImageUrl("");
    setImageFileId("");
    setImageFileName("");
    setArtifactIds([]);
    setSelectedSkills([]);
    setLastRequest(null);
  }, [resumeId]);

  return (
    <div className="studio-page">
      <div className="studio-header">
        <div>
          <span className="eyebrow">Playground</span>
          <h1>AK Studio</h1>
        </div>
        <div className="studio-contract-switch">
          <button disabled={mutation.isPending} className={contract === "professional" ? "active" : ""} onClick={() => { resetSession(); setContract("professional"); }}><Braces size={15} /> Professional</button>
          <button disabled={mutation.isPending} className={contract === "easy" ? "active" : ""} onClick={() => { resetSession(); setContract("easy"); }}><Command size={15} /> Easy</button>
        </div>
        <button className="button button-secondary" disabled={mutation.isPending} onClick={resetSession}><Plus size={15} /> Nueva sesión</button>
      </div>
      <div className="studio-layout">
        <aside className="studio-settings">
          <div className="settings-section">
            <label>Modelo</label>
            <select value={model} disabled={studioLocked} onChange={(event) => setModel(event.target.value)}>
              <option value="ak-auto">AK Auto</option>
              <option value="ak-balanced">AK Balanced</option>
              <option value="ak-fast">AK Fast</option>
              <option value="ak-reasoning">AK Reasoning</option>
              <option value="ak-free">AK Free</option>
              {(customModels.data?.endpoints || []).filter((item) => item.status === "active").map((item) => (
                <option value={item.model_alias} key={item.id}>{item.name} · Personal</option>
              ))}
            </select>
          </div>
          <div className="settings-section">
            <label>Modo de ejecución</label>
            <select value={mode} disabled={studioLocked} onChange={(event) => setMode(event.target.value)}>
              <option value="auto">Auto</option>
              <option value="fast" disabled={!user.planModes.includes("fast")}>Fast</option>
              <option value="standard" disabled={!user.planModes.includes("standard")}>Standard</option>
              <option value="thinking" disabled={!user.planModes.includes("thinking")}>Thinking</option>
              <option value="agentic" disabled={!user.planModes.includes("agentic")}>Agentic{!user.planModes.includes("agentic") ? " · requiere otro plan" : ""}</option>
            </select>
          </div>
          {contract === "easy" && (
            <>
              <div className="settings-section">
                <label>Nombre del asistente</label>
                <input value={name} disabled={studioLocked} onChange={(event) => setName(event.target.value)} />
              </div>
              <div className="settings-section">
                <label>Personalidad</label>
                <textarea rows={3} value={personality} disabled={studioLocked} onChange={(event) => setPersonality(event.target.value)} />
              </div>
              <div className="settings-section">
                <label>Contexto</label>
                <textarea rows={6} value={context} disabled={studioLocked} onChange={(event) => setContext(event.target.value)} />
              </div>
              <div className="settings-section">
                <label>Género o identidad</label>
                <input value={gender} disabled={studioLocked} onChange={(event) => setGender(event.target.value)} />
              </div>
            </>
          )}
          {contract === "professional" && (
            <div className="contract-note">
              <FileCode2 size={17} />
              <div><strong>Contrato OpenAI</strong><span>La conversación se enviará como un array <code>messages</code>.</span></div>
            </div>
          )}
          <div className="settings-divider" />
          <div className="toggle-row"><span>Herramientas</span><button type="button" disabled={studioLocked} aria-pressed={toolsEnabled} className={`toggle ${toolsEnabled ? "active" : ""}`} onClick={() => setToolsEnabled((value) => !value)}><span /></button></div>
          <div className="toggle-row"><span>Guardar memoria</span><button type="button" disabled={studioLocked} aria-pressed={memoryEnabled} className={`toggle ${memoryEnabled ? "active" : ""}`} onClick={() => { setMemoryEnabled((value) => !value); setThreadId(null); }}><span /></button></div>
          <div className="toggle-row"><span>Respuesta estructurada</span><button type="button" disabled={studioLocked} aria-pressed={structured} className={`toggle ${structured ? "active" : ""}`} onClick={() => setStructured((value) => !value)}><span /></button></div>
          {toolsEnabled && <button type="button" disabled={studioLocked} className="button button-ghost button-full" onClick={() => setToolModal(true)}><Wrench size={15} /> {selectedTools.length ? `${selectedTools.length} herramientas` : "Elegir herramientas"}</button>}
          <button type="button" disabled={studioLocked} className={`button button-ghost button-full ${selectedSkills.length ? "active" : ""}`} onClick={() => setSkillModal(true)}><Sparkles size={15} /> {selectedSkills.length ? `${selectedSkills.length} Skills activas` : "Probar Skills"}</button>
          {!!selectedSkills.length && <div className="selected-skill-stack">{selectedSkills.map((slug) => <span key={slug}><code>/{slug}</code><button type="button" onClick={() => setSelectedSkills((current) => current.filter((item) => item !== slug))}><X size={12} /></button></span>)}</div>}
        </aside>
        <main className="studio-chat">
          <div className="chat-toolbar">
            <div><span className="status-dot" /> {threadId ? "Memoria activa" : "Sesión nueva"}</div>
            <div className="chat-meta"><span>{model}</span><span>{mode}</span>{threadId && <span>{threadId.slice(0, 8)}</span>}</div>
          </div>
          <div className="chat-messages">
            {resumedThread.isLoading && <LoadingBlock />}
            {resumedThread.error && <ErrorState error={resumedThread.error} onRetry={() => resumedThread.refetch()} />}
            {!messages.length && !resumeId && (
              <div className="studio-empty">
                <div className="studio-orb"><Bot size={30} /></div>
                <h2>¿Qué quieres construir?</h2>
                <p>Prueba el contrato {contract === "professional" ? "profesional" : "fácil"} con una solicitud real.</p>
                <div className="suggestion-grid">
                  {[
                    "Diseña una estrategia de lanzamiento",
                    "Resume las ventajas de una API multimodelo",
                    "Compara tres arquitecturas de agentes",
                    "Crea una checklist de seguridad"
                  ].map((suggestion) => (
                    <button key={suggestion} disabled={studioLocked} onClick={() => setPrompt(suggestion)}>{suggestion}<ArrowRight size={14} /></button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((message, index) => (
              <div className={`chat-message ${message.role}`} key={`${message.role}-${index}`}>
                <div className="message-avatar">{message.role === "assistant" ? "AK" : "Tú"}</div>
                <div><div className="message-role">{message.role === "assistant" ? "AK-v5.0" : "Usuario"}</div><MarkdownContent content={message.content} className="message-content" /></div>
              </div>
            ))}
            {mutation.isPending && (
              <div className="chat-message assistant">
                <div className="message-avatar">AK</div>
                <div><div className="message-role">AK-v5.0</div>{streamingText ? <MarkdownContent content={streamingText} className="message-content" /> : <div className="typing"><span /><span /><span /></div>}</div>
              </div>
            )}
            {error && <div className="inline-error">{error}</div>}
            {!!responseMeta?.ui_blocks?.length && (
              <div className="structured-results">
                {responseMeta.ui_blocks.map((block) => <UiBlockRenderer block={block} key={block.id} />)}
              </div>
            )}
            {!!responseMeta?.artifacts?.length && (
              <div className="structured-results">
                {responseMeta.artifacts.map((artifact, index) => (
                  <article className="structured-card" key={index}>
                    <strong>{String(artifact.title || artifact.type || "Artefacto")}</strong>
                    <pre>{JSON.stringify(artifact, null, 2)}</pre>
                  </article>
                ))}
              </div>
            )}
            {!!responseMeta?.generations?.length && <div className="structured-results">{responseMeta.generations.map((generation: any) => <article className="structured-card" key={generation.id}><strong>Generación {generation.type} en cola</strong><p>ID <code>{generation.id}</code> · {generation.status}</p><NavLink className="button button-ghost" to="/jobs">Ver en Generaciones</NavLink></article>)}</div>}
            {!!responseMeta?.citations?.length && <div className="structured-results">{responseMeta.citations.map((citation: any, index) => <article className="structured-card" key={index}><strong>{String(citation.title || `Fuente ${index + 1}`)}</strong>{typeof citation.url === "string" && /^https:\/\//.test(citation.url) ? <a href={citation.url} target="_blank" rel="noreferrer">{citation.url}</a> : <pre>{JSON.stringify(citation, null, 2)}</pre>}</article>)}</div>}
            <div ref={chatEndRef} />
          </div>
          <form className="chat-composer" onSubmit={submit}>
            {(imageUrl || imageFileId) && <div className="attachment-chip"><span>Imagen adjunta</span>{imageUrl ? <a href={imageUrl} target="_blank" rel="noreferrer">{new URL(imageUrl).hostname}</a> : <code>{imageFileName || imageFileId.slice(0, 8)}</code>}<button type="button" onClick={() => { setImageUrl(""); setImageFileId(""); setImageFileName(""); }} aria-label="Quitar imagen"><X size={14} /></button></div>}
            {!!artifactIds.length && <div className="attachment-chip"><span>Contexto de artefacto</span><code>{artifactIds.map((id) => id.slice(0, 8)).join(", ")}</code><button type="button" onClick={() => setArtifactIds([])} aria-label="Quitar artefactos"><X size={14} /></button></div>}
            <textarea
              ref={composerRef}
              value={prompt}
              disabled={resumeBlocked}
              onChange={(event) => setPrompt(event.target.value)}
              onInput={(event) => {
                event.currentTarget.style.height = "auto";
                event.currentTarget.style.height = `${Math.min(180, event.currentTarget.scrollHeight)}px`;
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submit(event as unknown as FormEvent);
                }
              }}
              rows={2}
              placeholder={resumeBlocked ? "Espera mientras se recupera la memoria…" : contract === "professional" ? "Envía un mensaje o estructura JSON…" : "Escribe una solicitud para el asistente…"}
            />
            <div className="composer-footer">
              <div className="composer-tools">
                <button type="button" disabled={studioLocked} title="Herramientas" onClick={() => setToolModal(true)} className={selectedTools.length ? "active" : ""}><Wrench size={17} /></button>
                <button type="button" disabled={studioLocked} title="Skills" onClick={() => setSkillModal(true)} className={selectedSkills.length ? "active" : ""}><Sparkles size={17} /></button>
                <button type="button" disabled={studioLocked} title="Adjuntar imagen por URL" onClick={() => { setAttachmentDraft(imageUrl); setAttachmentModal(true); }}><Plus size={17} /></button>
                <span>Shift + Enter para una línea nueva</span>
              </div>
              {mutation.isPending
                ? <button className="send-button" type="button" title="Detener generación" onClick={() => requestAbortRef.current?.abort()}><X size={17} /></button>
                : <button className="send-button" type="submit" disabled={!prompt.trim() || resumeBlocked}><Send size={17} /></button>}
            </div>
          </form>
        </main>
        <aside className="studio-inspector">
          <div className="inspector-tabs"><button className={inspectorTab === "inspector" ? "active" : ""} onClick={() => setInspectorTab("inspector")}>Inspector</button><button className={inspectorTab === "json" ? "active" : ""} onClick={() => setInspectorTab("json")}>JSON</button></div>
          {inspectorTab === "json" ? <div className="inspector-json"><strong>Solicitud</strong><pre>{JSON.stringify(lastRequest, null, 2)}</pre><strong>Respuesta</strong><pre>{JSON.stringify(responseMeta, null, 2)}</pre></div> : <>
          <div className="inspector-section">
            <div className="inspector-title">Última ejecución</div>
            <dl className="inspector-list">
              <div><dt>Contrato</dt><dd>{contract}</dd></div>
              <div><dt>Ruta</dt><dd className="mono">{user.role === "admin" ? responseMeta?.model || model : model}</dd></div>
              <div><dt>Proveedor</dt><dd>{responseMeta?.provider || "—"}</dd></div>
              <div><dt>Modo</dt><dd>{responseMeta?.mode || mode}</dd></div>
              <div><dt>Latencia</dt><dd>{formatLatency(responseMeta?.latency_ms)}</dd></div>
              <div><dt>Tokens</dt><dd>{responseMeta ? responseMeta.usage.total_tokens : "—"}</dd></div>
              <div><dt>Créditos</dt><dd>{responseMeta ? responseMeta.usage.credits : "—"}</dd></div>
              <div><dt>Facturación</dt><dd>{responseMeta ? responseMeta.usage.billing_source === "byok" ? "Clave propia" : "Créditos AK" : "—"}</dd></div>
            </dl>
          </div>
          <div className="inspector-section">
            <div className="inspector-title">Tool calls</div>
            {responseMeta?.tool_calls?.length
              ? responseMeta.tool_calls.map((call, index) => (
                  <div className="inspector-empty" key={`${String(call.tool)}-${index}`}>
                    <TerminalSquare size={18} />
                    <span>{String(call.tool)} · {String(call.status || "completed")}</span>
                  </div>
                ))
              : <div className="inspector-empty"><TerminalSquare size={18} /><span>No se ejecutaron herramientas.</span></div>}
          </div>
          <div className="inspector-section">
            <div className="inspector-title">Request ID</div>
            <code className="request-id">{responseMeta?.request_id || "Se mostrará después de ejecutar"}</code>
          </div>
          </>}
        </aside>
      </div>
      <Modal
        open={toolModal}
        title="Herramientas de la sesión"
        description="Selecciona conectores permitidos. Sin selección, AK podrá elegir cualquier herramienta disponible."
        wide
        onClose={() => setToolModal(false)}
        footer={<>
          <button type="button" className="button button-secondary" onClick={() => setSelectedTools([])}>Usar todas</button>
          <button type="button" className="button button-primary" onClick={() => { setToolsEnabled(true); setToolModal(false); }}>Aplicar selección</button>
        </>}
      >
        {connectors.isLoading ? <LoadingBlock /> : connectors.error ? <ErrorState error={connectors.error} onRetry={() => connectors.refetch()} /> : (
          <div className="tool-picker-list">
            {connectors.data?.map((connector) => (
              <label className="tool-picker-item" key={connector.id}>
                <input
                  type="checkbox"
                  checked={selectedTools.includes(connector.id)}
                  onChange={() => setSelectedTools((current) => current.includes(connector.id) ? current.filter((id) => id !== connector.id) : [...current, connector.id])}
                />
                <span><strong>{connector.name}</strong><small>{connector.description}</small></span>
                <code>{connector.id}</code>
              </label>
            ))}
          </div>
        )}
      </Modal>
      <Modal
        open={skillModal}
        title="Skills de esta prueba"
        description="Selecciona uno o varios procedimientos. Se enviarán en el campo skills de la solicitud y AK aplicará sus instrucciones antes de responder."
        wide
        onClose={() => setSkillModal(false)}
        footer={<>
          <button type="button" className="button button-secondary" onClick={() => setSelectedSkills([])}>Limpiar selección</button>
          <button type="button" className="button button-primary" onClick={() => setSkillModal(false)}>Aplicar a Studio</button>
        </>}
      >
        {(studioBasicSkills.isLoading || studioAgentSkills.isLoading) && <LoadingBlock />}
        {(studioBasicSkills.error || studioAgentSkills.error) && <ErrorState error={studioBasicSkills.error || studioAgentSkills.error} onRetry={() => { studioBasicSkills.refetch(); studioAgentSkills.refetch(); }} />}
        <div className="studio-skill-picker">
          <section><div className="picker-section-heading"><div><strong>System prompts</strong><span>Instrucciones breves y reutilizables</span></div><span className="badge">{studioBasicSkills.data?.filter((skill) => skill.status === "active").length || 0}</span></div><div className="tool-picker-list">{studioBasicSkills.data?.filter((skill) => skill.status === "active").map((skill) => <label className="tool-picker-item" key={skill.id}><input type="checkbox" checked={selectedSkills.includes(skill.slug)} onChange={() => setSelectedSkills((current) => current.includes(skill.slug) ? current.filter((item) => item !== skill.slug) : [...current, skill.slug])} /><span><strong>{skill.name}</strong><small>{skill.description || "System prompt disponible"}</small></span><code>/{skill.slug}</code></label>)}</div></section>
          <section><div className="picker-section-heading"><div><strong>Agent Skills revisadas</strong><span>SKILL.md y referencias cargadas progresivamente</span></div><span className="badge badge-success">{studioAgentSkills.data?.filter((skill) => skill.status === "active" && ["reviewed", "trusted"].includes(skill.trust_status)).length || 0}</span></div><div className="tool-picker-list">{studioAgentSkills.data?.filter((skill) => skill.status === "active" && ["reviewed", "trusted"].includes(skill.trust_status)).map((skill) => <label className="tool-picker-item" key={skill.id}><input type="checkbox" checked={selectedSkills.includes(skill.slug)} onChange={() => setSelectedSkills((current) => current.includes(skill.slug) ? current.filter((item) => item !== skill.slug) : [...current, skill.slug])} /><span><strong>{skill.name}</strong><small>{skill.description}</small></span><code>{skill.file_count || 1} archivos</code></label>)}</div></section>
        </div>
        <div className="docs-note"><strong>Cómo probar:</strong> aplica la selección, escribe una tarea que coincida con su descripción y ejecuta la solicitud. En el inspector JSON podrás comprobar el array <code>skills</code> enviado.</div>
      </Modal>
      <Modal
        open={attachmentModal}
        title="Adjuntar imagen"
        description="Usa una URL pública o sube una imagen privada. AK la normaliza antes de enviarla al modelo multimodal."
        onClose={() => setAttachmentModal(false)}
        footer={<>
          <button type="button" className="button button-secondary" onClick={() => setAttachmentModal(false)}>Cancelar</button>
          <button type="button" className="button button-primary" onClick={() => {
            try {
              const url = new URL(attachmentDraft);
              if (url.protocol !== "https:") throw new Error();
              setImageUrl(url.toString());
              setImageFileId("");
              setImageFileName("");
              setAttachmentModal(false);
            } catch {
              toast("Introduce una URL HTTPS válida.", "error");
            }
          }}>Adjuntar</button>
        </>}
      >
        <label className="field"><span>URL de la imagen</span><input type="url" value={attachmentDraft} onChange={(event) => setAttachmentDraft(event.target.value)} placeholder="https://…" autoFocus /></label>
        <div className="settings-divider" />
        <label className="field"><span>O subir archivo (JPEG, PNG, WebP o GIF · máx. 10 MiB)</span><input type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={imageUploading} onChange={async (event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          const data = new FormData();
          data.append("file", file);
          setImageUploading(true);
          try {
            const uploaded = await api<any>("/api/v1/files/vision", { method: "POST", body: data });
            setImageFileId(uploaded.id);
            setImageFileName(uploaded.filename || file.name);
            setImageUrl("");
            setAttachmentDraft("");
            setAttachmentModal(false);
            toast("Imagen privada subida y adjuntada.", "success");
          } catch (uploadError) {
            toast((uploadError as Error).message, "error");
          } finally {
            setImageUploading(false);
          }
        }} /></label>
      </Modal>
    </div>
  );
}

function ApiKeys() {
  const scopeOptions = [
    { id: "ai:read", label: "Leer IA", description: "Rutas, uso y memoria" },
    { id: "ai:write", label: "Generar con IA", description: "Easy, Responses y Chat Completions" },
    { id: "tools:read", label: "Leer conectores", description: "Consultar el catálogo disponible" },
    { id: "tools:execute", label: "Ejecutar conectores", description: "Permitir acciones del modo agéntico" },
    { id: "jobs:read", label: "Leer jobs", description: "Consultar trabajos y resultados" },
    { id: "jobs:write", label: "Administrar jobs", description: "Crear, cancelar y reintentar trabajos" },
    { id: "artifacts:read", label: "Leer artefactos", description: "Consultar contenido persistente" },
    { id: "artifacts:write", label: "Editar artefactos", description: "Crear y modificar contenido persistente" }
  ] as const;
  const queryClient = useQueryClient();
  const toast = useToast();
  const keys = useQuery({ queryKey: ["api-keys"], queryFn: () => api<any[]>("/api/v1/api-keys") });
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState("Production key");
  const [scopes, setScopes] = useState<string[]>(["ai:read", "ai:write"]);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (input: { label: string; scopes: string[] }) => api<any>("/api/v1/api-keys", { method: "POST", body: JSON.stringify(input) }),
    onSuccess: (data) => {
      setCreatedKey(data.key);
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast("API key creada. Guárdala ahora.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/v1/api-keys/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setRevokeId(null);
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast("API key revocada.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  return (
    <div className="page">
      <PageHeader title="API keys" description="Credenciales seguras para integrar AK-v5.0 en tus productos." actions={<button className="button button-primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> Crear API key</button>} />
      <div className="security-callout"><LockKeyhole size={20} /><div><strong>Las claves se almacenan como hash.</strong><p>Una clave nueva solo se muestra una vez. No la incluyas en código público ni en URLs.</p></div></div>
      {createdKey && <div className="created-key"><div><span>Nueva clave</span><code>{createdKey}</code></div><button className="button button-secondary" onClick={async () => { try { await navigator.clipboard.writeText(createdKey); toast("Clave copiada.", "success"); } catch { toast("No se pudo acceder al portapapeles.", "error"); } }}><Copy size={15} /> Copiar</button><button className="icon-button" onClick={() => setCreatedKey(null)}><X size={17} /></button></div>}
      <section className="panel">
        {keys.error && <ErrorState error={keys.error} onRetry={() => keys.refetch()} />}
        {keys.isLoading && <LoadingBlock />}
        <div className="table">
          <div className="table-head keys-table"><span>Nombre</span><span>Prefijo</span><span>Último uso</span><span>Creada</span><span /></div>
          {keys.data?.map((key) => (
            <div className="table-row keys-table" key={key.id}>
              <span className="cell-primary api-key-name"><KeyRound size={16} /><span><strong>{key.label} {key.status !== "active" && <span className="badge">{key.status}</span>}</strong><small>{key.scopes?.join(" · ") || "Sin permisos"}</small></span></span>
              <span className="mono">{key.prefix}••••••••</span>
              <span>{formatDate(key.last_used_at)}</span>
              <span>{formatDate(key.created_at)}</span>
              {key.status === "active" ? <button className="icon-button danger" title="Revocar clave" onClick={() => setRevokeId(key.id)}><Trash2 size={16} /></button> : <span />}
            </div>
          ))}
          {!keys.isLoading && !keys.error && !keys.data?.length && <EmptyState icon={<KeyRound />} title="Sin API keys" description="Crea una para comenzar a integrar AK-v5.0." />}
        </div>
      </section>
      <FormModal
        open={createOpen}
        title="Crear API key"
        description="El nombre te ayudará a identificar dónde se utiliza."
        submitLabel="Crear clave"
        busy={create.isPending}
        submitDisabled={!label.trim() || !scopes.length}
        onClose={() => setCreateOpen(false)}
        onSubmit={(event) => { event.preventDefault(); if (label.trim() && scopes.length) create.mutate({ label: label.trim(), scopes }); }}
      >
        <label className="field"><span>Nombre</span><input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={120} autoFocus /></label>
        <div className="scope-picker">
          <span className="field-label">Permisos</span>
          <p>Aplica privilegio mínimo. Puedes revocar la clave en cualquier momento.</p>
          <div className="tool-picker-list">
            {scopeOptions.map((scope) => (
              <label className="tool-picker-item" key={scope.id}>
                <input
                  type="checkbox"
                  checked={scopes.includes(scope.id)}
                  onChange={() => setScopes((current) => current.includes(scope.id) ? current.filter((id) => id !== scope.id) : [...current, scope.id])}
                />
                <span><strong>{scope.label}</strong><small>{scope.description}</small></span>
                <code>{scope.id}</code>
              </label>
            ))}
          </div>
        </div>
      </FormModal>
      <ConfirmModal
        open={Boolean(revokeId)}
        title="Revocar API key"
        description="Las integraciones que utilicen esta clave dejarán de funcionar."
        confirmLabel="Revocar"
        busy={revoke.isPending}
        onClose={() => setRevokeId(null)}
        onConfirm={() => revokeId && revoke.mutate(revokeId)}
      />
    </div>
  );
}

function ProviderSettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const settings = useQuery({
    queryKey: ["provider-keys"],
    queryFn: () => api<ProviderKeySettingsResponse>("/api/v1/provider-keys")
  });
  const customModels = useQuery({
    queryKey: ["user-model-endpoints"],
    queryFn: () => api<UserModelEndpointResponse>("/api/v1/user-model-endpoints")
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderKeyItem | null>(null);
  const [provider, setProvider] = useState("gemini");
  const [label, setLabel] = useState("");
  const [providerApiKey, setProviderApiKey] = useState("");
  const [deleteKey, setDeleteKey] = useState<ProviderKeyItem | null>(null);
  const [modelFormOpen, setModelFormOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<UserModelEndpointItem | null>(null);
  const [replaceModelConfig, setReplaceModelConfig] = useState(true);
  const [deleteModel, setDeleteModel] = useState<UserModelEndpointItem | null>(null);
  const [testingModel, setTestingModel] = useState<UserModelEndpointItem | null>(null);
  const [modelTestPrompt, setModelTestPrompt] = useState("Hola, responde brevemente para confirmar que estás disponible.");
  const [modelTestResult, setModelTestResult] = useState<any | null>(null);
  const [modelForm, setModelForm] = useState({
    name: "",
    slug: "",
    description: "",
    tier: "balanced" as "fast" | "balanced" | "reasoning",
    priority: 50,
    status: "active" as "active" | "disabled",
    url: "",
    method: "POST" as "GET" | "POST",
    headers: "{}",
    request_template: "",
    response_path: "",
    timeout_ms: 30_000,
    allow_private_network: false
  });

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setProviderApiKey("");
  };
  const openCreate = (providerId?: string) => {
    const selected = providerId || settings.data?.providers.find((item) => item.can_add)?.id || "gemini";
    const providerDefinition = settings.data?.providers.find((item) => item.id === selected);
    setEditing(null);
    setProvider(selected);
    setLabel(providerDefinition ? `${providerDefinition.name} principal` : "Clave principal");
    setProviderApiKey("");
    setFormOpen(true);
  };
  const openEdit = (key: ProviderKeyItem) => {
    setEditing(key);
    setProvider(key.provider);
    setLabel(key.label);
    setProviderApiKey("");
    setFormOpen(true);
  };
  const openCreateModel = () => {
    setEditingModel(null);
    setReplaceModelConfig(true);
    setModelForm({
      name: "",
      slug: "",
      description: "",
      tier: "balanced",
      priority: 50,
      status: "active",
      url: "",
      method: "POST",
      headers: "{}",
      request_template: "",
      response_path: "",
      timeout_ms: 30_000,
      allow_private_network: false
    });
    setModelFormOpen(true);
  };
  const openEditModel = (item: UserModelEndpointItem) => {
    setEditingModel(item);
    setReplaceModelConfig(true);
    setModelForm({
      name: item.name,
      slug: item.slug,
      description: item.description || "",
      tier: item.tier,
      priority: item.priority,
      status: item.status,
      url: item.config.url || "",
      method: item.config.method || "POST",
      headers: JSON.stringify(item.config.headers || {}, null, 2),
      request_template: item.config.request_template || "",
      response_path: item.config.response_path || "",
      timeout_ms: Number(item.config.timeout_ms || 30_000),
      allow_private_network: Boolean(item.config.allow_private_network)
    });
    setModelFormOpen(true);
  };
  const refreshProviderData = () => {
    queryClient.invalidateQueries({ queryKey: ["provider-keys"] });
    queryClient.invalidateQueries({ queryKey: ["usage"] });
  };
  const refreshCustomModels = () => {
    queryClient.invalidateQueries({ queryKey: ["user-model-endpoints"] });
    queryClient.invalidateQueries({ queryKey: ["usage"] });
  };
  const save = useMutation({
    mutationFn: () => editing
      ? api(`/api/v1/provider-keys/${editing.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            label: label.trim(),
            ...(providerApiKey.trim() ? { api_key: providerApiKey.trim() } : {})
          })
        })
      : api("/api/v1/provider-keys", {
          method: "POST",
          body: JSON.stringify({
            provider,
            label: label.trim(),
            api_key: providerApiKey.trim()
          })
        }),
    onSuccess: () => {
      refreshProviderData();
      closeForm();
      toast(editing ? "Clave de proveedor actualizada." : "Clave de proveedor añadida.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const toggleStatus = useMutation({
    mutationFn: (key: ProviderKeyItem) => api(`/api/v1/provider-keys/${key.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: key.status === "active" ? "disabled" : "active" })
    }),
    onSuccess: () => {
      refreshProviderData();
      toast("Estado de la clave actualizado.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/provider-keys/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setDeleteKey(null);
      refreshProviderData();
      toast("Clave eliminada de forma segura.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const saveCustomModel = useMutation({
    mutationFn: () => {
      let headers: Record<string, string> = {};
      if (!editingModel || replaceModelConfig) {
        let parsedHeaders: unknown;
        try {
          parsedHeaders = JSON.parse(modelForm.headers);
        } catch {
          throw new SyntaxError("Headers JSON no contiene un objeto JSON válido.");
        }
        if (!parsedHeaders || Array.isArray(parsedHeaders) || typeof parsedHeaders !== "object") {
          throw new SyntaxError("Los headers deben ser un objeto JSON.");
        }
        if (Object.values(parsedHeaders as Record<string, unknown>).some((value) => typeof value !== "string")) {
          throw new SyntaxError("Todos los valores de headers deben ser texto.");
        }
        headers = parsedHeaders as Record<string, string>;
        try {
          validateDynamicJsonTemplate(modelForm.request_template);
        } catch {
          throw new SyntaxError("La plantilla JSON no es válida. Usa placeholders dinámicos sin comillas para arrays/números o dentro de cadenas para texto.");
        }
      }
      const payload = {
        name: modelForm.name.trim(),
        slug: modelForm.slug.trim(),
        description: modelForm.description.trim(),
        tier: modelForm.tier,
        priority: modelForm.priority,
        status: modelForm.status,
        ...(!editingModel || replaceModelConfig ? {
          config: {
            url: modelForm.url.trim(),
            method: modelForm.method,
            headers,
            request_template: modelForm.request_template,
            response_path: modelForm.response_path.trim() || undefined,
            timeout_ms: modelForm.timeout_ms,
            allow_private_network: modelForm.allow_private_network
          }
        } : {})
      };
      return api(
        editingModel ? `/api/v1/user-model-endpoints/${editingModel.id}` : "/api/v1/user-model-endpoints",
        { method: editingModel ? "PATCH" : "POST", body: JSON.stringify(payload) }
      );
    },
    onSuccess: () => {
      refreshCustomModels();
      setModelFormOpen(false);
      setEditingModel(null);
      toast(editingModel ? "Modelo HTTP actualizado." : "Modelo HTTP añadido.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const toggleCustomModel = useMutation({
    mutationFn: (item: UserModelEndpointItem) => api(`/api/v1/user-model-endpoints/${item.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: item.status === "active" ? "disabled" : "active" })
    }),
    onSuccess: () => {
      refreshCustomModels();
      toast("Estado del modelo actualizado.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const removeCustomModel = useMutation({
    mutationFn: (id: string) => api(`/api/v1/user-model-endpoints/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      refreshCustomModels();
      setDeleteModel(null);
      toast("Modelo HTTP eliminado.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const testCustomModel = useMutation({
    mutationFn: (item: UserModelEndpointItem) => api<any>(`/api/v1/user-model-endpoints/${item.id}/test`, {
      method: "POST",
      body: JSON.stringify({ prompt: modelTestPrompt })
    }),
    onSuccess: (result) => {
      setModelTestResult(result);
      refreshCustomModels();
      toast("El modelo respondió correctamente.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });

  const keys = settings.data?.keys || [];
  const totals = keys.reduce((result, key) => ({
    requests: result.requests + Number(key.usage.requests_total || 0),
    tokens: result.tokens + Number(key.usage.tokens_total || 0),
    errors: result.errors + Number(key.usage.errors_total || 0)
  }), { requests: 0, tokens: 0, errors: 0 });
  const providerName = (id: string) => settings.data?.providers.find((item) => item.id === id)?.name || id;
  const limitLabel = settings.data?.limit_per_provider === null
    ? "Sin límite"
    : `${settings.data?.limit_per_provider ?? 1} por proveedor`;
  const submitDisabled = (
    !label.trim() ||
    (!editing && providerApiKey.trim().length < 12) ||
    (Boolean(editing) && !providerApiKey.trim() && label.trim() === editing?.label)
  );
  const modelEndpoints = customModels.data?.endpoints || [];
  const canAddCustomModel = customModels.data?.limit === null || modelEndpoints.length < Number(customModels.data?.limit || 0);
  const customModelLimitLabel = customModels.data?.limit === null
    ? "Sin límite"
    : `${modelEndpoints.length} de ${customModels.data?.limit ?? 1}`;

  return (
    <div className="page">
      <PageHeader
        title="Configuración"
        description="Gestiona credenciales BYOK y modelos HTTP propios para ampliar las rutas de tu cuenta."
        actions={<div className="inline-actions"><button className="button button-secondary" disabled={!canAddCustomModel} onClick={openCreateModel}><Network size={16} /> Añadir modelo API</button><button className="button button-primary" disabled={!settings.data?.providers.some((item) => item.can_add)} onClick={() => openCreate()}><Plus size={16} /> Añadir clave</button></div>}
      />
      <div className="security-callout">
        <ShieldCheck size={20} />
        <div>
          <strong>Tus claves se cifran con AES-256-GCM antes de guardarse.</strong>
          <p>AK nunca vuelve a mostrar el secreto. Las solicitudes compatibles usan primero tus claves, consumen 0 créditos AK y quedan sujetas a los límites del proveedor.</p>
        </div>
        <NavLink className="button button-secondary" to="/docs#byok">Ver documentación</NavLink>
      </div>
      {customModels.error && <ErrorState error={customModels.error} onRetry={() => customModels.refetch()} />}
      <section className="panel custom-models-panel">
        <div className="panel-header"><div><h2>Modelos mediante API HTTP</h2><p>Añade cualquier endpoint GET o POST, adapta su payload y úsalo como ruta privada en Studio o en la API.</p></div><div className="inline-actions"><span className="badge">{customModelLimitLabel}</span><button className="button button-secondary" disabled={!canAddCustomModel} onClick={openCreateModel}><Plus size={15} /> Nuevo modelo</button></div></div>
        {customModels.isLoading && <LoadingBlock />}
        <div className="custom-model-list">
          {modelEndpoints.map((item) => (
            <article className="custom-model-row" key={item.id}>
              <span className={`status-dot ${item.status === "active" ? "" : "muted"}`} />
              <div className="custom-model-identity"><strong>{item.name}</strong><span><code>{item.model_alias}</code> · {item.config.method || "POST"} · {item.tier}</span></div>
              <div className="custom-model-usage"><span><strong>{formatNumber(item.usage.requests_total)}</strong> solicitudes</span><span><strong>{formatLatency(item.last_test_latency_ms)}</strong> prueba</span></div>
              <span className={`badge ${item.status === "active" ? "badge-success" : ""}`}>{item.status === "active" ? "Activo" : "Desactivado"}</span>
              <div className="inline-actions">
                <button className="icon-button" title="Probar modelo" onClick={() => { setTestingModel(item); setModelTestResult(null); }}><Play size={15} /></button>
                <button className="icon-button" title="Editar modelo" onClick={() => openEditModel(item)}><Pencil size={15} /></button>
                <button className="icon-button" disabled={toggleCustomModel.isPending} title={item.status === "active" ? "Desactivar" : "Activar"} onClick={() => toggleCustomModel.mutate(item)}>{item.status === "active" ? <X size={15} /> : <Check size={15} />}</button>
                <button className="icon-button danger" title="Eliminar modelo" onClick={() => setDeleteModel(item)}><Trash2 size={15} /></button>
              </div>
              {item.last_error && <div className="custom-model-error"><ShieldCheck size={14} /><span>{item.last_error}</span></div>}
            </article>
          ))}
          {!customModels.isLoading && !customModels.error && !modelEndpoints.length && <EmptyState icon={<Network />} title="Aún no tienes modelos HTTP" description="Conecta una API local, privada o pública y define cómo AK envía el historial y lee la respuesta." action={<button className="button button-primary" disabled={!canAddCustomModel} onClick={openCreateModel}><Plus size={15} /> Añadir modelo API</button>} />}
        </div>
      </section>
      {settings.error && <ErrorState error={settings.error} onRetry={() => settings.refetch()} />}
      {settings.isLoading && <LoadingBlock />}
      {!settings.isLoading && !settings.error && (
        <>
          <section className="metric-grid provider-metrics">
            <MetricCard icon={<KeyRound size={19} />} label="Claves configuradas" value={formatNumber(keys.length)} detail={`${settings.data?.plan_id || "free"} · ${limitLabel}`} />
            <MetricCard icon={<Activity size={19} />} label="Solicitudes BYOK" value={formatNumber(totals.requests)} detail="Todas tus claves" tone="accent" />
            <MetricCard icon={<Braces size={19} />} label="Tokens externos" value={formatNumber(totals.tokens)} detail="Entrada + salida" tone="positive" />
            <MetricCard icon={<Gauge size={19} />} label="Errores de proveedor" value={formatNumber(totals.errors)} detail="Histórico registrado" />
          </section>

          <section className="provider-settings-grid">
            {settings.data?.providers.map((item) => (
              <article className="provider-settings-card" key={item.id}>
                <div className="provider-card-heading">
                  <span className="provider-mark">{item.name.slice(0, 1)}</span>
                  <div><h2>{item.name}</h2><p>{item.description}</p></div>
                </div>
                <div className="provider-card-meta">
                  <span><strong>{item.configured}</strong> {settings.data.limit_per_provider === null ? "configuradas" : `de ${settings.data.limit_per_provider}`}</span>
                  <span><strong>{item.model_count}</strong> ruta{item.model_count === 1 ? "" : "s"} activa{item.model_count === 1 ? "" : "s"}</span>
                </div>
                {item.model_count === 0 && <p className="provider-card-notice">Puedes guardar la clave, pero se usará cuando el administrador habilite una ruta de este proveedor.</p>}
                <button className="button button-secondary" disabled={!item.can_add} onClick={() => openCreate(item.id)}>
                  <Plus size={15} /> {item.can_add ? "Añadir clave" : "Límite alcanzado"}
                </button>
              </article>
            ))}
          </section>

          <section className="panel provider-keys-panel">
            <div className="panel-header"><div><h2>Claves guardadas</h2><p>Uso, disponibilidad y errores por credencial. El secreto cifrado nunca se entrega al navegador.</p></div><span className="badge">{limitLabel}</span></div>
            <div className="provider-key-list">
              {keys.map((key) => (
                <article className={`provider-key-row ${key.over_limit ? "over-limit" : ""}`} key={key.id}>
                  <div className="provider-key-identity">
                    <span className={`status-dot ${key.status === "active" && !key.over_limit ? "" : "muted"}`} />
                    <div>
                      <div className="provider-key-title"><strong>{key.label}</strong><span className="badge">{providerName(key.provider)}</span>{key.over_limit && <span className="badge badge-warning">Fuera del límite</span>}</div>
                      <code>{key.hint}</code>
                    </div>
                  </div>
                  <div className="provider-key-stats">
                    <span><strong>{formatNumber(key.usage.requests_total)}</strong> solicitudes</span>
                    <span><strong>{formatNumber(key.usage.tokens_total)}</strong> tokens</span>
                    <span><strong>{formatDate(key.last_used_at)}</strong> último uso</span>
                  </div>
                  <div className="provider-key-actions">
                    <button className="button button-secondary" disabled={key.over_limit} onClick={() => openEdit(key)}><Pencil size={14} /> Editar</button>
                    <button className="button button-ghost" disabled={toggleStatus.isPending || (key.over_limit && key.status !== "active")} onClick={() => toggleStatus.mutate(key)}>
                      {key.status === "active" ? <><X size={14} /> Desactivar</> : <><Check size={14} /> Activar</>}
                    </button>
                    <button className="icon-button danger" title="Eliminar clave" onClick={() => setDeleteKey(key)}><Trash2 size={15} /></button>
                  </div>
                  {key.last_error && <div className="provider-key-error"><ShieldCheck size={14} /><span><strong>Último error:</strong> {key.last_error}</span></div>}
                  {key.over_limit && <div className="provider-key-error"><LockKeyhole size={14} /><span>Esta clave no participa en el routing. Elimínala o mejora el plan para volver a editarla.</span></div>}
                </article>
              ))}
              {!keys.length && <EmptyState icon={<KeyRound />} title="Aún no tienes claves de proveedor" description="Añade OpenAI, Gemini, OpenRouter, Groq, Cohere u otro proveedor compatible para usar su propia cuota." action={<button className="button button-primary" onClick={() => openCreate()}><Plus size={15} /> Añadir la primera</button>} />}
            </div>
          </section>
        </>
      )}

      <FormModal
        open={formOpen}
        title={editing ? "Editar clave de proveedor" : "Añadir clave de proveedor"}
        description={editing ? `Actualiza ${editing.hint}. Deja el secreto vacío para conservarlo.` : "El secreto se cifrará en el servidor antes de persistirse."}
        submitLabel={editing ? "Guardar cambios" : "Guardar clave"}
        busy={save.isPending}
        submitDisabled={submitDisabled}
        onClose={closeForm}
        onSubmit={(event) => { event.preventDefault(); if (!submitDisabled) save.mutate(); }}
      >
        <div className="form-grid">
          <label className="field"><span>Proveedor</span><select value={provider} disabled={Boolean(editing)} onChange={(event) => setProvider(event.target.value)}>{settings.data?.providers.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
          <label className="field"><span>Nombre interno</span><input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={120} placeholder="Producción" autoFocus /></label>
          <label className="field wide"><span>{editing ? "Nueva API key (opcional)" : "API key del proveedor"}</span><input type="password" value={providerApiKey} onChange={(event) => setProviderApiKey(event.target.value)} minLength={12} maxLength={4096} autoComplete="new-password" spellCheck={false} placeholder={editing ? "Vacío = conservar la actual" : "Pega aquí el secreto"} /><small className="field-hint">No incluyas esta clave en prompts, código cliente ni capturas de pantalla.</small></label>
        </div>
      </FormModal>
      <FormModal
        open={modelFormOpen}
        title={editingModel ? "Editar modelo HTTP" : "Añadir modelo mediante API"}
        description="Configura el contrato de entrada y la ruta que contiene el texto de salida. Los headers se guardan cifrados."
        submitLabel={editingModel ? "Guardar modelo" : "Crear modelo"}
        busy={saveCustomModel.isPending}
        submitDisabled={!modelForm.name.trim() || !modelForm.slug.trim() || ((!editingModel || replaceModelConfig) && !modelForm.url.trim())}
        wide
        onClose={() => { setModelFormOpen(false); setEditingModel(null); }}
        onSubmit={(event) => { event.preventDefault(); saveCustomModel.mutate(); }}
      >
        <div className="form-grid">
          <label className="field"><span>Nombre</span><input required maxLength={120} value={modelForm.name} onChange={(event) => setModelForm({ ...modelForm, name: event.target.value })} placeholder="Modelo HTTP personal" /></label>
          <label className="field"><span>Alias</span><input required className="mono" pattern="[a-z][a-z0-9_-]+" value={modelForm.slug} onChange={(event) => setModelForm({ ...modelForm, slug: event.target.value.toLowerCase().replace(/\s+/g, "-") })} placeholder="modelo-personal" /><small className="field-hint">Se usará como <code>custom:{modelForm.slug || "alias"}</code>.</small></label>
          <label className="field"><span>Perfil de routing</span><select value={modelForm.tier} onChange={(event) => setModelForm({ ...modelForm, tier: event.target.value as typeof modelForm.tier })}><option value="fast">Fast</option><option value="balanced">Balanced</option><option value="reasoning">Reasoning</option></select></label>
          <label className="field"><span>Estado</span><select value={modelForm.status} onChange={(event) => setModelForm({ ...modelForm, status: event.target.value as typeof modelForm.status })}><option value="active">Activo</option><option value="disabled">Deshabilitado</option></select></label>
          <label className="field"><span>Prioridad</span><input type="number" min={1} max={1000} value={modelForm.priority} onChange={(event) => setModelForm({ ...modelForm, priority: Number(event.target.value) || 50 })} /><small className="field-hint">Un número menor se intenta primero.</small></label>
          <label className="field"><span>Descripción</span><input maxLength={500} value={modelForm.description} onChange={(event) => setModelForm({ ...modelForm, description: event.target.value })} /></label>
        </div>
        {(!editingModel || replaceModelConfig) && <>
          <div className="form-grid">
            <label className="field wide"><span>URL del endpoint</span><input required value={modelForm.url} onChange={(event) => setModelForm({ ...modelForm, url: event.target.value })} placeholder="https://api.example.com/models/chat" /></label>
            <label className="field"><span>Método</span><select value={modelForm.method} onChange={(event) => setModelForm({ ...modelForm, method: event.target.value as typeof modelForm.method })}><option value="POST">POST</option><option value="GET">GET</option></select></label>
            <label className="field"><span>Timeout</span><select value={modelForm.timeout_ms} onChange={(event) => setModelForm({ ...modelForm, timeout_ms: Number(event.target.value) })}><option value={15000}>15 segundos</option><option value={30000}>30 segundos</option><option value={60000}>60 segundos</option><option value={120000}>120 segundos</option></select></label>
          </div>
          <label className="field"><span>Headers JSON</span><textarea rows={4} className="mono" value={modelForm.headers} onChange={(event) => setModelForm({ ...modelForm, headers: event.target.value })} placeholder="{}" /><small className="field-hint">Se envían como un objeto JSON real. Los valores sensibles aparecen como <code>[SECRETO GUARDADO]</code> y se conservan si no los reemplazas.</small></label>
          <label className="field"><span>Plantilla JSON de solicitud</span><textarea rows={8} className="mono" value={modelForm.request_template} onChange={(event) => setModelForm({ ...modelForm, request_template: event.target.value })} /><small className="field-hint">Variables: <code>{"{{messages}}"}</code>, <code>{"{{history}}"}</code>, <code>{"{{prompt}}"}</code>, <code>{"{{system}}"}</code>, <code>{"{{model}}"}</code>, <code>{"{{temperature}}"}</code> y <code>{"{{max_tokens}}"}</code>.</small></label>
          <label className="field"><span>Ruta del texto en la respuesta</span><input className="mono" value={modelForm.response_path} onChange={(event) => setModelForm({ ...modelForm, response_path: event.target.value })} placeholder="response o choices.0.message.content" /><small className="field-hint">Déjala vacía para detección automática de formatos comunes.</small></label>
          <label className="check-row warning"><input type="checkbox" checked={modelForm.allow_private_network} onChange={(event) => setModelForm({ ...modelForm, allow_private_network: event.target.checked })} /><span><strong>Permitir red privada</strong><small>Habilita RFC1918/ULA. Loopback, link-local, multicast y metadatos siguen bloqueados.</small></span></label>
        </>}
        <div className="docs-note"><strong>JSON dinámico:</strong> usa <code>{"{{messages}}"}</code> o <code>{"{{history}}"}</code> sin comillas para insertar arrays reales. Los placeholders escalares también pueden usarse dentro de cadenas.</div>
      </FormModal>
      <Modal
        open={Boolean(testingModel)}
        title={`Probar ${testingModel?.name || "modelo"}`}
        description="La prueba usa exactamente el contrato HTTP guardado y actualiza su estado operativo."
        wide
        onClose={() => { if (!testCustomModel.isPending) { setTestingModel(null); setModelTestResult(null); } }}
        footer={<><button className="button button-secondary" disabled={testCustomModel.isPending} onClick={() => { setTestingModel(null); setModelTestResult(null); }}>Cerrar</button><button className="button button-primary" disabled={testCustomModel.isPending || !modelTestPrompt.trim()} onClick={() => testingModel && testCustomModel.mutate(testingModel)}>{testCustomModel.isPending ? "Probando…" : "Ejecutar prueba"}</button></>}
      >
        <label className="field"><span>Mensaje de prueba</span><textarea rows={5} value={modelTestPrompt} onChange={(event) => setModelTestPrompt(event.target.value)} disabled={testCustomModel.isPending} /></label>
        {modelTestResult && <div className="connector-test-result"><strong>Respuesta · {formatLatency(modelTestResult.latency_ms)}</strong><MarkdownContent content={String(modelTestResult.output_text || "")} /></div>}
      </Modal>
      <ConfirmModal
        open={Boolean(deleteKey)}
        title="Eliminar clave de proveedor"
        description={`Se borrará ${deleteKey?.label || "esta clave"} y AK dejará de utilizarla inmediatamente. Esta acción no elimina la clave en el proveedor original.`}
        confirmLabel="Eliminar"
        busy={remove.isPending}
        onClose={() => setDeleteKey(null)}
        onConfirm={() => deleteKey && remove.mutate(deleteKey.id)}
      />
      <ConfirmModal
        open={Boolean(deleteModel)}
        title="Eliminar modelo HTTP"
        description={`Se eliminará ${deleteModel?.name || "este modelo"}, su contrato cifrado y dejará de participar en el routing.`}
        confirmLabel="Eliminar"
        busy={removeCustomModel.isPending}
        onClose={() => setDeleteModel(null)}
        onConfirm={() => deleteModel && removeCustomModel.mutate(deleteModel.id)}
      />
    </div>
  );
}

function Models({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const models = useQuery({ queryKey: ["models"], queryFn: () => api<any[]>("/api/v1/models") });
  const health = useMutation({
    mutationFn: () => api<any>("/api/v1/models/health", { method: "POST", body: JSON.stringify({}) }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["models"] });
      const healthy = Array.isArray(result?.models) ? result.models.filter((item: any) => item.status === "healthy").length : null;
      toast(healthy === null ? "Health check completado." : `${healthy} rutas saludables verificadas.`, "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const updateModel = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api(`/api/v1/admin/models/${id}`, { method: "PATCH", body: JSON.stringify({ enabled }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["models"] });
      toast("Ruta de modelo actualizada.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const profiles = [
    { name: "AK Fast", description: "Respuestas de baja latencia", tier: "fast" },
    { name: "AK Balanced", description: "Equilibrio entre calidad y velocidad", tier: "balanced" },
    { name: "AK Reasoning", description: "Análisis y razonamiento complejo", tier: "reasoning" }
  ].map((profile) => ({
    ...profile,
    route: models.data?.find((item) => item.tier === profile.tier && item.enabled && !String(item.alias).includes("fallback"))
      || models.data?.find((item) => item.tier === profile.tier && item.enabled)
  }));
  return (
    <div className="page">
      <PageHeader title="Orquestación y rutas" description="Perfiles lógicos que seleccionan automáticamente la ruta más rápida y saludable." actions={user.role === "admin" ? <button className="button button-secondary" disabled={health.isPending} onClick={() => health.mutate()}>{health.isPending ? <RefreshCw className="spin" size={16} /> : <Activity size={16} />} {health.isPending ? "Verificando…" : "Ejecutar health check"}</button> : <button className="button button-secondary" disabled={models.isFetching} onClick={() => models.refetch()}><RefreshCw className={models.isFetching ? "spin" : ""} size={16} /> Actualizar estado</button>} />
      <div className="profile-grid">
        {profiles.map(({ name, description, route, tier }) => (
          <article className="profile-card" key={name}>
            <div className="profile-card-top"><span className={`tier-icon ${tier}`}><Cpu size={19} /></span><span className={`badge ${route && !route.last_error ? "badge-success" : ""}`}>{route ? route.last_error ? "Incidencia" : "Activo" : "Sin ruta"}</span></div>
            <h3>{name}</h3><p>{description}</p><code>{route ? user.role === "admin" ? `${route.provider} / ${route.provider_model}` : "Ruta dinámica administrada" : "No configurado"}</code>
          </article>
        ))}
      </div>
      <section className="panel">
        <div className="panel-header"><div><h2>Registro de rutas</h2><p>Los destinos se ordenan por latencia, salud y prioridad.</p></div></div>
        {models.error && <ErrorState error={models.error} onRetry={() => models.refetch()} />}
        {models.isLoading && <LoadingBlock />}
        <div className="table">
          <div className="table-head model-table"><span>Alias</span><span>Proveedor</span><span>Destino</span><span>Perfil</span><span>Latencia</span><span>Estado</span></div>
          {models.data?.map((model) => (
            <div className="table-row model-table" key={model.id}>
              <span className="cell-primary"><Cpu size={16} /> {model.alias}</span>
              <span>{model.provider}</span>
              <span className="mono ellipsis">{user.role === "admin" ? model.provider_model : "Administrado por AK"}</span>
              <span><span className="badge">{model.tier}</span></span>
              <span>{formatLatency(model.last_latency_ms)}</span>
              <span className="status-text" title={model.last_error || undefined}><span className={`status-dot ${model.enabled && !model.last_error ? "" : "muted"}`} /> {!model.enabled ? "Desactivado" : model.last_error ? "Con incidencias" : "Activo"} {user.role === "admin" && <button className="icon-button" disabled={updateModel.isPending} title={model.enabled ? "Desactivar ruta" : "Activar ruta"} onClick={() => updateModel.mutate({ id: model.id, enabled: !model.enabled })}>{model.enabled ? <X size={14} /> : <Check size={14} />}</button>}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Connectors({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const connectors = useQuery({ queryKey: ["connectors"], queryFn: () => api<any[]>("/api/v1/connectors") });
  const canCustom = user.role === "admin" || userHasFeature(user, "custom_connectors");
  const customConnectors = useQuery({
    queryKey: ["user-connectors"],
    queryFn: () => api<any>("/api/v1/user-connectors"),
    enabled: canCustom
  });
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [testing, setTesting] = useState<any | null>(null);
  const [testInput, setTestInput] = useState("{}");
  const [testResult, setTestResult] = useState<unknown>(null);
  const [editingConnector, setEditingConnector] = useState<any | null>(null);
  const [deleteConnector, setDeleteConnector] = useState<any | null>(null);
  const [replaceConfig, setReplaceConfig] = useState(false);
  const [connectorForm, setConnectorForm] = useState({
    name: "",
    slug: "",
    description: "",
    connector_type: "http" as "http" | "mysql" | "prompt",
    status: "active",
    input_schema: '{\n  "type": "object",\n  "additionalProperties": true\n}',
    url: "https://api.example.com/data",
    method: "GET",
    headers: "{}",
    body_template: "",
    allow_private_network: false,
    host: "",
    port: 3306,
    database: "",
    username: "",
    password: "",
    query_template: "SELECT * FROM records WHERE id = {{input.id}} LIMIT 1",
    ssl: true,
    template: "{{input.query}}",
    system_prompt: ""
  });
  const categories = [...new Set((connectors.data || []).map((item) => String(item.category)))];
  const filtered = connectors.data?.filter((item) =>
    (category === "all" || item.category === category) &&
    `${item.name} ${item.description} ${item.id}`.toLowerCase().includes(query.toLowerCase())
  ) || [];
  const run = useMutation({
    mutationFn: ({ id, input, managementId }: { id: string; input: unknown; managementId?: string }) => api(
      managementId ? `/api/v1/user-connectors/${managementId}/test` : `/api/v1/connectors/${id}/run`,
      { method: "POST", body: JSON.stringify(input) }
    ),
    onSuccess: (result) => {
      setTestResult(result);
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      queryClient.invalidateQueries({ queryKey: ["user-connectors"] });
      toast("Conector ejecutado correctamente.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const saveCustom = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api<any>(
      editingConnector?.isNew ? "/api/v1/user-connectors" : `/api/v1/user-connectors/${editingConnector.id}`,
      {
        method: editingConnector?.isNew ? "POST" : "PATCH",
        body: JSON.stringify(payload)
      }
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      queryClient.invalidateQueries({ queryKey: ["user-connectors"] });
      toast(`Conector ${editingConnector?.isNew ? "creado" : "actualizado"}.`, "success");
      setEditingConnector(null);
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const removeCustom = useMutation({
    mutationFn: (id: string) => api(`/api/v1/user-connectors/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      queryClient.invalidateQueries({ queryKey: ["user-connectors"] });
      setDeleteConnector(null);
      toast("Conector personal eliminado.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });

  function openTest(connector: any) {
    const examples: Record<string, unknown> = {
      read_url: { url: "https://example.com" },
      jina_reader: { url: "https://example.com" },
      crypto_prices: { coin: "bitcoin" },
      exchange_rate: { from: "USD", to: "EUR", amount: 1 },
      world_weather: { city: "Lima" },
      world_time: { timezone: "America/Lima" }
    };
    const schemaProperties = connector.input_schema?.properties || {};
    const schemaExample = Object.fromEntries(Object.entries(schemaProperties).map(([key, raw]) => {
      const property = raw as Record<string, any>;
      if (Array.isArray(property.enum) && property.enum.length) return [key, property.enum[0]];
      if (property.default !== undefined) return [key, property.default];
      if (property.type === "number" || property.type === "integer") return [key, property.minimum ?? 1];
      if (property.type === "boolean") return [key, false];
      if (property.type === "array") return [key, []];
      if (property.format === "uri" || key.toLowerCase().includes("url")) return [key, "https://example.com"];
      if (key.toLowerCase().includes("city")) return [key, "Lima"];
      if (key.toLowerCase().includes("timezone")) return [key, "America/Lima"];
      return [key, property.example ?? "ejemplo"];
    }));
    setTesting(connector);
    setTestResult(null);
    setTestInput(JSON.stringify(examples[connector.id] || (Object.keys(schemaExample).length ? schemaExample : { query: "inteligencia artificial" }), null, 2));
  }
  function closeTest() {
    if (run.isPending) return;
    setTesting(null);
    setTestResult(null);
  }
  function openCreateConnector() {
    setEditingConnector({ isNew: true });
    setReplaceConfig(true);
    setConnectorForm({
      name: "",
      slug: "",
      description: "",
      connector_type: "http",
      status: "active",
      input_schema: '{\n  "type": "object",\n  "additionalProperties": true\n}',
      url: "https://api.example.com/data",
      method: "GET",
      headers: "{}",
      body_template: "",
      allow_private_network: false,
      host: "",
      port: 3306,
      database: "",
      username: "",
      password: "",
      query_template: "SELECT * FROM records WHERE id = {{input.id}} LIMIT 1",
      ssl: true,
      template: "{{input.query}}",
      system_prompt: ""
    });
  }
  function openEditConnector(connector: any) {
    const config = connector.config || {};
    setEditingConnector(connector);
    setReplaceConfig(false);
    setConnectorForm({
      name: connector.name,
      slug: connector.slug,
      description: connector.description || "",
      connector_type: connector.connector_type,
      status: connector.status,
      input_schema: JSON.stringify(connector.input_schema || { type: "object", additionalProperties: true }, null, 2),
      url: config.url || "",
      method: config.method || "GET",
      headers: "{}",
      body_template: config.body_template || "",
      allow_private_network: Boolean(config.allow_private_network),
      host: config.host || "",
      port: Number(config.port || 3306),
      database: config.database || "",
      username: config.username || "",
      password: "",
      query_template: config.query_template || "",
      ssl: config.ssl !== false,
      template: config.template || "",
      system_prompt: config.system_prompt || ""
    });
  }
  function submitCustomConnector(event: FormEvent) {
    event.preventDefault();
    let parsedSchema: Record<string, unknown>;
    try {
      parsedSchema = JSON.parse(connectorForm.input_schema);
    } catch {
      toast("El esquema de entrada no contiene JSON válido.", "error");
      return;
    }
    const payload: Record<string, unknown> = {
      name: connectorForm.name,
      slug: connectorForm.slug,
      description: connectorForm.description,
      input_schema: parsedSchema,
      status: connectorForm.status,
      ...(editingConnector?.isNew ? { connector_type: connectorForm.connector_type } : {})
    };
    if (editingConnector?.isNew || replaceConfig) {
      if (connectorForm.connector_type === "http") {
        let headers: Record<string, string>;
        try {
          headers = JSON.parse(connectorForm.headers);
        } catch {
          toast("Los headers deben ser un objeto JSON válido.", "error");
          return;
        }
        payload.config = {
          url: connectorForm.url,
          method: connectorForm.method,
          headers,
          body_template: connectorForm.body_template || undefined,
          allow_private_network: connectorForm.allow_private_network,
          timeout_ms: 10_000
        };
      } else if (connectorForm.connector_type === "mysql") {
        if (!connectorForm.password) {
          toast("Ingresa la contraseña MySQL al reemplazar la configuración.", "error");
          return;
        }
        payload.config = {
          host: connectorForm.host,
          port: connectorForm.port,
          database: connectorForm.database,
          username: connectorForm.username,
          password: connectorForm.password,
          query_template: connectorForm.query_template,
          ssl: connectorForm.ssl,
          allow_private_network: connectorForm.allow_private_network,
          timeout_ms: 8_000
        };
      } else {
        payload.config = {
          template: connectorForm.template,
          system_prompt: connectorForm.system_prompt || undefined
        };
      }
    }
    saveCustom.mutate(payload);
  }
  return (
    <div className="page">
      <PageHeader
        title="Herramientas y conectores"
        description="Capacidades externas disponibles para los modos profesional y agéntico."
        actions={canCustom
          ? <button className="button button-primary" onClick={openCreateConnector}><Plus size={16} /> Crear conector</button>
          : <NavLink className="button button-secondary" to="/plans"><LockKeyhole size={15} /> Conectores Premium</NavLink>}
      />
      {!canCustom && <div className="security-callout"><LockKeyhole size={20} /><div><strong>Conectores personales disponibles en Premium</strong><p>Premium permite crear APIs HTTP, consultas MySQL de solo lectura y plantillas de respuesta o system prompt con pruebas integradas.</p></div></div>}
      {canCustom && customConnectors.error && <ErrorState error={customConnectors.error} onRetry={() => customConnectors.refetch()} />}
      {canCustom && (
        <section className="panel custom-connectors-panel">
          <div className="panel-header"><div><h2>Tus conectores</h2><p>{customConnectors.data?.connectors?.length || 0} de {customConnectors.data?.limit || 25} configurados · secretos cifrados.</p></div></div>
          <div className="custom-connector-list">
            {customConnectors.data?.connectors?.map((connector: any) => (
              <div className="custom-connector-row" key={connector.id}>
                <span className="connector-icon"><Network size={17} /></span>
                <div><strong>{connector.name}</strong><span>{connector.connector_type.toUpperCase()} · <code>{connector.catalog_id}</code></span></div>
                <span className={`badge ${connector.status === "active" ? "badge-success" : ""}`}>{connector.status}</span>
                <span>{connector.last_test_at ? `${formatLatency(connector.last_test_latency_ms)} · ${formatDate(connector.last_test_at)}` : "Sin probar"}</span>
                <div className="inline-actions">
                  <button className="icon-button" title="Probar conector" onClick={() => openTest({ ...connector, id: connector.catalog_id, management_id: connector.id })}><Play size={15} /></button>
                  <button className="icon-button" title="Editar conector" onClick={() => openEditConnector(connector)}><Pencil size={15} /></button>
                  <button className="icon-button danger" title="Eliminar conector" onClick={() => setDeleteConnector(connector)}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {!customConnectors.isLoading && !customConnectors.error && !customConnectors.data?.connectors?.length && <EmptyState icon={<Network />} title="Aún no tienes conectores personales" description="Crea una API, una fuente MySQL de solo lectura o una plantilla reutilizable." />}
          </div>
        </section>
      )}
      <div className="toolbar">
        <label className="search-field"><Search size={17} /><input placeholder="Buscar herramienta…" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        <select value={category} onChange={(event) => setCategory(event.target.value)}><option value="all">Todas las categorías</option>{categories.map((item) => <option value={item} key={item}>{item}</option>)}</select>
      </div>
      {connectors.error && <ErrorState error={connectors.error} onRetry={() => connectors.refetch()} />}
      {connectors.isLoading && <LoadingBlock />}
      <div className="connector-grid">
        {filtered.map((connector) => (
          <article className="connector-card" key={connector.id}>
            <div className="connector-head"><span className="connector-icon"><Wrench size={18} /></span><span className={`badge ${connector.status === "healthy" ? "badge-success" : ""}`}>{connector.custom ? "Personal" : connector.status}</span></div>
            <h3>{connector.name}</h3><p>{connector.description}</p>
            <div className="connector-footer"><code>{connector.id}</code><button className="button button-ghost" onClick={() => openTest(connector)}>Probar <Play size={14} /></button></div>
          </article>
        ))}
        {!connectors.isLoading && !connectors.error && !filtered.length && <EmptyState icon={<Wrench />} title="Sin resultados" description="Ajusta la búsqueda o la categoría." />}
      </div>
      <Modal
        open={Boolean(testing)}
        title={`Probar ${testing?.name || "conector"}`}
        description="Edita el JSON de entrada y revisa la respuesta sin salir del panel."
        wide
        onClose={closeTest}
        footer={<>
          <button className="button button-secondary" disabled={run.isPending} onClick={closeTest}>Cerrar</button>
          <button className="button button-primary" disabled={run.isPending} onClick={() => {
            try {
              const input = JSON.parse(testInput);
              setTestResult(null);
              run.mutate({ id: testing.id, input, managementId: testing.management_id });
            } catch {
              toast("El JSON de entrada no es válido.", "error");
            }
          }}>{run.isPending ? "Ejecutando…" : "Ejecutar prueba"}</button>
        </>}
      >
        <label className="field"><span>Entrada JSON</span><textarea rows={9} className="mono" disabled={run.isPending} value={testInput} onChange={(event) => setTestInput(event.target.value)} /></label>
        {testResult !== null && <div className="connector-test-result"><strong>Resultado</strong><pre>{JSON.stringify(testResult, null, 2)}</pre></div>}
      </Modal>
      <FormModal
        open={Boolean(editingConnector)}
        title={editingConnector?.isNew ? "Crear conector personal" : "Editar conector personal"}
        description="Las credenciales se cifran. El acceso a una red privada se habilita de forma explícita por conector."
        submitLabel={editingConnector?.isNew ? "Crear conector" : "Guardar cambios"}
        busy={saveCustom.isPending}
        wide
        onClose={() => setEditingConnector(null)}
        onSubmit={submitCustomConnector}
      >
        <div className="form-grid">
          <label className="field"><span>Nombre</span><input required maxLength={120} value={connectorForm.name} onChange={(event) => setConnectorForm({ ...connectorForm, name: event.target.value })} /></label>
          <label className="field"><span>Identificador</span><input required className="mono" pattern="[a-z][a-z0-9_-]+" value={connectorForm.slug} onChange={(event) => setConnectorForm({ ...connectorForm, slug: event.target.value.toLowerCase().replace(/\s+/g, "-") })} /></label>
          <label className="field"><span>Tipo</span><select disabled={!editingConnector?.isNew} value={connectorForm.connector_type} onChange={(event) => setConnectorForm({ ...connectorForm, connector_type: event.target.value as typeof connectorForm.connector_type })}><option value="http">API HTTP</option><option value="mysql">MySQL de solo lectura</option><option value="prompt">Respuesta / system prompt</option></select></label>
          <label className="field"><span>Estado</span><select value={connectorForm.status} onChange={(event) => setConnectorForm({ ...connectorForm, status: event.target.value })}><option value="active">Activo</option><option value="disabled">Deshabilitado</option></select></label>
        </div>
        <label className="field"><span>Descripción</span><input maxLength={500} value={connectorForm.description} onChange={(event) => setConnectorForm({ ...connectorForm, description: event.target.value })} /></label>
        <label className="field"><span>Esquema JSON de entrada</span><textarea rows={6} className="mono" value={connectorForm.input_schema} onChange={(event) => setConnectorForm({ ...connectorForm, input_schema: event.target.value })} /></label>
        {!editingConnector?.isNew && <label className="check-row"><input type="checkbox" checked={replaceConfig} onChange={(event) => setReplaceConfig(event.target.checked)} /><span><strong>Reemplazar configuración cifrada</strong><small>Los secretos actuales nunca se muestran. Activa esta opción para sustituirlos.</small></span></label>}
        {(editingConnector?.isNew || replaceConfig) && connectorForm.connector_type === "http" && <>
          <div className="form-grid"><label className="field"><span>URL del servicio</span><input required value={connectorForm.url} onChange={(event) => setConnectorForm({ ...connectorForm, url: event.target.value })} /></label><label className="field"><span>Método</span><select value={connectorForm.method} onChange={(event) => setConnectorForm({ ...connectorForm, method: event.target.value })}><option>GET</option><option>POST</option><option>PUT</option><option>PATCH</option></select></label></div>
          <label className="field"><span>Headers JSON</span><textarea rows={4} className="mono" value={connectorForm.headers} onChange={(event) => setConnectorForm({ ...connectorForm, headers: event.target.value })} placeholder={'{"Authorization":"Bearer ..."}'} /></label>
          <label className="field"><span>Body template</span><textarea rows={5} className="mono" value={connectorForm.body_template} onChange={(event) => setConnectorForm({ ...connectorForm, body_template: event.target.value })} placeholder={'{"query":"{{input.query}}"}'} /></label>
          <label className="check-row warning"><input type="checkbox" checked={connectorForm.allow_private_network} onChange={(event) => setConnectorForm({ ...connectorForm, allow_private_network: event.target.checked })} /><span><strong>Permitir red privada</strong><small>Habilita destinos RFC1918/ULA como 10.x, 172.16–31.x, 192.168.x y dominios internos. Loopback, link-local y metadatos continúan bloqueados.</small></span></label>
        </>}
        {(editingConnector?.isNew || replaceConfig) && connectorForm.connector_type === "mysql" && <>
          <div className="form-grid"><label className="field"><span>Host</span><input required value={connectorForm.host} onChange={(event) => setConnectorForm({ ...connectorForm, host: event.target.value })} /></label><label className="field"><span>Puerto</span><input type="number" min={1} max={65535} value={connectorForm.port} onChange={(event) => setConnectorForm({ ...connectorForm, port: Number(event.target.value) || 3306 })} /></label><label className="field"><span>Base de datos</span><input required value={connectorForm.database} onChange={(event) => setConnectorForm({ ...connectorForm, database: event.target.value })} /></label><label className="field"><span>Usuario</span><input required value={connectorForm.username} onChange={(event) => setConnectorForm({ ...connectorForm, username: event.target.value })} /></label></div>
          <label className="field"><span>Contraseña</span><input required type="password" autoComplete="new-password" value={connectorForm.password} onChange={(event) => setConnectorForm({ ...connectorForm, password: event.target.value })} /></label>
          <label className="field"><span>Consulta de solo lectura</span><textarea required rows={5} className="mono" value={connectorForm.query_template} onChange={(event) => setConnectorForm({ ...connectorForm, query_template: event.target.value })} /></label>
          <label className="check-row"><input type="checkbox" checked={connectorForm.ssl} onChange={(event) => setConnectorForm({ ...connectorForm, ssl: event.target.checked })} /><span><strong>Validar TLS</strong><small>Recomendado para bases de datos externas.</small></span></label>
          <label className="check-row warning"><input type="checkbox" checked={connectorForm.allow_private_network} onChange={(event) => setConnectorForm({ ...connectorForm, allow_private_network: event.target.checked })} /><span><strong>Permitir red privada</strong><small>Permite conectarse a MySQL en RFC1918/ULA. La consulta sigue siendo preparada, de solo lectura y limitada a 100 filas.</small></span></label>
        </>}
        {(editingConnector?.isNew || replaceConfig) && connectorForm.connector_type === "prompt" && <>
          <label className="field"><span>System prompt opcional</span><textarea rows={5} value={connectorForm.system_prompt} onChange={(event) => setConnectorForm({ ...connectorForm, system_prompt: event.target.value })} /></label>
          <label className="field"><span>Respuesta template</span><textarea required rows={7} value={connectorForm.template} onChange={(event) => setConnectorForm({ ...connectorForm, template: event.target.value })} /></label>
        </>}
        <div className="docs-note"><strong>Variables:</strong> usa <code>{"{{input.campo}}"}</code>. En MySQL se convierte en parámetro preparado; solo se aceptan consultas de lectura y hasta 100 filas.</div>
      </FormModal>
      <ConfirmModal
        open={Boolean(deleteConnector)}
        title="Eliminar conector personal"
        description="Se eliminará la configuración cifrada y dejará de estar disponible en Studio y la API."
        confirmLabel="Eliminar"
        busy={removeCustom.isPending}
        onClose={() => setDeleteConnector(null)}
        onConfirm={() => deleteConnector && removeCustom.mutate(deleteConnector.id)}
      />
    </div>
  );
}

function Skills({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const skills = useQuery({ queryKey: ["skills"], queryFn: () => api<any[]>("/api/v1/skills") });
  const [section, setSection] = useState<"basic" | "agent" | "library">("basic");
  const agentSkills = useQuery({ queryKey: ["agent-skills"], queryFn: () => api<any[]>("/api/v1/agent-skills"), enabled: section === "agent" });
  const skillLibrary = useQuery({ queryKey: ["skill-library"], queryFn: () => api<any[]>("/api/v1/skill-library"), enabled: section === "library" });
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [inspection, setInspection] = useState<{ type: "installed" | "library"; item: any } | null>(null);
  const [selectedSkillFile, setSelectedSkillFile] = useState("SKILL.md");
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryPublisher, setLibraryPublisher] = useState("all");
  const [libraryLimit, setLibraryLimit] = useState(60);
  const [filter, setFilter] = useState<"all" | "system" | "public" | "private">("all");
  const [editing, setEditing] = useState<any | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({
    slug: "",
    name: "",
    description: "",
    prompt_template: "",
    visibility: "private"
  });
  const canCustomize = userHasFeature(user, "skills");
  const canAgentSkills = user.role === "admin" || user.planFeatures.includes("all") || user.planFeatures.includes("agent_skills");
  const visibleSkills = skills.data?.filter((skill) => filter === "all" || skill.visibility === filter) || [];
  const installedInspection = useQuery({
    queryKey: ["agent-skill-detail", inspection?.item?.id],
    queryFn: () => api<any>(`/api/v1/agent-skills/${inspection?.item?.id}`),
    enabled: inspection?.type === "installed"
  });
  const libraryInspection = useQuery({
    queryKey: ["skill-library-inspect", inspection?.item?.source_url],
    queryFn: () => api<any>(`/api/v1/skill-library/inspect?source_url=${encodeURIComponent(inspection?.item?.source_url || "")}`),
    enabled: inspection?.type === "library"
  });
  const inspectionData = inspection?.type === "installed" ? installedInspection.data : libraryInspection.data;
  const inspectionLoading = inspection?.type === "installed" ? installedInspection.isLoading : libraryInspection.isLoading;
  const inspectionError = inspection?.type === "installed" ? installedInspection.error : libraryInspection.error;
  const inspectionFiles: any[] = inspectionData?.files || [];
  const activeInspectionFile = inspectionFiles.find((file) => file.path === selectedSkillFile) || inspectionFiles[0];
  const libraryItems = skillLibrary.data || [];
  const libraryPublishers = [...new Set<string>(libraryItems.map((item: any) => item.publisher))];
  const filteredLibrary = libraryItems.filter((item: any) => {
    const haystack = `${item.name} ${item.publisher} ${item.category} ${item.repository}`.toLowerCase();
    return (!librarySearch.trim() || haystack.includes(librarySearch.toLowerCase())) && (libraryPublisher === "all" || item.publisher === libraryPublisher);
  });
  const openInspection = (type: "installed" | "library", item: any) => {
    setSelectedSkillFile("SKILL.md");
    setInspection({ type, item });
  };
  const save = useMutation({
    mutationFn: () => api<any>(editing?.isNew ? "/api/v1/skills" : `/api/v1/skills/${editing.id}`, {
      method: editing?.isNew ? "POST" : "PATCH",
      body: JSON.stringify({
        ...form,
        ...(editing?.isNew ? { input_schema: {} } : { expected_version: editing.version })
      })
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      toast(`Skill ${editing?.isNew ? "creada" : "actualizada"}.`, "success");
      setEditing(null);
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/skills/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      setDeleteId(null);
      toast("Skill eliminada.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const importSkill = useMutation({
    mutationFn: (source_url: string) => api<any>("/api/v1/agent-skills/import", { method: "POST", body: JSON.stringify({ source_url, visibility: "private" }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-skills"] });
      setImportOpen(false);
      setImportUrl("");
      setSection("agent");
      toast("Skill importada como borrador. Revísala antes de activarla.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const reviewSkill = useMutation({
    mutationFn: (id: string) => api<any>(`/api/v1/agent-skills/${id}/review`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ["agent-skills"] });
      toast(report.passed ? "Revisión superada; Skill activada." : "La Skill fue bloqueada por la revisión.", report.passed ? "success" : "error");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const removeAgentSkill = useMutation({
    mutationFn: (id: string) => api(`/api/v1/agent-skills/${id}`, { method: "DELETE" }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["agent-skills"] }); toast("Agent Skill eliminada.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const exportAgentSkill = async (skill: any) => {
    try {
      const bundle = await api<any>(`/api/v1/agent-skills/${skill.id}/export`);
      downloadContent(`${skill.slug}.akskill.json`, JSON.stringify(bundle, null, 2), "application/json");
    } catch (error) { toast((error as Error).message, "error"); }
  };
  const openCreate = () => {
    setEditing({ isNew: true });
    setForm({ slug: "", name: "", description: "", prompt_template: "", visibility: "private" });
  };
  const openEdit = (skill: any) => {
    setEditing(skill);
    setForm({
      slug: skill.slug,
      name: skill.name,
      description: skill.description || "",
      prompt_template: skill.prompt_template || "",
      visibility: skill.visibility
    });
  };
  return (
    <div className="page">
      <PageHeader title="Skills" description="Separa system prompts breves de paquetes Agent Skill con SKILL.md, recursos, revisión, importación y exportación." actions={section === "basic" ? (canCustomize ? <button className="button button-primary" onClick={openCreate}><Plus size={16} /> Nuevo system prompt</button> : <NavLink className="button button-secondary" to="/plans"><Sparkles size={16} /> Activar edición</NavLink>) : (canAgentSkills ? <button className="button button-primary" onClick={() => setImportOpen(true)}><Plus size={16} /> Importar Skill</button> : <NavLink className="button button-secondary" to="/plans"><LockKeyhole size={15} /> Requiere Premium</NavLink>)} />
      <div className="tabs skill-kind-tabs" role="tablist"><button className={section === "basic" ? "active" : ""} onClick={() => setSection("basic")}>System prompts / Skills básicas</button><button className={section === "agent" ? "active" : ""} onClick={() => setSection("agent")}>Agent Skills</button><button className={section === "library" ? "active" : ""} onClick={() => setSection("library")}>Biblioteca de Skills</button></div>
      {section === "basic" && <>
      {!canCustomize && <div className="security-callout"><LockKeyhole size={20} /><div><strong>Catálogo en modo lectura</strong><p>Tu plan permite consultar las skills del sistema; crear o editar skills requiere Pro o superior.</p></div></div>}
      <div className="tabs" role="tablist">
        {([
          ["all", "Todas"],
          ["system", "Sistema"],
          ["public", "Comunidad"],
          ["private", "Privadas"]
        ] as const).map(([value, label]) => <button role="tab" aria-selected={filter === value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)} key={value}>{label}</button>)}
      </div>
      {skills.error && <ErrorState error={skills.error} onRetry={() => skills.refetch()} />}
      {skills.isLoading && <LoadingBlock />}
      <div className="skill-grid">
        {visibleSkills.map((skill) => {
          const canManage = canCustomize && (skill.owner_user_id === user.userId || user.role === "admin");
          return (
          <article className="skill-card" key={skill.id}>
            <div className="skill-head"><span className="skill-icon"><Sparkles size={19} /></span><span className="badge">{skill.visibility}</span></div>
            <h3>{skill.name}</h3><p>{skill.description || "Skill reutilizable para AK-v5.0."}</p>
            <div className="skill-footer">
              <code>/{skill.slug}</code><span>v{skill.version}</span>
              {canManage && <span className="inline-actions"><button className="icon-button" title="Editar skill" onClick={() => openEdit(skill)}><Pencil size={15} /></button><button className="icon-button danger" title="Eliminar skill" onClick={() => setDeleteId(skill.id)}><Trash2 size={15} /></button></span>}
            </div>
            <NavLink className="button button-secondary button-full" to={`/studio?skill=${encodeURIComponent(skill.slug)}`}><Play size={14} /> Probar en Studio</NavLink>
          </article>
        )})}
        {!skills.isLoading && !skills.error && !visibleSkills.length && <EmptyState icon={<Sparkles />} title="No hay prompts en esta vista" description="Crea un system prompt o selecciona otro filtro." />}
      </div>
      </>}
      {section === "agent" && <>
        {!canAgentSkills && <div className="security-callout"><LockKeyhole size={20} /><div><strong>Agent Skills requiere Premium</strong><p>Los prompts básicos continúan disponibles. Los paquetes con archivos y recursos se habilitan en Premium y Enterprise.</p></div></div>}
        {agentSkills.error && <ErrorState error={agentSkills.error} onRetry={() => agentSkills.refetch()} />}
        {agentSkills.isLoading && <LoadingBlock />}
        <div className="skill-grid">{agentSkills.data?.map((skill) => <article className="skill-card agent-skill-card" key={skill.id}><div className="skill-head"><span className="skill-icon"><Blocks size={19} /></span><span className={`badge ${skill.trust_status === "reviewed" || skill.trust_status === "trusted" ? "badge-success" : ""}`}>{skill.trust_status}</span></div><h3>{skill.name}</h3><p>{skill.description}</p><div className="skill-package-meta"><span><FileText size={13} /> {skill.file_count || 1} archivos</span><span>{formatNumber(skill.package_bytes || 0)} bytes</span><span>v{skill.version}</span><span className={`badge ${skill.status === "active" ? "badge-success" : ""}`}>{skill.status}</span></div><div className="skill-card-actions"><button className="button button-secondary" onClick={() => openInspection("installed", skill)}><Eye size={15} /> Inspeccionar</button>{skill.status === "active" ? <NavLink className="button button-primary" to={`/studio?skill=${encodeURIComponent(skill.slug)}`}><Play size={14} /> Probar</NavLink> : <button className="button button-primary" onClick={() => reviewSkill.mutate(skill.id)} disabled={reviewSkill.isPending}><ShieldCheck size={15} /> Revisar y activar</button>}<button className="icon-button" title="Exportar paquete portable" onClick={() => exportAgentSkill(skill)}><Download size={15} /></button>{(skill.owner_user_id === user.userId || user.role === "admin") && <button className="icon-button danger" title="Eliminar" onClick={() => removeAgentSkill.mutate(skill.id)}><Trash2 size={15} /></button>}</div></article>)}</div>
        {!agentSkills.isLoading && !agentSkills.error && !agentSkills.data?.length && <EmptyState icon={<Blocks />} title="No hay Agent Skills instaladas" description="Importa una carpeta con SKILL.md desde una fuente confiable o desde la biblioteca." action={canAgentSkills && <button className="button button-primary" onClick={() => setImportOpen(true)}>Importar primera Skill</button>} />}
      </>}
      {section === "library" && <>
        <div className="library-hero"><div><span className="eyebrow">Catálogo multifuente</span><h2>{formatNumber(libraryItems.length)} Agent Skills disponibles</h2><p>Fuentes de Anthropic, OpenAI y colecciones comunitarias compatibles con SKILL.md. Inspecciona siempre instrucciones, scripts, referencias y licencia antes de instalar.</p></div><div className="library-source-marks"><span><ShieldCheck size={15} /> Oficiales verificadas</span><span><Github size={15} /> Procedencia visible</span><span><Blocks size={15} /> Paquete completo</span></div></div>
        <div className="library-toolbar"><label className="search-field"><Search size={16} /><input value={librarySearch} onChange={(event) => { setLibrarySearch(event.target.value); setLibraryLimit(60); }} placeholder="Buscar por nombre, categoría o repositorio…" /></label><select value={libraryPublisher} onChange={(event) => { setLibraryPublisher(event.target.value); setLibraryLimit(60); }}><option value="all">Todas las fuentes</option>{libraryPublishers.map((publisher) => <option key={publisher}>{publisher}</option>)}</select><span className="badge">{filteredLibrary.length} resultados</span></div>
        {skillLibrary.error && <ErrorState error={skillLibrary.error} onRetry={() => skillLibrary.refetch()} />}
        {skillLibrary.isLoading && <LoadingBlock />}
        <div className="skill-grid library-grid">{filteredLibrary.slice(0, libraryLimit).map((skill: any) => <article className="skill-card library-card" key={skill.id}><div className="skill-head"><span className="skill-icon"><Github size={19} /></span><span className={`badge ${skill.trust === "official" ? "badge-success" : ""}`}>{skill.trust === "official" ? "Fuente oficial" : "Comunidad"}</span></div><div className="library-card-meta"><span>{skill.publisher}</span><span>{skill.category}</span></div><h3>{skill.name}</h3><p><code>{skill.repository}</code><br />Licencia: {skill.license}</p><div className="skill-card-actions"><button className="button button-secondary" onClick={() => openInspection("library", skill)}><Eye size={15} /> Inspeccionar</button><button className="button button-primary" disabled={!canAgentSkills || importSkill.isPending || skill.installed} onClick={() => importSkill.mutate(skill.import_url)}>{skill.installed ? <><Check size={15} /> Instalada</> : <><Download size={15} /> Importar</>}</button></div></article>)}</div>
        {!skillLibrary.isLoading && !skillLibrary.error && filteredLibrary.length > libraryLimit && <div className="load-more-row"><button className="button button-secondary" onClick={() => setLibraryLimit((value) => value + 60)}>Mostrar 60 más</button><span>{Math.min(libraryLimit, filteredLibrary.length)} de {filteredLibrary.length}</span></div>}
      </>}
      <Modal open={Boolean(inspection)} title={inspection?.item?.name || "Inspector de Skill"} description={inspection?.type === "installed" ? "Paquete instalado, manifiesto, revisión y todos sus componentes." : `${inspection?.item?.publisher || "Fuente"} · ${inspection?.item?.repository || "Repositorio"}`} wide onClose={() => setInspection(null)} footer={<>{inspection?.type === "library" && <a className="button button-secondary" href={inspection.item.source_url} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Abrir repositorio</a>}{inspection?.type === "library" && <button className="button button-primary" disabled={!canAgentSkills || importSkill.isPending || inspection.item.installed} onClick={() => importSkill.mutate(inspection.item.import_url)}><Download size={15} /> Importar paquete</button>}{inspection?.type === "installed" && inspection.item.status === "active" && <NavLink className="button button-primary" to={`/studio?skill=${encodeURIComponent(inspection.item.slug)}`}><Play size={15} /> Probar en Studio</NavLink>}</>}>
        {inspectionLoading && <LoadingBlock />}
        {inspectionError && <ErrorState error={inspectionError} onRetry={() => inspection?.type === "installed" ? installedInspection.refetch() : libraryInspection.refetch()} />}
        {inspectionData && <div className="skill-inspector"><aside><div className="skill-inspector-summary"><span className="skill-icon"><Blocks size={19} /></span><div><strong>{inspectionData.skill?.name || inspectionData.name}</strong><span>{inspectionData.skill?.description || inspectionData.description}</span></div></div><div className="skill-file-tree">{inspectionFiles.map((file) => <button key={file.path} className={activeInspectionFile?.path === file.path ? "active" : ""} onClick={() => setSelectedSkillFile(file.path)}><span><FileText size={14} />{file.path}</span><small>{file.kind} · {formatNumber(Number(file.size_bytes || 0))} B</small></button>)}</div></aside><main><div className="skill-file-header"><div><strong>{activeInspectionFile?.path}</strong><span>{activeInspectionFile?.mime_type || "text/plain"} · {activeInspectionFile?.kind}</span></div>{activeInspectionFile?.kind === "script" && <span className="badge">No ejecutable</span>}</div>{activeInspectionFile ? (String(activeInspectionFile.content || "").startsWith("base64:") ? <div className="binary-preview"><Blocks size={30} /><strong>Recurso binario incluido</strong><p>AK conserva este archivo dentro del paquete, pero no lo ejecuta ni lo interpreta automáticamente.</p></div> : /markdown/i.test(activeInspectionFile.mime_type) || activeInspectionFile.path.endsWith(".md") ? <MarkdownContent content={String(activeInspectionFile.content || "").slice(0, 250_000)} className="skill-markdown-preview" /> : <pre className="skill-code-preview"><code>{String(activeInspectionFile.content || "").slice(0, 250_000)}</code></pre>) : <EmptyState icon={<FileText />} title="Paquete sin archivos visibles" description="No se encontró contenido para mostrar." />}</main></div>}
      </Modal>
      <FormModal
        open={Boolean(editing)}
        title={editing?.isNew ? "Nuevo system prompt" : "Editar system prompt"}
        description="Define una instrucción breve. Para procedimientos con archivos, scripts o referencias usa una Agent Skill."
        submitLabel={editing?.isNew ? "Crear prompt" : "Guardar cambios"}
        busy={save.isPending}
        onClose={() => setEditing(null)}
        onSubmit={(event) => {
          event.preventDefault();
          if (!form.slug.trim() || !form.name.trim() || form.prompt_template.trim().length < 5) {
            toast("Completa el slug, nombre y una instrucción de al menos 5 caracteres.", "error");
            return;
          }
          save.mutate();
        }}
      >
        <div className="form-grid">
          <label className="field"><span>Nombre</span><input required maxLength={160} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
          <label className="field"><span>Slug</span><input required pattern="[a-z0-9][a-z0-9_-]{2,119}" className="mono" value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value.toLowerCase().replace(/\s+/g, "-") })} /></label>
          <label className="field"><span>Visibilidad</span><select value={form.visibility} onChange={(event) => setForm({ ...form, visibility: event.target.value })}><option value="private">Privada</option><option value="public">Comunidad</option>{user.role === "admin" && <option value="system">Sistema</option>}</select></label>
        </div>
        <label className="field"><span>Descripción</span><input maxLength={2000} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
        <label className="field"><span>Prompt template</span><textarea required rows={9} value={form.prompt_template} onChange={(event) => setForm({ ...form, prompt_template: event.target.value })} placeholder="Describe cómo debe actuar la IA…" /></label>
      </FormModal>
      <FormModal open={importOpen} title="Importar Agent Skill" description="Acepta una URL de GitHub al directorio de la Skill o a su archivo SKILL.md. Se guardará como borrador desactivado." submitLabel="Importar como borrador" busy={importSkill.isPending} onClose={() => setImportOpen(false)} onSubmit={(event) => { event.preventDefault(); if (importUrl.trim()) importSkill.mutate(importUrl.trim()); }}><label className="field"><span>URL de fuente</span><input required type="url" value={importUrl} onChange={(event) => setImportUrl(event.target.value)} placeholder="https://github.com/organizacion/repositorio/tree/main/skills/mi-skill" /></label><div className="security-callout"><ShieldCheck size={19} /><div><strong>Importación segura</strong><p>Los scripts quedan inactivos. AK revisa secretos incrustados e intentos de anular instrucciones antes de permitir activación.</p></div></div></FormModal>
      <ConfirmModal
        open={Boolean(deleteId)}
        title="Eliminar skill"
        description="Esta acción elimina la skill del catálogo y no se puede deshacer."
        confirmLabel="Eliminar"
        busy={remove.isPending}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && remove.mutate(deleteId)}
      />
    </div>
  );
}

function Threads() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const threads = useQuery({ queryKey: ["threads"], queryFn: () => api<any[]>("/api/v1/threads") });
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const filtered = threads.data?.filter((thread) => `${thread.title} ${thread.request_mode} ${thread.model_alias || ""}`.toLowerCase().includes(query.toLowerCase())) || [];
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/threads/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["threads"] });
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      setDeleteId(null);
      toast("Registro de memoria archivado.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  return (
    <div className="page">
      <PageHeader eyebrow="Datos personales" title="Memoria" description="Base privada de sesiones, contexto y mensajes guardados únicamente para tu cuenta." actions={<button className="button button-primary" onClick={() => navigate("/studio?new=1")}><Plus size={16} /> Nueva sesión</button>} />
      <section className="metric-grid memory-metrics">
        <MetricCard icon={<Database size={19} />} label="Registros" value={formatNumber(threads.data?.length || 0)} detail="Memorias activas" />
        <MetricCard icon={<MessageSquareText size={19} />} label="Entradas" value={formatNumber((threads.data || []).reduce((total, item) => total + Number(item.message_count || 0), 0))} detail="Mensajes persistentes" tone="accent" />
        <MetricCard icon={<ShieldCheck size={19} />} label="Propiedad" value="Privada" detail="Aislada por usuario" tone="positive" />
        <MetricCard icon={<Clock3 size={19} />} label="Actualización" value={threads.data?.[0]?.updated_at ? formatDate(threads.data[0].updated_at) : "—"} detail="Última escritura" />
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>Registros de memoria</h2><p>Continúa una sesión o archiva el contexto que ya no necesites.</p></div><label className="search-field compact"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar en memoria…" /></label></div>
        {threads.error && <ErrorState error={threads.error} onRetry={() => threads.refetch()} />}
        {threads.isLoading && <LoadingBlock />}
        {filtered.length ? <div className="memory-db-table">
          <div className="memory-db-head"><span>ID / memoria</span><span>Contrato</span><span>Modelo</span><span>Filas</span><span>Última escritura</span><span /></div>
          {filtered.map((thread) => (
            <div key={thread.id} className="memory-db-row">
              <button className="memory-db-primary" onClick={() => navigate(`/memory/${thread.id}`)}><strong>{thread.title}</strong><code>{thread.id}</code></button>
              <span className="badge">{thread.request_mode}</span>
              <code>{thread.model_alias || "ak-auto"}</code>
              <span>{formatNumber(thread.message_count || 0)}</span>
              <time>{formatDate(thread.last_message_at || thread.updated_at)}</time>
              <span className="inline-actions"><button className="icon-button" title="Abrir" onClick={() => navigate(`/memory/${thread.id}`)}><ArrowRight size={16} /></button><button className="icon-button danger" title="Archivar memoria" onClick={() => setDeleteId(thread.id)}><Trash2 size={16} /></button></span>
            </div>
          ))}
        </div> : !threads.isLoading && !threads.error && <EmptyState icon={<Database />} title={query ? "Sin coincidencias" : "Tu memoria está vacía"} description={query ? "Prueba con otra búsqueda." : "Las sesiones persistentes de Studio aparecerán aquí."} />}
      </section>
      <ConfirmModal open={Boolean(deleteId)} title="Archivar registro" description="Este registro dejará de aparecer en tu memoria activa." confirmLabel="Archivar" busy={remove.isPending} onClose={() => setDeleteId(null)} onConfirm={() => deleteId && remove.mutate(deleteId)} />
    </div>
  );
}

function ThreadDetail() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [location, navigate] = useLocation();
  const id = location.split("/").pop();
  const thread = useQuery({ queryKey: ["thread", id], queryFn: () => api<any>(`/api/v1/threads/${id}`) });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => api(`/api/v1/threads/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["threads"] });
      toast("Registro de memoria archivado.", "success");
      navigate("/memory");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  return (
    <div className="page">
      <PageHeader eyebrow="Memoria privada" title={thread.data?.thread?.title || "Registro"} description={`${thread.data?.thread?.request_mode || "professional"} · ${thread.data?.messages?.length || 0} entradas · ${thread.data?.thread?.model_alias || "auto"}`} actions={<div className="inline-actions"><button className="button button-secondary" onClick={() => navigate("/memory")}><RotateCcw size={15} /> Volver</button><button className="button button-primary" disabled={!thread.data} onClick={() => navigate(`/studio?thread=${id}`)}><Send size={15} /> Continuar en Studio</button><button className="button button-secondary danger" onClick={() => setConfirmDelete(true)}><Trash2 size={15} /> Archivar</button></div>} />
      <section className="panel memory-database-panel">
        <div className="panel-header"><div><h2>Solicitudes almacenadas</h2><p>Petición, respuesta y telemetría completa agrupadas por request ID.</p></div><span className="badge">{thread.data?.requests?.length || 0} transacciones</span></div>
        {thread.error && <ErrorState error={thread.error} onRetry={() => thread.refetch()} />}
        {thread.isLoading && <LoadingBlock />}
        {thread.data?.requests?.length ? <div className="memory-request-table">
          <div className="memory-request-head"><span>Request ID</span><span>Solicitud</span><span>Respuesta</span><span>Modelo / proveedor</span><span>Tokens</span><span>Tiempo</span><span>Estado</span></div>
          {thread.data.requests.map((entry: any, index: number) => {
            const requestContent = entry.request?.content ?? "";
            const responseContent = entry.response?.content ?? "";
            const requestText = typeof requestContent === "string" ? requestContent : JSON.stringify(requestContent, null, 2);
            const responseText = typeof responseContent === "string" ? responseContent : JSON.stringify(responseContent, null, 2);
            const telemetry = entry.telemetry || {};
            const usage = entry.response?.usage_data || {};
            return <details className="memory-request-record" key={entry.request_id || index}>
              <summary>
                <code title={entry.request_id || "Sin request ID"}>{entry.request_id ? `${entry.request_id.slice(0, 8)}…` : "legacy"}</code>
                <span title={requestText}>{requestText.slice(0, 90) || "—"}</span>
                <span title={responseText}>{responseText.slice(0, 90) || "—"}</span>
                <span><strong>{entry.response?.model || telemetry.model || "—"}</strong><small>{entry.response?.provider || telemetry.provider || "—"}</small></span>
                <span>{formatNumber(Number(usage.input_tokens || telemetry.input_tokens || 0) + Number(usage.output_tokens || telemetry.output_tokens || 0))}</span>
                <span>{formatLatency(entry.response?.latency_ms ?? telemetry.latency_ms)}</span>
                <span className={`badge ${Number(telemetry.status_code || 200) < 400 ? "badge-success" : ""}`}>{telemetry.status_code || 200}</span>
              </summary>
              <div className="memory-record-detail">
                <article><div className="memory-record-title"><strong>Solicitud completa</strong><button className="icon-button" onClick={() => copyToClipboard(requestText, toast)}><Copy size={14} /></button></div>{typeof requestContent === "string" ? <MarkdownContent content={requestContent} /> : <pre className="json-inspector">{requestText}</pre>}</article>
                <article><div className="memory-record-title"><strong>Respuesta completa</strong><button className="icon-button" onClick={() => copyToClipboard(responseText, toast)}><Copy size={14} /></button></div>{typeof responseContent === "string" ? <MarkdownContent content={responseContent} /> : <pre className="json-inspector">{responseText}</pre>}</article>
                <article className="memory-telemetry"><strong>Telemetría y ejecución</strong><pre className="json-inspector">{JSON.stringify({ telemetry, metadata: entry.response?.metadata || {}, usage, tool_calls: entry.response?.tool_calls || [], ui_blocks: entry.response?.ui_blocks || [], created_at: entry.created_at }, null, 2)}</pre></article>
              </div>
            </details>;
          })}
        </div> : null}
        {!thread.isLoading && !thread.error && !thread.data?.messages?.length && <EmptyState icon={<Database />} title="Registro vacío" description="Aún no contiene entradas." />}
      </section>
      <ConfirmModal open={confirmDelete} title="Archivar memoria" description="El registro dejará de aparecer en tu base de memoria activa." confirmLabel="Archivar" busy={remove.isPending} onClose={() => setConfirmDelete(false)} onConfirm={() => remove.mutate()} />
    </div>
  );
}

function Usage() {
  const toast = useToast();
  const usage = useQuery({ queryKey: ["usage"], queryFn: () => api<any>("/api/v1/usage") });
  const daily = usage.data?.daily || [];
  const totals = daily.reduce((result: { requests: number; tokens: number; credits: number; byok: number }, item: any) => ({
    requests: result.requests + Number(item.requests || 0),
    tokens: result.tokens + Number(item.input_tokens || 0) + Number(item.output_tokens || 0),
    credits: result.credits + Number(item.credits || 0),
    byok: result.byok + Number(item.byok_requests || 0)
  }), { requests: 0, tokens: 0, credits: 0, byok: 0 });
  const maxTokens = Math.max(1, ...daily.map((item: any) => Number(item.input_tokens || 0) + Number(item.output_tokens || 0)));
  const exportCsv = () => {
    if (!daily.length) {
      toast("No hay datos para exportar.", "info");
      return;
    }
    const lines = [
      "fecha,solicitudes,solicitudes_byok,tokens_entrada,tokens_salida,creditos,latencia_media_ms",
      ...daily.map((item: any) => [item.day, item.requests, item.byok_requests, item.input_tokens, item.output_tokens, item.credits, item.avg_latency_ms].join(","))
    ];
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `ak-uso-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast("Reporte CSV descargado.", "success");
  };
  return (
    <div className="page">
      <PageHeader title="Uso y analítica" description="Consumo por día, proveedor y modelo durante los últimos 30 días." actions={<button className="button button-secondary" onClick={exportCsv}><Download size={16} /> Exportar CSV</button>} />
      {usage.error && <ErrorState error={usage.error} onRetry={() => usage.refetch()} />}
      {usage.isLoading && <LoadingBlock />}
      {!usage.isLoading && !usage.error && <section className="metric-grid">
        <MetricCard icon={<Activity size={19} />} label="Solicitudes" value={formatNumber(totals.requests)} detail="Últimos 30 días" />
        <MetricCard icon={<Braces size={19} />} label="Tokens" value={formatNumber(totals.tokens)} detail="Entrada + salida" tone="accent" />
        <MetricCard icon={<Gauge size={19} />} label="Créditos" value={formatNumber(totals.credits)} detail="Consumo registrado" tone="positive" />
        <MetricCard icon={<KeyRound size={19} />} label="Solicitudes BYOK" value={formatNumber(totals.byok)} detail="0 créditos AK" />
      </section>}
      <section className="panel chart-panel large">
        <div className="panel-header"><div><h2>Consumo diario</h2><p>Tokens de entrada y salida.</p></div><span className="badge">30 días</span></div>
        {daily.length ? <div className="chart-placeholder" aria-label="Gráfico de tokens diarios">
          {daily.map((item: any, index: number) => (
            <span key={index} title={`${String(item.day).slice(0, 10)}: ${formatNumber(Number(item.input_tokens || 0) + Number(item.output_tokens || 0))} tokens`} style={{ height: `${Math.max(4, ((Number(item.input_tokens || 0) + Number(item.output_tokens || 0)) / maxTokens) * 100)}%` }} />
          ))}
        </div> : !usage.isLoading && !usage.error && <EmptyState icon={<BarChart3 />} title="Todavía no hay consumo" description="Las solicitudes aparecerán aquí en cuanto uses Studio o la API." />}
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>Distribución por rutas</h2><p>Volumen y latencia media del orquestador.</p></div></div>
        <div className="table">
          <div className="table-head usage-table"><span>Proveedor</span><span>Ruta</span><span>Solicitudes</span><span>Tokens</span><span>Latencia</span></div>
          {usage.data?.providers?.map((row: any) => <div className="table-row usage-table" key={`${row.provider}-${row.model}-${row.billing_source}`}><span className="cell-primary"><Cpu size={16} />{row.provider} <span className={`badge ${row.billing_source === "byok" ? "badge-success" : ""}`}>{row.billing_source === "byok" ? "Clave propia" : "AK"}</span></span><span className="mono">Ruta administrada</span><span>{formatNumber(row.requests)}</span><span>{formatNumber(row.tokens)}</span><span>{formatLatency(row.avg_latency_ms)}</span></div>)}
        </div>
        {!usage.isLoading && !usage.error && !usage.data?.providers?.length && <EmptyState icon={<Cpu />} title="Sin distribución disponible" description="No hay uso de rutas durante este periodo." />}
      </section>
    </div>
  );
}

function LegacyOperations({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const workspaces = useQuery({ queryKey: ["workspaces"], queryFn: () => api<any[]>("/api/v1/workspaces") });
  const security = useQuery({ queryKey: ["security-center"], queryFn: () => api<any>("/api/v1/security-center") });
  const webhooks = useQuery({ queryKey: ["webhooks"], queryFn: () => api<any>("/api/v1/webhooks") });
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState("");
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEvents, setWebhookEvents] = useState<string[]>(["ai.response.completed"]);
  const [webhookSecret, setWebhookSecret] = useState("");
  const createWorkspace = useMutation({
    mutationFn: () => api<any>("/api/v1/workspaces", { method: "POST", body: JSON.stringify({ name: workspaceName }) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["workspaces"] }); setWorkspaceOpen(false); setWorkspaceName(""); toast("Workspace creado con un proyecto Default.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const createWebhook = useMutation({
    mutationFn: () => api<any>("/api/v1/webhooks", { method: "POST", body: JSON.stringify({ url: webhookUrl, events: webhookEvents }) }),
    onSuccess: (data) => { queryClient.invalidateQueries({ queryKey: ["webhooks"] }); setWebhookOpen(false); setWebhookUrl(""); setWebhookSecret(data.secret); toast("Webhook creado. Copia el secreto ahora.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const removeWebhook = useMutation({
    mutationFn: (id: string) => api(`/api/v1/webhooks/${id}`, { method: "DELETE" }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["webhooks"] }); toast("Webhook eliminado.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const exportAccount = async () => {
    try {
      const data = await api<any>("/api/v1/account/export");
      downloadContent(`ak-account-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(data, null, 2), "application/json");
    } catch (error) { toast((error as Error).message, "error"); }
  };
  return <div className="page">
    <PageHeader eyebrow="Enterprise foundation" title="Operaciones" description="Workspaces, proyectos, webhooks firmados, seguridad y herramientas para integrar AK en producción." actions={<div className="inline-actions"><a className="button button-secondary" href="/openapi.json" target="_blank" rel="noreferrer"><Braces size={15} /> OpenAPI 3.1</a><button className="button button-primary" onClick={() => setWorkspaceOpen(true)}><Plus size={15} /> Workspace</button></div>} />
    <section className="metric-grid"><MetricCard icon={<Network size={19} />} label="Workspaces" value={formatNumber(workspaces.data?.length || 0)} detail="Aislamiento organizativo" /><MetricCard icon={<ShieldCheck size={19} />} label="Sesiones" value={formatNumber(security.data?.sessions?.length || 0)} detail="Sesiones activas" tone="positive" /><MetricCard icon={<KeyRound size={19} />} label="Credenciales" value={formatNumber((security.data?.api_keys?.length || 0) + (security.data?.provider_keys?.length || 0))} detail="API + BYOK" /><MetricCard icon={<Send size={19} />} label="Webhooks" value={formatNumber(webhooks.data?.webhooks?.length || 0)} detail="Entrega HMAC y reintentos" tone="accent" /></section>
    <div className="two-column-grid">
      <section className="panel"><div className="panel-header"><div><h2>Workspaces y proyectos</h2><p>Base para separar equipos, entornos y presupuestos.</p></div></div>{workspaces.isLoading ? <LoadingBlock /> : workspaces.error ? <ErrorState error={workspaces.error} onRetry={() => workspaces.refetch()} /> : <div className="billing-history-list">{workspaces.data?.map((workspace) => <div className="history-row" key={workspace.id}><div><strong>{workspace.name}</strong><span>{workspace.role} · {workspace.member_count} miembro(s)</span></div><div><span>{workspace.project_count} proyecto(s)</span><code>{workspace.slug}</code></div></div>)}</div>}</section>
      <section className="panel"><div className="panel-header"><div><h2>Centro de seguridad</h2><p>Inventario de acceso y recomendaciones.</p></div><button className="button button-ghost" onClick={() => security.refetch()}><RefreshCw size={15} /> Actualizar</button></div>{security.isLoading ? <LoadingBlock /> : security.error ? <ErrorState error={security.error} onRetry={() => security.refetch()} /> : <><div className="security-score"><ShieldCheck size={28} /><div><strong>{security.data?.events?.some((event: any) => event.severity === "critical" && !event.resolved_at) ? "Revisión requerida" : "Sin alertas críticas"}</strong><p>{security.data?.recommendations?.[0]}</p></div></div><button className="button button-secondary" onClick={exportAccount}><Download size={15} /> Exportar mis datos</button></>}</section>
    </div>
    <section className="panel"><div className="panel-header"><div><h2>Webhooks firmados</h2><p>Eventos asíncronos con HMAC-SHA256, cinco intentos y backoff exponencial.</p></div><button className="button button-primary" onClick={() => setWebhookOpen(true)}><Plus size={15} /> Añadir endpoint</button></div>{webhooks.isLoading ? <LoadingBlock /> : webhooks.error ? <ErrorState error={webhooks.error} onRetry={() => webhooks.refetch()} /> : <div className="table"><div className="table-head"><span>Endpoint</span><span>Eventos</span><span>Entregas</span><span>Estado</span><span /></div>{webhooks.data?.webhooks?.map((webhook: any) => <div className="table-row" key={webhook.id}><code>{webhook.url}</code><span>{webhook.events.join(", ")}</span><span>{webhook.delivered} ok · {webhook.failed} fallidas</span><span className="badge">{webhook.status}</span><button className="icon-button danger" onClick={() => removeWebhook.mutate(webhook.id)}><Trash2 size={15} /></button></div>)}</div>}</section>
    <section className="panel"><div className="panel-header"><div><h2>Recursos para desarrolladores</h2><p>Contrato portable y clientes mínimos sin dependencias del dashboard.</p></div></div><div className="inline-actions"><a className="button button-secondary" href="/sdks/javascript"><Download size={15} /> SDK JavaScript</a><a className="button button-secondary" href="/sdks/python"><Download size={15} /> SDK Python</a><NavLink className="button button-secondary" to="/docs"><BookOpen size={15} /> Guía completa</NavLink>{user.role === "admin" && <a className="button button-secondary" href="/metrics" target="_blank" rel="noreferrer"><Activity size={15} /> Prometheus</a>}</div></section>
    <FormModal open={workspaceOpen} title="Nuevo workspace" description="Se creará con tu rol Owner y un proyecto Default." submitLabel="Crear workspace" busy={createWorkspace.isPending} onClose={() => setWorkspaceOpen(false)} onSubmit={(event) => { event.preventDefault(); if (workspaceName.trim()) createWorkspace.mutate(); }}><label className="field"><span>Nombre</span><input required minLength={2} maxLength={160} value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} /></label></FormModal>
    <FormModal open={webhookOpen} title="Nuevo webhook" description="El endpoint debe usar HTTPS. El secreto se mostrará una sola vez." submitLabel="Crear webhook" busy={createWebhook.isPending} onClose={() => setWebhookOpen(false)} onSubmit={(event) => { event.preventDefault(); if (webhookUrl && webhookEvents.length) createWebhook.mutate(); }}><label className="field"><span>Endpoint HTTPS</span><input required type="url" value={webhookUrl} onChange={(event) => setWebhookUrl(event.target.value)} placeholder="https://tu-app.com/webhooks/ak" /></label><div className="tool-picker-list">{(webhooks.data?.available_events || ["ai.response.completed", "artifact.created", "generation.queued"]).map((event: string) => <label className="tool-picker-item" key={event}><input type="checkbox" checked={webhookEvents.includes(event)} onChange={() => setWebhookEvents((current) => current.includes(event) ? current.filter((item) => item !== event) : [...current, event])} /><span><strong>{event}</strong><small>Entrega JSON firmada</small></span></label>)}</div></FormModal>
    <Modal open={Boolean(webhookSecret)} title="Secreto del webhook" description="Guárdalo ahora; AK no volverá a mostrarlo." onClose={() => setWebhookSecret("")} footer={<button className="button button-primary" onClick={() => copyToClipboard(webhookSecret, toast, "Secreto copiado.")}><Copy size={15} /> Copiar secreto</button>}><div className="secret-box"><code>{webhookSecret}</code></div></Modal>
  </div>;
}

function Operations({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<"organization" | "security" | "webhooks" | "developer">("organization");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(() => localStorage.getItem("ak_active_workspace") || "");
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", description: "", brand_color: "#2563eb" });
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberForm, setMemberForm] = useState({ email: "", role: "developer" });
  const [removeMember, setRemoveMember] = useState<any | null>(null);
  const [projectOpen, setProjectOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<any | null>(null);
  const [projectForm, setProjectForm] = useState({ name: "", description: "", monthly_credit_budget: "" });
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEvents, setWebhookEvents] = useState<string[]>(["ai.response.completed"]);
  const [webhookSecret, setWebhookSecret] = useState("");

  const workspaces = useQuery({ queryKey: ["workspaces"], queryFn: () => api<any[]>("/api/v1/workspaces") });
  const detail = useQuery({
    queryKey: ["workspace-detail", selectedWorkspaceId],
    queryFn: () => api<any>(`/api/v1/workspaces/${selectedWorkspaceId}`),
    enabled: Boolean(selectedWorkspaceId)
  });
  const security = useQuery({ queryKey: ["security-center"], queryFn: () => api<any>("/api/v1/security-center"), enabled: tab === "security" });
  const webhooks = useQuery({ queryKey: ["webhooks"], queryFn: () => api<any>("/api/v1/webhooks"), enabled: tab === "webhooks" });
  const selectedWorkspace = detail.data?.workspace;
  const capabilities = detail.data?.capabilities || {};
  const refreshWorkspace = () => {
    queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    queryClient.invalidateQueries({ queryKey: ["workspace-detail", selectedWorkspaceId] });
  };

  useEffect(() => {
    if (!workspaces.data?.length) return;
    if (!selectedWorkspaceId || !workspaces.data.some((workspace) => workspace.id === selectedWorkspaceId)) setSelectedWorkspaceId(workspaces.data[0].id);
  }, [workspaces.data, selectedWorkspaceId]);
  useEffect(() => {
    const sync = (event: Event) => {
      const workspaceId = (event as CustomEvent<{ workspaceId?: string }>).detail?.workspaceId;
      if (workspaceId) setSelectedWorkspaceId(workspaceId);
    };
    window.addEventListener("ak:workspace-changed", sync);
    return () => window.removeEventListener("ak:workspace-changed", sync);
  }, []);

  const activateWorkspace = (projectId?: string) => {
    if (!selectedWorkspaceId) return;
    const fallbackProject = detail.data?.projects?.find((project: any) => project.status === "active")?.id || "";
    const selectedProject = projectId || fallbackProject;
    localStorage.setItem("ak_active_workspace", selectedWorkspaceId);
    if (selectedProject) localStorage.setItem("ak_active_project", selectedProject); else localStorage.removeItem("ak_active_project");
    window.dispatchEvent(new CustomEvent("ak:workspace-changed", { detail: { workspaceId: selectedWorkspaceId, projectId: selectedProject } }));
    queryClient.invalidateQueries({ predicate: (query) => !["me", "workspaces", "workspace-detail"].includes(String(query.queryKey[0])) });
    toast(`${selectedWorkspace?.name || "Workspace"} es ahora tu contexto activo.`, "success");
  };

  const createWorkspace = useMutation({
    mutationFn: () => api<any>("/api/v1/workspaces", { method: "POST", body: JSON.stringify({ name: workspaceName }) }),
    onSuccess: (created) => {
      setWorkspaceOpen(false); setWorkspaceName(""); setSelectedWorkspaceId(created.id); refreshWorkspace();
      localStorage.setItem("ak_active_workspace", created.id); localStorage.setItem("ak_active_project", created.default_project_id);
      window.dispatchEvent(new CustomEvent("ak:workspace-changed", { detail: { workspaceId: created.id, projectId: created.default_project_id } }));
      toast("Organización creada y seleccionada.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const saveProfile = useMutation({
    mutationFn: () => api(`/api/v1/workspaces/${selectedWorkspaceId}`, { method: "PATCH", body: JSON.stringify(profileForm) }),
    onSuccess: () => { setProfileOpen(false); refreshWorkspace(); toast("Perfil de empresa actualizado.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const addMember = useMutation({
    mutationFn: () => api(`/api/v1/workspaces/${selectedWorkspaceId}/members`, { method: "POST", body: JSON.stringify(memberForm) }),
    onSuccess: () => { setMemberOpen(false); setMemberForm({ email: "", role: "developer" }); refreshWorkspace(); toast("Miembro añadido al workspace.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const updateMember = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => api(`/api/v1/workspaces/${selectedWorkspaceId}/members/${userId}`, { method: "PATCH", body: JSON.stringify({ role }) }),
    onSuccess: () => { refreshWorkspace(); toast("Rol actualizado.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const deleteMember = useMutation({
    mutationFn: (userId: string) => api(`/api/v1/workspaces/${selectedWorkspaceId}/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => { setRemoveMember(null); refreshWorkspace(); toast("Miembro retirado.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const saveProject = useMutation({
    mutationFn: () => api(editingProject ? `/api/v1/workspaces/${selectedWorkspaceId}/projects/${editingProject.id}` : `/api/v1/workspaces/${selectedWorkspaceId}/projects`, {
      method: editingProject ? "PATCH" : "POST",
      body: JSON.stringify({ name: projectForm.name, description: projectForm.description, monthly_credit_budget: projectForm.monthly_credit_budget ? Number(projectForm.monthly_credit_budget) : null })
    }),
    onSuccess: () => { setProjectOpen(false); setEditingProject(null); setProjectForm({ name: "", description: "", monthly_credit_budget: "" }); refreshWorkspace(); toast(`Proyecto ${editingProject ? "actualizado" : "creado"}.`, "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const archiveProject = useMutation({
    mutationFn: (project: any) => api(`/api/v1/workspaces/${selectedWorkspaceId}/projects/${project.id}`, { method: "PATCH", body: JSON.stringify({ status: project.status === "active" ? "archived" : "active" }) }),
    onSuccess: () => { refreshWorkspace(); toast("Estado del proyecto actualizado.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const createWebhook = useMutation({
    mutationFn: () => api<any>("/api/v1/webhooks", { method: "POST", body: JSON.stringify({ url: webhookUrl, events: webhookEvents }) }),
    onSuccess: (data) => { queryClient.invalidateQueries({ queryKey: ["webhooks"] }); setWebhookOpen(false); setWebhookUrl(""); setWebhookSecret(data.secret); toast("Webhook creado.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const removeWebhook = useMutation({
    mutationFn: (id: string) => api(`/api/v1/webhooks/${id}`, { method: "DELETE" }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["webhooks"] }); toast("Webhook eliminado.", "success"); },
    onError: (error: Error) => toast(error.message, "error")
  });
  const exportAccount = async () => {
    try {
      const data = await api<any>("/api/v1/account/export");
      downloadContent(`ak-account-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(data, null, 2), "application/json");
    } catch (error) { toast((error as Error).message, "error"); }
  };
  const openProfile = () => {
    setProfileForm({ name: selectedWorkspace?.name || "", description: selectedWorkspace?.description || "", brand_color: selectedWorkspace?.brand_color || "#2563eb" });
    setProfileOpen(true);
  };
  const openProject = (project?: any) => {
    setEditingProject(project || null);
    setProjectForm({ name: project?.name || "", description: project?.description || "", monthly_credit_budget: project?.monthly_credit_budget == null ? "" : String(project.monthly_credit_budget) });
    setProjectOpen(true);
  };

  return <div className="page operations-page">
    <PageHeader eyebrow="Organization control plane" title="Operaciones" description="Gestiona equipos, permisos, proyectos, seguridad e integraciones desde un único espacio de control." actions={<div className="inline-actions"><label className="workspace-admin-select"><Building2 size={15} /><select value={selectedWorkspaceId} onChange={(event) => setSelectedWorkspaceId(event.target.value)}>{workspaces.data?.map((workspace) => <option value={workspace.id} key={workspace.id}>{workspace.name}</option>)}</select></label><button className="button button-primary" onClick={() => setWorkspaceOpen(true)}><Plus size={15} /> Organización</button></div>} />
    <div className="operations-tabs" role="tablist">
      <button className={tab === "organization" ? "active" : ""} onClick={() => setTab("organization")}><Building2 size={16} /> Organización</button>
      <button className={tab === "security" ? "active" : ""} onClick={() => setTab("security")}><ShieldCheck size={16} /> Seguridad</button>
      <button className={tab === "webhooks" ? "active" : ""} onClick={() => setTab("webhooks")}><Send size={16} /> Webhooks</button>
      <button className={tab === "developer" ? "active" : ""} onClick={() => setTab("developer")}><Braces size={16} /> Desarrolladores</button>
    </div>

    {tab === "organization" && <>
      {detail.isLoading && <LoadingBlock />}
      {detail.error && <ErrorState error={detail.error} onRetry={() => detail.refetch()} />}
      {selectedWorkspace && <>
        <section className="organization-hero" style={{ "--org-color": selectedWorkspace.brand_color || "#2563eb" } as React.CSSProperties}>
          <span className="organization-mark"><Building2 size={27} /></span>
          <div><span className="eyebrow">{selectedWorkspace.role} · {selectedWorkspace.slug}</span><h2>{selectedWorkspace.name}</h2><p>{selectedWorkspace.description || "Añade una descripción para que el equipo entienda el propósito de este workspace."}</p></div>
          <div className="organization-actions"><button className="button button-secondary" onClick={() => activateWorkspace()}><Check size={15} /> Usar workspace</button>{capabilities.manage_workspace && <button className="button button-secondary" onClick={openProfile}><Pencil size={15} /> Editar perfil</button>}</div>
        </section>
        <section className="metric-grid organization-metrics"><MetricCard icon={<Users size={19} />} label="Miembros" value={formatNumber(detail.data.members.length)} detail="Accesos asignados" /><MetricCard icon={<FolderKanban size={19} />} label="Proyectos" value={formatNumber(detail.data.projects.filter((project: any) => project.status === "active").length)} detail="Entornos activos" /><MetricCard icon={<MessageSquareText size={19} />} label="Solicitudes" value={formatNumber(detail.data.stats.requests)} detail="Mes en curso" tone="positive" /><MetricCard icon={<CircleDollarSign size={19} />} label="Créditos" value={formatNumber(detail.data.stats.credits)} detail="Consumo mensual" tone="accent" /></section>
        <div className="organization-grid">
          <section className="panel organization-members"><div className="panel-header"><div><h2>Equipo y permisos</h2><p>Roles claros para operar recursos compartidos.</p></div>{capabilities.manage_members && <button className="button button-primary" onClick={() => setMemberOpen(true)}><UserPlus size={15} /> Añadir miembro</button>}</div>
            <div className="member-table"><div className="member-table-head"><span>Miembro</span><span>Rol</span><span>Estado</span><span /></div>{detail.data.members.map((member: any) => <div className="member-row" key={member.user_id}><div className="member-identity"><span className="avatar">{member.avatar_url ? <img src={member.avatar_url} alt="" /> : member.name.slice(0, 1)}</span><span><strong>{member.name}</strong><small>{member.email}</small></span></div><div>{member.role === "owner" || !capabilities.manage_members ? <span className={`role-badge role-${member.role}`}>{member.role}</span> : <select value={member.role} onChange={(event) => updateMember.mutate({ userId: member.user_id, role: event.target.value })}><option value="admin">Admin</option><option value="developer">Developer</option><option value="viewer">Viewer</option></select>}</div><span className="badge badge-success">{member.status}</span><span>{member.role !== "owner" && capabilities.manage_members && <button className="icon-button danger" title="Retirar miembro" onClick={() => setRemoveMember(member)}><Trash2 size={15} /></button>}</span></div>)}</div>
            <div className="permission-legend"><div><strong>Owner</strong><span>Control total y propiedad.</span></div><div><strong>Admin</strong><span>Equipo, perfil y proyectos.</span></div><div><strong>Developer</strong><span>Crea y usa recursos.</span></div><div><strong>Viewer</strong><span>Acceso de solo lectura.</span></div></div>
          </section>
          <section className="panel organization-projects"><div className="panel-header"><div><h2>Proyectos</h2><p>Separa memoria, artefactos, Skills y consumo.</p></div>{capabilities.manage_projects && <button className="button button-secondary" onClick={() => openProject()}><Plus size={15} /> Proyecto</button>}</div>
            <div className="project-card-list">{detail.data.projects.map((project: any) => <article className={`project-card ${project.status}`} key={project.id}><div className="project-card-head"><span className="project-icon"><FolderKanban size={17} /></span><span className={`badge ${project.status === "active" ? "badge-success" : ""}`}>{project.status}</span></div><h3>{project.name}</h3><p>{project.description || "Sin descripción"}</p><div className="project-stats"><span><strong>{formatNumber(project.threads)}</strong> memorias</span><span><strong>{formatNumber(project.artifacts)}</strong> artefactos</span><span><strong>{formatNumber(project.requests)}</strong> solicitudes</span></div><div className="project-budget"><span>Presupuesto</span><strong>{project.monthly_credit_budget ? `${formatNumber(project.monthly_credit_budget)} créditos/mes` : "Sin límite propio"}</strong></div><div className="skill-card-actions">{project.status === "active" && <button className="button button-primary" onClick={() => activateWorkspace(project.id)}><Play size={14} /> Abrir contexto</button>}{capabilities.manage_projects && <button className="icon-button" title="Editar" onClick={() => openProject(project)}><Pencil size={15} /></button>}{capabilities.manage_projects && <button className="icon-button" title={project.status === "active" ? "Archivar" : "Restaurar"} onClick={() => archiveProject.mutate(project)}><RotateCcw size={15} /></button>}</div></article>)}</div>
          </section>
        </div>
      </>}
    </>}

    {tab === "security" && <section className="security-dashboard">
      <div className="security-overview"><span className="security-shield"><ShieldCheck size={30} /></span><div><span className="eyebrow">Postura de acceso</span><h2>{security.data?.events?.some((event: any) => event.severity === "critical" && !event.resolved_at) ? "Revisión requerida" : "Sin alertas críticas"}</h2><p>Revisa sesiones, credenciales y actividad sensible sin exponer secretos.</p></div><button className="button button-secondary" onClick={() => security.refetch()}><RefreshCw size={15} /> Actualizar</button></div>
      {security.isLoading && <LoadingBlock />}{security.error && <ErrorState error={security.error} onRetry={() => security.refetch()} />}
      {security.data && <><section className="metric-grid"><MetricCard icon={<Users size={19} />} label="Sesiones" value={formatNumber(security.data.sessions.length)} detail="Accesos vigentes" /><MetricCard icon={<KeyRound size={19} />} label="Claves AK" value={formatNumber(security.data.api_keys.length)} detail="Credenciales de API" /><MetricCard icon={<Cpu size={19} />} label="Claves BYOK" value={formatNumber(security.data.provider_keys.length)} detail="Proveedores propios" /><MetricCard icon={<Activity size={19} />} label="Eventos" value={formatNumber(security.data.events.length)} detail="Últimos 100 registros" /></section><div className="security-grid"><section className="panel"><div className="panel-header"><div><h2>Sesiones activas</h2><p>Dispositivos con acceso a tu cuenta.</p></div></div><div className="activity-list">{security.data.sessions.map((session: any) => <div className="activity-row" key={session.id}><span className="activity-icon"><TerminalSquare size={16} /></span><div><strong>{session.user_agent || "Cliente desconocido"}</strong><span>IP protegida {session.ip_hash?.slice(0, 12)}… · visto {formatDate(session.last_seen_at)}</span></div><span className="badge">vence {formatDate(session.expires_at)}</span></div>)}</div></section><section className="panel"><div className="panel-header"><div><h2>Eventos de seguridad</h2><p>Registro legible de actividad sensible.</p></div></div><div className="activity-list">{security.data.events.slice(0, 10).map((event: any) => <div className="activity-row" key={event.id}><span className={`severity-dot severity-${event.severity}`} /><div><strong>{event.event_type}</strong><span>{event.description} · {formatDate(event.created_at)}</span></div><span className="badge">{event.severity}</span></div>)}{!security.data.events.length && <EmptyState icon={<ShieldCheck />} title="Sin eventos" description="No hay alertas de seguridad registradas." />}</div></section></div><div className="security-footer"><div>{security.data.recommendations.map((recommendation: string) => <span key={recommendation}><Check size={14} /> {recommendation}</span>)}</div><button className="button button-secondary" onClick={exportAccount}><Download size={15} /> Exportar mis datos</button></div></>}
    </section>}

    {tab === "webhooks" && <section className="panel"><div className="panel-header"><div><h2>Webhooks firmados</h2><p>Automatiza eventos con firma HMAC-SHA256, reintentos y estado de entrega.</p></div><button className="button button-primary" onClick={() => setWebhookOpen(true)}><Plus size={15} /> Endpoint</button></div>{webhooks.isLoading ? <LoadingBlock /> : webhooks.error ? <ErrorState error={webhooks.error} onRetry={() => webhooks.refetch()} /> : <div className="webhook-card-list">{webhooks.data?.webhooks?.map((webhook: any) => <article className="webhook-card" key={webhook.id}><div className="webhook-status"><span className="status-dot" /><span>{webhook.status}</span></div><code>{webhook.url}</code><div className="webhook-events">{webhook.events.map((event: string) => <span key={event}>{event}</span>)}</div><div className="webhook-delivery"><span><strong>{webhook.delivered}</strong> entregadas</span><span><strong>{webhook.failed}</strong> fallidas</span><span>{webhook.last_delivery_at ? `Última: ${formatDate(webhook.last_delivery_at)}` : "Sin entregas"}</span></div><button className="icon-button danger" onClick={() => removeWebhook.mutate(webhook.id)}><Trash2 size={15} /></button></article>)}{!webhooks.data?.webhooks?.length && <EmptyState icon={<Send />} title="Sin endpoints" description="Añade un webhook cuando necesites recibir eventos fuera de AK." />}</div>}</section>}

    {tab === "developer" && <div className="developer-resource-grid"><article className="developer-resource-card"><span><Braces size={20} /></span><h3>OpenAPI 3.1</h3><p>Explora el contrato completo de rutas, esquemas y errores.</p><a className="button button-secondary" href="/openapi.json" target="_blank" rel="noreferrer">Abrir especificación <ExternalLink size={14} /></a></article><article className="developer-resource-card"><span><FileCode2 size={20} /></span><h3>SDK JavaScript</h3><p>Cliente ligero para Node.js, workers y aplicaciones web.</p><a className="button button-secondary" href="/sdks/javascript">Descargar SDK <Download size={14} /></a></article><article className="developer-resource-card"><span><TerminalSquare size={20} /></span><h3>SDK Python</h3><p>Integra flujos de datos, agentes y automatizaciones.</p><a className="button button-secondary" href="/sdks/python">Descargar SDK <Download size={14} /></a></article><article className="developer-resource-card"><span><BookOpen size={20} /></span><h3>Documentación pública</h3><p>Guías de solicitudes, visión, Skills, conectores y workspaces.</p><NavLink className="button button-secondary" to="/docs">Leer documentación <ArrowRight size={14} /></NavLink></article>{user.role === "admin" && <article className="developer-resource-card"><span><Activity size={20} /></span><h3>Prometheus</h3><p>Métricas protegidas para observabilidad de producción.</p><a className="button button-secondary" href="/metrics" target="_blank" rel="noreferrer">Abrir métricas <ExternalLink size={14} /></a></article>}</div>}

    <FormModal open={workspaceOpen} title="Nueva organización" description="Crea un workspace real con un proyecto inicial y aislamiento de recursos." submitLabel="Crear organización" busy={createWorkspace.isPending} onClose={() => setWorkspaceOpen(false)} onSubmit={(event) => { event.preventDefault(); if (workspaceName.trim()) createWorkspace.mutate(); }}><label className="field"><span>Nombre de la empresa o equipo</span><input required minLength={2} maxLength={160} value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} /></label></FormModal>
    <FormModal open={profileOpen} title="Perfil de organización" description="Identidad visible para todos los miembros del workspace." submitLabel="Guardar perfil" busy={saveProfile.isPending} onClose={() => setProfileOpen(false)} onSubmit={(event) => { event.preventDefault(); saveProfile.mutate(); }}><label className="field"><span>Nombre</span><input required minLength={2} maxLength={160} value={profileForm.name} onChange={(event) => setProfileForm({ ...profileForm, name: event.target.value })} /></label><label className="field"><span>Descripción</span><textarea rows={4} maxLength={1000} value={profileForm.description} onChange={(event) => setProfileForm({ ...profileForm, description: event.target.value })} /></label><label className="field"><span>Color de marca</span><div className="color-field"><input type="color" value={profileForm.brand_color} onChange={(event) => setProfileForm({ ...profileForm, brand_color: event.target.value })} /><input className="mono" pattern="#[0-9a-fA-F]{6}" value={profileForm.brand_color} onChange={(event) => setProfileForm({ ...profileForm, brand_color: event.target.value })} /></div></label></FormModal>
    <FormModal open={memberOpen} title="Añadir miembro" description="La persona debe tener una cuenta activa en AK. Recibirá una notificación por correo." submitLabel="Añadir al workspace" busy={addMember.isPending} onClose={() => setMemberOpen(false)} onSubmit={(event) => { event.preventDefault(); addMember.mutate(); }}><label className="field"><span>Correo de la cuenta AK</span><input required type="email" value={memberForm.email} onChange={(event) => setMemberForm({ ...memberForm, email: event.target.value })} /></label><label className="field"><span>Rol inicial</span><select value={memberForm.role} onChange={(event) => setMemberForm({ ...memberForm, role: event.target.value })}><option value="admin">Admin — administra equipo y proyectos</option><option value="developer">Developer — crea y usa recursos</option><option value="viewer">Viewer — solo lectura</option></select></label></FormModal>
    <FormModal open={projectOpen} title={editingProject ? "Editar proyecto" : "Nuevo proyecto"} description="Los recursos y el consumo se separan por proyecto dentro de la organización." submitLabel={editingProject ? "Guardar proyecto" : "Crear proyecto"} busy={saveProject.isPending} onClose={() => { setProjectOpen(false); setEditingProject(null); }} onSubmit={(event) => { event.preventDefault(); saveProject.mutate(); }}><label className="field"><span>Nombre</span><input required minLength={2} maxLength={160} value={projectForm.name} onChange={(event) => setProjectForm({ ...projectForm, name: event.target.value })} /></label><label className="field"><span>Descripción</span><textarea rows={3} maxLength={1000} value={projectForm.description} onChange={(event) => setProjectForm({ ...projectForm, description: event.target.value })} /></label><label className="field"><span>Presupuesto mensual de créditos</span><input type="number" min={1} max={1000000} placeholder="Sin límite propio" value={projectForm.monthly_credit_budget} onChange={(event) => setProjectForm({ ...projectForm, monthly_credit_budget: event.target.value })} /></label></FormModal>
    <ConfirmModal open={Boolean(removeMember)} title="Retirar miembro" description={`${removeMember?.name || "Este usuario"} perderá acceso inmediato a los recursos compartidos del workspace.`} confirmLabel="Retirar miembro" busy={deleteMember.isPending} onClose={() => setRemoveMember(null)} onConfirm={() => removeMember && deleteMember.mutate(removeMember.user_id)} />
    <FormModal open={webhookOpen} title="Nuevo webhook" description="El endpoint debe usar HTTPS. El secreto se mostrará una sola vez." submitLabel="Crear webhook" busy={createWebhook.isPending} onClose={() => setWebhookOpen(false)} onSubmit={(event) => { event.preventDefault(); if (webhookUrl && webhookEvents.length) createWebhook.mutate(); }}><label className="field"><span>Endpoint HTTPS</span><input required type="url" value={webhookUrl} onChange={(event) => setWebhookUrl(event.target.value)} placeholder="https://tu-app.com/webhooks/ak" /></label><div className="tool-picker-list">{(webhooks.data?.available_events || ["ai.response.completed", "artifact.created", "generation.queued"]).map((event: string) => <label className="tool-picker-item" key={event}><input type="checkbox" checked={webhookEvents.includes(event)} onChange={() => setWebhookEvents((current) => current.includes(event) ? current.filter((item) => item !== event) : [...current, event])} /><span><strong>{event}</strong><small>Entrega JSON firmada</small></span></label>)}</div></FormModal>
    <Modal open={Boolean(webhookSecret)} title="Secreto del webhook" description="Guárdalo ahora; AK no volverá a mostrarlo." onClose={() => setWebhookSecret("")} footer={<button className="button button-primary" onClick={() => copyToClipboard(webhookSecret, toast, "Secreto copiado.")}><Copy size={15} /> Copiar secreto</button>}><div className="secret-box"><code>{webhookSecret}</code></div></Modal>
  </div>;
}

function Plans({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pendingCaptureId, setPendingCaptureId] = useState<string | null>(null);
  const captureHandled = useRef(false);
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => api<any[]>("/api/v1/plans") });
  const billingStatus = useQuery({ queryKey: ["billing-status"], queryFn: () => api<any>("/api/v1/billing/status") });
  const history = useQuery({
    queryKey: ["billing-history"],
    queryFn: () => api<any>("/api/v1/billing/history"),
    enabled: historyOpen
  });
  const checkout = useMutation({
    mutationFn: (plan_id: string) => api<any>("/api/v1/billing/paypal/orders", { method: "POST", body: JSON.stringify({ plan_id }) }),
    onSuccess: (data) => {
      if (data.approval_url) window.location.href = data.approval_url;
      else toast("PayPal no devolvió una URL de aprobación. Inténtalo nuevamente.", "error");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const capture = useMutation({
    mutationFn: (order_id: string) => api<any>("/api/v1/billing/paypal/capture", { method: "POST", body: JSON.stringify({ order_id }) }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      queryClient.invalidateQueries({ queryKey: ["billing-history"] });
      queryClient.invalidateQueries({ queryKey: ["billing-status"] });
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      setPendingCaptureId(null);
      toast(`Pago confirmado. El plan ${data.plan_id} ya está activo.`, "success");
      window.history.replaceState({}, "", "/plans");
    },
    onError: (error: Error) => {
      toast(`No se pudo confirmar el pago: ${error.message}`, "error");
    }
  });
  useEffect(() => {
    if (captureHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const token = params.get("token");
    if (payment === "success" && token) {
      captureHandled.current = true;
      setPendingCaptureId(token);
      capture.mutate(token);
    } else if (payment === "cancelled" || payment === "cancel") {
      captureHandled.current = true;
      setPendingCaptureId(null);
      toast("El pago fue cancelado; no se realizó ningún cargo.", "info");
      window.history.replaceState({}, "", "/plans");
    }
  }, []);
  const currentSubscription = billingStatus.data?.current_subscription;
  const currentPlanId = currentSubscription?.plan_id || user.planId;
  const paymentConfirmationPending = capture.isPending || Boolean(pendingCaptureId);
  return (
    <div className="page">
      <PageHeader title="Planes y facturación" description="Escala desde prototipos hasta cargas de trabajo empresariales." />
      {capture.isPending && <div className="security-callout"><RefreshCw className="spin" size={20} /><div><strong>Confirmando tu pago con PayPal…</strong><p>No cierres esta página hasta terminar la activación.</p></div></div>}
      {capture.error && pendingCaptureId && !capture.isPending && (
        <div className="security-callout">
          <ShieldCheck size={20} />
          <div><strong>No se pudo confirmar todavía el pago.</strong><p>{capture.error instanceof Error ? capture.error.message : "La confirmación quedó pendiente."} El identificador se conservó para volver a intentarlo sin repetir la compra.</p></div>
          <button className="button button-secondary" onClick={() => capture.mutate(pendingCaptureId)}>Reintentar confirmación</button>
        </div>
      )}
      {billingStatus.error
        ? <ErrorState error={billingStatus.error} onRetry={() => billingStatus.refetch()} />
        : billingStatus.isLoading
          ? <LoadingBlock />
          : <div className="billing-summary"><div><span className="plan-logo"><Sparkles size={20} /></span><div><span>Plan actual</span><strong>{currentSubscription?.plan_name || currentPlanId}</strong></div></div><div><span>Vigencia</span><strong>{currentPlanId === "free" ? "Sin facturación" : currentSubscription?.ends_at ? `Hasta ${formatDate(currentSubscription.ends_at)}` : "30 días por compra"}</strong></div><button className="button button-secondary" onClick={() => setHistoryOpen(true)}><Clock3 size={15} /> Historial</button></div>}
      {plans.error && <ErrorState error={plans.error} onRetry={() => plans.refetch()} />}
      {plans.isLoading && <LoadingBlock />}
      <div className="plans-grid">
        {plans.data?.map((plan) => (
          <article className={`plan-card ${plan.id === "pro" ? "recommended" : ""}`} key={plan.id}>
            {plan.id === "pro" && <span className="plan-ribbon">Más popular</span>}
            <div className="plan-head"><span className="badge">{plan.name}</span><h2>{plan.price_usd === null ? "Personalizado" : plan.price_usd === 0 ? "Gratis" : `$${plan.price_usd}`} {plan.price_usd > 0 && <small>/ 30 días</small>}</h2><p>{plan.id === "free" ? "Para explorar la plataforma." : plan.id === "pro" ? "Para creadores y equipos pequeños." : plan.id === "premium" ? "Para productos en crecimiento." : "Para organizaciones con requisitos propios."}</p></div>
            <ul>
              <li><Check size={16} /> {plan.credits_day === null ? "Uso ampliado con protección" : `${plan.credits_day} créditos diarios`}</li>
              <li><Check size={16} /> {plan.api_keys_limit === null ? "API keys AK personalizadas" : `${plan.api_keys_limit} API key${plan.api_keys_limit === 1 ? "" : "s"} AK`}</li>
              <li><Check size={16} /> {plan.id === "free" ? "1 clave propia por proveedor" : plan.id === "pro" ? "5 claves propias por proveedor" : "Claves de proveedor sin límite"}</li>
              <li><Check size={16} /> {plan.id === "premium" || plan.id === "enterprise" ? "Conectores personales HTTP, MySQL y prompts" : "Catálogo de conectores administrados"}</li>
              <li><Check size={16} /> {plan.context_messages} mensajes de contexto</li>
              <li><Check size={16} /> Modos {plan.modes.join(", ")}</li>
            </ul>
            <button
              disabled={plan.id === currentPlanId || checkout.isPending || (Number(plan.price_usd) > 0 && paymentConfirmationPending) || (plan.id === "free" && currentPlanId !== "free")}
              className={`button ${plan.id === "pro" ? "button-primary" : "button-secondary"}`}
              onClick={() => {
                if (plan.id === "enterprise") {
                  window.location.href = "https://discord.gg/PwXEZCFBjY";
                } else if (Number(plan.price_usd) > 0) {
                  checkout.mutate(plan.id);
                }
              }}
            >
              {plan.id === currentPlanId ? "Plan actual" : plan.id === "enterprise" ? "Contactar ventas" : plan.id === "free" ? "Plan base incluido" : paymentConfirmationPending ? "Confirmación pendiente" : checkout.isPending ? "Abriendo PayPal…" : "Elegir plan"}
            </button>
          </article>
        ))}
      </div>
      <Modal open={historyOpen} title="Historial de facturación" description="Pagos y suscripciones asociados a tu cuenta." wide onClose={() => setHistoryOpen(false)}>
        {history.error && <ErrorState error={history.error} onRetry={() => history.refetch()} />}
        {history.isLoading && <LoadingBlock />}
        <div className="billing-history-list">
          {history.data?.payments?.map((payment: any) => <div className="history-row" key={payment.id}><div><strong>{payment.plan_name}</strong><span>{formatDate(payment.created_at)} · PayPal</span></div><div><strong>${Number(payment.amount_usd).toFixed(2)}</strong><span className={`badge ${payment.status === "captured" ? "badge-success" : ""}`}>{payment.status}</span></div></div>)}
          {!history.isLoading && !history.error && !history.data?.payments?.length && <EmptyState icon={<CreditCard />} title="Sin pagos" description="Aún no hay movimientos de facturación." />}
        </div>
      </Modal>
    </div>
  );
}

function Docs() {
  const toast = useToast();
  const [tab, setTab] = useState<"professional" | "easy">("professional");
  const examples = {
    professional: `curl https://api.akiomae.xyz/v1/chat/completions \\
  -H "Authorization: Bearer $AK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "ak-auto",
    "mode": "agentic",
    "messages": [
      {"role": "system", "content": "Responde como analista empresarial."},
      {"role": "user", "content": "Resume las novedades de IA de hoy"}
    ],
    "skills": ["executive-summary"],
    "tools": ["search_internet", "read_url"],
    "tool_choice": "auto",
    "stream": false
  }'`,
    easy: `curl https://api.akiomae.xyz/api/v1/easy \\
  -H "Authorization: Bearer $AK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "Prepara una propuesta para este cliente",
    "context": "Somos una consultora B2B de automatización",
    "personality": "Directo y cordial",
    "name": "AK",
    "genero": "neutral",
    "lang": "Español",
    "mode": "standard",
    "skills": ["executive-summary"],
    "tools": [],
    "settings": {
      "temperature": 0.4,
      "memory": true,
      "ui_blocks": true
    }
  }'`,
    skills: `curl https://api.akiomae.xyz/api/v1/responses \\
  -H "Authorization: Bearer $AK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "input": {
      "project": "Migración de ERP",
      "findings": ["Retraso de 2 semanas", "Riesgo en datos maestros"]
    },
    "skills": ["executive-summary", "checklist"],
    "mode": "standard"
  }'`,
    connectors: `curl https://api.akiomae.xyz/api/v1/responses \\
  -H "Authorization: Bearer $AK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "input": "Investiga el clima y la hora actual en Lima",
    "mode": "agentic",
    "tools": ["world_weather", "world_time"],
    "tool_choice": "required"
  }'`,
    structured: `{
  "model": "ak-auto",
  "input": "Extrae empresa, contacto y prioridad del texto...",
  "response_format": {
    "type": "json_schema",
    "json_schema": {
      "name": "lead",
      "schema": {
        "type": "object",
        "properties": {
          "empresa": {"type": "string"},
          "contacto": {"type": "string"},
          "prioridad": {"enum": ["alta", "media", "baja"]}
        },
        "required": ["empresa", "contacto", "prioridad"],
        "additionalProperties": false
      }
    }
  }
}`,
    response: `{
  "object": "ak.response",
  "output_text": "…",
  "provider": "gemini",
  "model": "gemini-…",
  "usage": {
    "input_tokens": 142,
    "output_tokens": 386,
    "total_tokens": 528,
    "credits": 0,
    "billing_source": "byok"
  },
  "tool_calls": [],
  "artifacts": [],
  "request_id": "…"
}`
  };
  const example = examples[tab];
  const CopyCode = ({ value, label = "Copiar" }: { value: string; label?: string }) => (
    <div className="code-sample">
      <button className="button button-ghost code-copy" onClick={() => copyToClipboard(value, toast, "Ejemplo copiado.")}><Copy size={14} /> {label}</button>
      <pre className="code-block"><code>{value}</code></pre>
    </div>
  );
  return (
    <div className="page docs-page">
      <PageHeader title="Documentación" description="Integra AK-v5.0 con contratos simples, tipados y versionados." actions={<a className="button button-secondary" href="/status">Estado API <ExternalLink size={15} /></a>} />
      <div className="docs-layout">
        <aside className="docs-nav">
          <strong>Primeros pasos</strong>
          <a href="#auth">Autenticación</a><a href="#requests">Solicitudes</a><a href="#professional">Professional</a><a href="#easy">Easy</a>
          <strong>Capacidades</strong>
          <a href="#skills">Skills</a><a href="#connectors">Conectores</a><a href="#custom-connectors">Conectores Premium</a><a href="#byok">Claves propias</a><a href="#structured">JSON estructurado</a><a href="#streaming">Streaming</a>
          <strong>Referencia</strong>
          <a href="#scopes">Permisos</a><a href="#models">Orquestación</a><a href="#response">Respuesta</a><a href="#errors">Errores</a>
        </aside>
        <article className="docs-content">
          <section><span className="eyebrow">Quickstart</span><h2>Tu primera respuesta</h2><p>El contrato profesional es el predeterminado y mantiene compatibilidad con el formato de Chat Completions.</p>
            <div className="code-tabs"><button className={tab === "professional" ? "active" : ""} onClick={() => setTab("professional")}>Professional</button><button className={tab === "easy" ? "active" : ""} onClick={() => setTab("easy")}>Easy</button></div>
            <CopyCode value={example} />
          </section>
          <section id="auth"><h2>Autenticación</h2><p>Incluye la API key como Bearer token. Las claves en parámetros de URL no están permitidas y el valor completo solo se muestra una vez al crearla.</p><code className="inline-code">Authorization: Bearer ak-live-...</code></section>
          <section id="requests"><h2>Contratos y rutas</h2><p>Todos aceptan JSON en el cuerpo. <code>/v1/chat/completions</code> facilita migrar clientes OpenAI; <code>/api/v1/responses</code> expone la respuesta completa de AK; <code>/api/v1/easy</code> conserva los parámetros guiados.</p><div className="endpoint"><span className="method">POST</span><code>/v1/chat/completions</code><span>OpenAI-compatible</span></div><div className="endpoint"><span className="method">POST</span><code>/api/v1/responses</code><span>Respuesta nativa AK</span></div><div className="endpoint"><span className="method">POST</span><code>/api/v1/easy</code><span>Contrato guiado</span></div></section>
          <section id="professional"><h2>Modo Professional</h2><p>Es el contrato predeterminado. Envía <code>messages</code> al estilo OpenAI o usa <code>input</code>/<code>prompt</code> con un string, objeto JSON o array. Parámetros principales: <code>model</code>, <code>mode</code>, <code>temperature</code>, <code>top_p</code>, <code>max_output_tokens</code>, <code>skills</code>, <code>tools</code>, <code>tool_choice</code>, <code>response_format</code>, <code>thread_id</code> y <code>stream</code>.</p><div className="docs-note"><strong>Modos:</strong> <code>auto</code> elige el perfil; <code>fast</code> prioriza latencia; <code>standard</code> equilibra calidad; <code>thinking</code> razona más; <code>agentic</code> puede realizar múltiples pasos y conectores hasta completar la tarea dentro de sus protecciones operativas.</div></section>
          <section id="easy"><h2>Modo Easy</h2><p>Usa <code>prompt</code> como entrada principal y compone las instrucciones con <code>context</code>, <code>personality</code>, <code>name</code>, <code>genero</code>/<code>gender</code> y <code>lang</code>. También acepta <code>mode</code>, <code>model</code>, <code>tokens</code>, <code>images</code>, <code>skills</code>, <code>tools</code>, <code>stream</code> y <code>settings</code>. Dentro de <code>settings</code> puedes enviar <code>temperature</code>, <code>top_p</code>, <code>memory</code>, <code>tool_choice</code>, <code>ui_blocks</code> y <code>response_format</code>.</p><p>Si activas memoria, reutiliza el <code>thread_id</code> devuelto para continuar una conversación de forma explícita.</p></section>
          <section id="skills"><h2>Usar skills</h2><p>Una skill es una plantilla versionada que añade instrucciones especializadas. Consulta <code>GET /api/v1/skills</code>, copia el valor <code>slug</code> y envíalo en el array <code>skills</code>. Puedes combinar hasta 20; el orden enviado también es el orden de aplicación.</p><CopyCode value={examples.skills} /><div className="endpoint"><span className="method">GET</span><code>/api/v1/skills</code><span>Lista skills visibles y sus slugs</span></div><p>Las skills privadas se crean y administran desde <NavLink to="/skills">Skills</NavLink>. Requieren que el plan incluya esa capacidad; un slug inexistente o desactivado no se ejecuta.</p></section>
          <section id="connectors"><h2>Usar conectores</h2><p>Consulta <code>GET /api/v1/connectors</code> con el permiso <code>tools:read</code>. Para que el modelo los use, incluye sus identificadores en <code>tools</code> y concede <code>tools:execute</code> a tu API key AK. <code>tool_choice</code> acepta <code>auto</code>, <code>none</code> o <code>required</code>.</p><CopyCode value={examples.connectors} /><p>Usa el modo <code>agentic</code> cuando la tarea pueda necesitar varios pasos. El motor conserva resultados de herramientas, evita repetir la misma llamada y detiene el ciclo cuando obtiene una respuesta final. Solo se ejecutan conectores habilitados y permitidos por tu plan.</p><div className="endpoint"><span className="method">POST</span><code>/api/v1/connectors/:id/run</code><span>Ejecución directa con tools:execute</span></div></section>
          <section id="custom-connectors"><h2>Conectores personales Premium</h2><p>Premium puede crear herramientas desde <NavLink to="/connectors">Herramientas y conectores</NavLink>. Se admiten APIs HTTP públicas, MySQL remoto de solo lectura y plantillas que producen una respuesta o un <code>system_prompt</code>. Los secretos se guardan cifrados y nunca vuelven a mostrarse.</p><div className="docs-note"><strong>Variables:</strong> usa <code>{"{{input.campo}}"}</code> en URL, headers, body, consulta o prompt. En MySQL cada variable se convierte en un parámetro preparado; se bloquean escrituras, múltiples sentencias, redes privadas y respuestas de más de 100 filas.</div><p>Después de crearlo, prueba el conector desde el panel. Su identificador será <code>user:UUID</code> y se usa exactamente igual que cualquier otro valor del array <code>tools</code>. La API de gestión <code>/api/v1/user-connectors</code> requiere una sesión web Premium; la ejecución normal admite una API key AK con <code>tools:execute</code>.</p></section>
          <section id="byok"><h2>Claves propias de proveedores (BYOK)</h2><p>Añade OpenAI, Gemini, OpenRouter, Groq, Cohere u otro proveedor compatible desde <NavLink to="/settings">Configuración</NavLink>. No envíes el secreto en cada solicitud: queda cifrado en tu cuenta y el orquestador lo selecciona automáticamente cuando existe una ruta compatible.</p><div className="docs-note"><strong>Facturación:</strong> una solicitud resuelta con tu clave registra <code>credits: 0</code> y <code>billing_source: "byok"</code>. La cuota, velocidad y restricciones de inferencia pasan a ser las del proveedor. Los modos, skills, conectores, contexto y demás capacidades del plan AK se siguen aplicando.</div><p>Free permite 1 clave por proveedor, Pro hasta 5 y Premium/Enterprise no tienen límite. Si una clave propia aplicable falla, AK no cambia silenciosamente a una clave del sistema: así nunca consume créditos sin que lo sepas. Revisa solicitudes, tokens y errores por clave en Configuración.</p></section>
          <section id="structured"><h2>Salida JSON estructurada</h2><p>Usa <code>response_format: {"{"}"type":"json_object"{"}"}</code> para JSON libre o proporciona un <code>json_schema</code>. AK valida el resultado y realiza una corrección controlada si el proveedor devuelve una estructura inválida.</p><CopyCode value={examples.structured} /></section>
          <section id="streaming"><h2>Streaming</h2><p>Establece <code>stream: true</code>. Chat Completions devuelve Server-Sent Events compatibles con OpenAI y finaliza con <code>[DONE]</code>; la API nativa usa eventos <code>delta</code>, <code>done</code> y <code>error</code>, además de heartbeats. Cerrar la conexión cancela el flujo aguas arriba.</p></section>
          <section id="scopes"><h2>Permisos de API keys AK</h2><p>Las claves de integración AK son distintas de las claves BYOK. Aplica privilegio mínimo a cada integración.</p><div className="endpoint"><span className="method">IA</span><code>ai:read · ai:write</code><span>Rutas, uso, memoria y generación</span></div><div className="endpoint"><span className="method">TOOLS</span><code>tools:read · tools:execute</code><span>Catálogo y ejecución de conectores</span></div><div className="endpoint"><span className="method">DATA</span><code>jobs:read · jobs:write · artifacts:read · artifacts:write</code><span>Trabajos y artefactos persistentes</span></div></section>
          <section id="models"><h2>Orquestación de rutas</h2><p>Usa <code>ak-auto</code> para selección automática o un alias publicado en <NavLink to="/models">Orquestación</NavLink>. El router evalúa perfil, compatibilidad, disponibilidad, circuit breaker y credenciales. Una clave BYOK solo participa en las rutas de su proveedor.</p></section>
          <section id="response"><h2>Respuesta nativa</h2><p>Además del texto, la respuesta expone proveedor, modelo, modo, uso, llamadas a herramientas, bloques UI, artefactos, citas, latencia y un <code>request_id</code> trazable.</p><CopyCode value={examples.response} /></section>
          <section id="errors"><h2>Errores</h2><p>Los errores usan códigos HTTP y un objeto <code>error</code> con <code>code</code>, <code>message</code> y <code>request_id</code>. Conserva ese identificador al contactar soporte.</p><div className="endpoint"><span className="method">403</span><code>insufficient_scope</code><span>La API key AK no autoriza la operación</span></div><div className="endpoint"><span className="method">403</span><code>feature_not_available</code><span>El plan no incluye la capacidad solicitada</span></div><div className="endpoint"><span className="method">429</span><code>credits_exhausted</code><span>Se alcanzó el saldo del sistema</span></div><div className="endpoint"><span className="method">502</span><code>byok_provider_failed</code><span>Fallaron las credenciales propias compatibles</span></div><div className="endpoint"><span className="method">502</span><code>structured_response_invalid</code><span>La salida no cumplió el esquema tras corregir</span></div></section>
        </article>
      </div>
    </div>
  );
}

function Admin({ currentUser }: { currentUser: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const users = useQuery({ queryKey: ["admin-users"], queryFn: () => api<any[]>("/api/v1/admin/users") });
  const overview = useQuery({ queryKey: ["admin-overview"], queryFn: () => api<any>("/api/v1/admin/overview") });
  const audit = useQuery({ queryKey: ["admin-audit"], queryFn: () => api<any[]>("/api/v1/admin/audit-logs?limit=25") });
  const emailSummary = useQuery({ queryKey: ["admin-email-summary"], queryFn: () => api<any>("/api/v1/admin/emails/summary") });
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<any | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    audience: "user",
    user_id: "",
    subject: "",
    format: "text",
    content: ""
  });
  const [form, setForm] = useState({ role: "user", status: "active", plan_id: "free", days: 30 });
  const filtered = users.data?.filter((user) => `${user.name} ${user.email} ${user.role} ${user.plan_id}`.toLowerCase().includes(query.toLowerCase())) || [];
  const hasAdminChanges = Boolean(editing && (
    form.role !== editing.role ||
    form.status !== editing.status ||
    form.plan_id !== editing.plan_id
  ));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...(form.role !== editing.role ? { role: form.role } : {}),
        ...(form.status !== editing.status ? { status: form.status } : {}),
        ...(form.plan_id !== editing.plan_id ? { plan_id: form.plan_id, days: form.days } : {})
      };
      return api(`/api/v1/admin/users/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify(body)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-overview"] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit"] });
      if (editing?.id === currentUser.userId) queryClient.invalidateQueries({ queryKey: ["me"] });
      setEditing(null);
      toast("Usuario actualizado.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const revokeSessions = useMutation({
    mutationFn: () => api<any>(`/api/v1/admin/users/${editing.id}/sessions/revoke`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-audit"] });
      queryClient.invalidateQueries({ queryKey: ["admin-overview"] });
      toast(`${Number(result.sessions_revoked || 0)} sesiones cerradas.`, "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const sendEmail = useMutation({
    mutationFn: () => api<any>("/api/v1/admin/emails", {
      method: "POST",
      body: JSON.stringify({
        audience: emailForm.audience,
        ...(emailForm.audience === "user" ? { user_id: emailForm.user_id } : {}),
        subject: emailForm.subject,
        format: emailForm.format,
        content: emailForm.content
      })
    }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-email-summary"] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit"] });
      setEmailOpen(false);
      setEmailForm({ audience: "user", user_id: "", subject: "", format: "text", content: "" });
      toast(`${Number(result.recipients || 0)} correo(s) añadidos a la cola.`, "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const openEdit = (user: any) => {
    setEditing(user);
    setForm({ role: user.role, status: user.status, plan_id: user.plan_id, days: 30 });
  };
  return (
    <div className="page">
      <PageHeader eyebrow="Administración" title="Control del sistema" description="Usuarios, acceso, consumo y operaciones de la plataforma." actions={<button className="button button-primary" onClick={() => setEmailOpen(true)}><Send size={15} /> Enviar correo</button>} />
      <section className="metric-grid">
        <MetricCard icon={<Users size={19} />} label="Usuarios" value={formatNumber(overview.data?.users?.total ?? users.data?.length ?? 0)} detail={`${formatNumber(overview.data?.users?.active || 0)} activos`} />
        <MetricCard icon={<Activity size={19} />} label="Solicitudes" value={formatNumber(overview.data?.last_24h?.requests || 0)} detail="Últimas 24 horas" tone="accent" />
        <MetricCard icon={<CircleDollarSign size={19} />} label="Suscripciones" value={formatNumber(overview.data?.active_subscriptions || 0)} detail="Activas" tone="positive" />
        <MetricCard icon={<ShieldCheck size={19} />} label="Errores" value={formatNumber(overview.data?.last_24h?.errors || 0)} detail="Últimas 24 horas" />
      </section>
      {overview.error && <ErrorState error={overview.error} onRetry={() => overview.refetch()} />}
      <section className="panel admin-email-panel">
        <div className="panel-header"><div><h2>Comunicaciones</h2><p>Mensajes importantes y anuncios enviados mediante la cola SMTP.</p></div><span className={`badge ${Number(emailSummary.data?.failed || 0) === 0 ? "badge-success" : ""}`}>{emailSummary.error ? "Sin telemetría" : `${formatNumber(emailSummary.data?.queued || 0)} en cola`}</span></div>
        <div className="email-summary-grid">
          <div><strong>{formatNumber(emailSummary.data?.sent || 0)}</strong><span>Entregados</span></div>
          <div><strong>{formatNumber(emailSummary.data?.queued || 0)}</strong><span>Pendientes</span></div>
          <div><strong>{formatNumber(emailSummary.data?.failed || 0)}</strong><span>Fallidos</span></div>
          <div><strong>{emailSummary.data?.last_sent_at ? formatDate(emailSummary.data.last_sent_at) : "—"}</strong><span>Último envío</span></div>
        </div>
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>Usuarios</h2><p>Acceso y plan activo.</p></div><label className="search-field compact"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar usuario…" /></label></div>
        {users.error && <ErrorState error={users.error} onRetry={() => users.refetch()} />}
        {users.isLoading && <LoadingBlock />}
        <div className="table">
          <div className="table-head admin-table"><span>Usuario</span><span>Rol</span><span>Plan</span><span>Solicitudes</span><span>Estado</span><span /></div>
          {filtered.map((user) => <div className="table-row admin-table" key={user.id}><span className="cell-user"><span className="avatar small">{user.name?.slice(0, 1)}</span><span><strong>{user.name}</strong><small>{user.email}</small></span></span><span>{user.role}</span><span><span className="badge">{user.plan_id}</span></span><span>{formatNumber(user.requests)}</span><span className="status-text"><span className={`status-dot ${user.status !== "active" ? "muted" : ""}`} />{user.status}</span><span className="admin-row-actions"><button className="icon-button" title="Administrar usuario" onClick={() => openEdit(user)}><Settings size={16} /></button></span></div>)}
        </div>
        {!users.isLoading && !users.error && !filtered.length && <EmptyState icon={<Users />} title="Sin usuarios" description={query ? "No hay coincidencias para la búsqueda." : "Aún no hay usuarios registrados."} />}
      </section>
      <FormModal
        open={Boolean(editing)}
        title="Administrar usuario"
        description={editing ? `${editing.name} · ${editing.email}` : undefined}
        submitLabel="Guardar cambios"
        busy={save.isPending}
        submitDisabled={!hasAdminChanges}
        onClose={() => setEditing(null)}
        onSubmit={(event) => { event.preventDefault(); save.mutate(); }}
      >
        <div className="form-grid">
          <label className="field"><span>Rol</span><select value={form.role} disabled={editing?.id === currentUser.userId} onChange={(event) => setForm({ ...form, role: event.target.value })}><option value="user">Usuario</option><option value="support">Soporte</option><option value="admin">Administrador</option></select></label>
          <label className="field"><span>Estado</span><select value={form.status} disabled={editing?.id === currentUser.userId} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="active">Activo</option><option value="suspended">Suspendido</option></select></label>
          <label className="field"><span>Plan</span><select value={form.plan_id} onChange={(event) => setForm({ ...form, plan_id: event.target.value })}><option value="free">Free</option><option value="pro">Pro</option><option value="premium">Premium</option><option value="enterprise">Enterprise</option></select></label>
          {form.plan_id !== editing?.plan_id && form.plan_id !== "free" && <label className="field"><span>Duración del nuevo plan (días)</span><input type="number" min={1} max={3650} value={form.days} onChange={(event) => setForm({ ...form, days: Math.max(1, Math.min(3650, Number(event.target.value) || 1)) })} /></label>}
        </div>
        {form.status === "suspended" && editing?.status !== "suspended" && <div className="security-callout"><ShieldCheck size={19} /><div><strong>La suspensión cierra sesiones y revoca API keys.</strong><p>Verifica esta decisión antes de guardar.</p></div></div>}
        {editing?.id !== currentUser.userId && <button type="button" className="button button-secondary danger" disabled={revokeSessions.isPending} onClick={() => revokeSessions.mutate()}><LogOut size={15} /> {revokeSessions.isPending ? "Cerrando sesiones…" : "Cerrar todas sus sesiones"}</button>}
      </FormModal>
      <FormModal
        open={emailOpen}
        title="Enviar comunicación"
        description="Usa este canal solo para mensajes relevantes. Las entregas se procesan con reintentos y quedan auditadas."
        submitLabel={emailForm.audience === "all" ? "Programar anuncio" : "Enviar al usuario"}
        busy={sendEmail.isPending}
        wide
        onClose={() => setEmailOpen(false)}
        onSubmit={(event) => {
          event.preventDefault();
          if (emailForm.audience === "user" && !emailForm.user_id) {
            toast("Selecciona un destinatario.", "error");
            return;
          }
          sendEmail.mutate();
        }}
      >
        <div className="form-grid">
          <label className="field"><span>Audiencia</span><select value={emailForm.audience} onChange={(event) => setEmailForm({ ...emailForm, audience: event.target.value })}><option value="user">Un usuario</option><option value="all">Anuncio a todos</option></select></label>
          {emailForm.audience === "user" && <label className="field"><span>Destinatario</span><select required value={emailForm.user_id} onChange={(event) => setEmailForm({ ...emailForm, user_id: event.target.value })}><option value="">Seleccionar…</option>{users.data?.filter((item) => item.status === "active").map((item) => <option key={item.id} value={item.id}>{item.name} · {item.email}</option>)}</select></label>}
          <label className="field"><span>Formato</span><select value={emailForm.format} onChange={(event) => setEmailForm({ ...emailForm, format: event.target.value })}><option value="text">Texto</option><option value="markdown">Markdown</option><option value="html">HTML</option></select></label>
        </div>
        {emailForm.audience === "all" && <div className="security-callout"><Send size={19} /><div><strong>Este anuncio se enviará a todos los usuarios activos.</strong><p>Revisa asunto y contenido antes de ponerlo en cola.</p></div></div>}
        <label className="field"><span>Asunto</span><input required minLength={3} maxLength={240} value={emailForm.subject} onChange={(event) => setEmailForm({ ...emailForm, subject: event.target.value })} /></label>
        <label className="field"><span>Contenido {emailForm.format.toUpperCase()}</span><textarea required rows={12} className={emailForm.format !== "text" ? "mono" : ""} value={emailForm.content} onChange={(event) => setEmailForm({ ...emailForm, content: event.target.value })} /></label>
      </FormModal>
      <section className="panel">
        <div className="panel-header"><div><h2>Auditoría reciente</h2><p>Cambios administrativos y eventos sensibles.</p></div><button className="button button-ghost" disabled={audit.isFetching} onClick={() => audit.refetch()}><RefreshCw className={audit.isFetching ? "spin" : ""} size={15} /> Actualizar</button></div>
        {audit.error && <ErrorState error={audit.error} onRetry={() => audit.refetch()} />}
        {audit.isLoading && <LoadingBlock />}
        <div className="billing-history-list">
          {audit.data?.map((entry) => <div className="history-row" key={entry.id}><div><strong>{entry.action}</strong><span>{entry.actor_email || "Sistema"} · {entry.target_type}</span></div><div><span>{formatDate(entry.created_at)}</span><code>{String(entry.request_id || "").slice(0, 12)}</code></div></div>)}
          {!audit.isLoading && !audit.error && !audit.data?.length && <EmptyState icon={<ShieldCheck />} title="Sin eventos" description="Los cambios sensibles aparecerán en esta sección." />}
        </div>
      </section>
    </div>
  );
}

function downloadContent(filename: string, content: string, type = "text/plain;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.replace(/[<>:"/\\|?*]+/g, "-");
  link.click();
  URL.revokeObjectURL(url);
}

function Artifacts({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const artifacts = useQuery({ queryKey: ["artifacts"], queryFn: () => api<any[]>("/api/v1/artifacts") });
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: "", type: "document", content: "" });
  const canWrite = userHasFeature(user, "artifacts");
  const detail = useQuery({
    queryKey: ["artifact", selectedId],
    queryFn: () => api<any>(`/api/v1/artifacts/${selectedId}`),
    enabled: Boolean(selectedId)
  });
  const filtered = artifacts.data?.filter((item) => `${item.title} ${item.type}`.toLowerCase().includes(query.toLowerCase())) || [];
  const save = useMutation({
    mutationFn: () => api<any>(editing?.isNew ? "/api/v1/artifacts" : `/api/v1/artifacts/${editing.id}`, {
      method: editing?.isNew ? "POST" : "PATCH",
      body: JSON.stringify({
        title: form.title,
        type: form.type,
        content: form.content,
        ...(editing?.isNew ? { metadata: {} } : { expected_version: editing.version })
      })
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artifacts"] });
      toast(`Artefacto ${editing?.isNew ? "creado" : "actualizado"}.`, "success");
      setEditing(null);
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/artifacts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artifacts"] });
      setDeleteId(null);
      if (selectedId === deleteId) setSelectedId(null);
      toast("Artefacto eliminado.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const openCreate = () => {
    setEditing({ isNew: true });
    setForm({ title: "", type: "document", content: "" });
  };
  const openEdit = async (artifact: any) => {
    try {
      const full = await api<any>(`/api/v1/artifacts/${artifact.id}`);
      setEditing(full);
      setForm({ title: full.title, type: full.type, content: full.content || "" });
    } catch (error) {
      toast((error as Error).message, "error");
    }
  };
  const downloadArtifact = async (artifact: any) => {
    try {
      const full = artifact.content !== undefined ? artifact : await api<any>(`/api/v1/artifacts/${artifact.id}`);
      const extension = full.type === "code" ? "txt" : full.type === "spreadsheet" || full.type === "table" ? "csv" : "md";
      downloadContent(`${full.title}.${extension}`, String(full.content || ""));
    } catch (error) {
      toast((error as Error).message, "error");
    }
  };
  return (
    <div className="page">
      <PageHeader title="Artefactos" description="Resultados reutilizables creados por AK o por ti, con IDs estables, procedencia y versiones iterables." actions={<div className="inline-actions"><NavLink className="button button-secondary" to="/studio"><WandSparkles size={16} /> Studio</NavLink>{canWrite ? <button className="button button-primary" onClick={openCreate}><Plus size={16} /> Nuevo artefacto</button> : <NavLink className="button button-secondary" to="/plans"><LockKeyhole size={15} /> Activar edición</NavLink>}</div>} />
      {!canWrite && <div className="security-callout"><LockKeyhole size={20} /><div><strong>Workspace en modo lectura</strong><p>Puedes abrir y descargar artefactos existentes. Crear, editar o eliminar requiere Pro o superior.</p></div></div>}
      <div className="filter-bar"><label className="search-field"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar artefactos…" /></label></div>
      {artifacts.error && <ErrorState error={artifacts.error} onRetry={() => artifacts.refetch()} />}
      {artifacts.isLoading && <LoadingBlock />}
      <div className="artifact-grid">
        {filtered.map((artifact) => <article className="artifact-card" key={artifact.id}>
          <div className="skill-head"><span className="skill-icon"><FileText size={19} /></span><span className="inline-actions"><span className="badge">{artifact.type}</span>{artifact.auto_generated && <span className="badge badge-success">Creado por AK</span>}</span></div>
          <h3>{artifact.title}</h3><p>Actualizado {formatDate(artifact.updated_at)} · v{artifact.version}</p><code className="artifact-id">{artifact.id}</code>
          <div className="inline-actions">
            <button className="button button-ghost" onClick={() => setSelectedId(artifact.id)}><Eye size={15} /> Abrir</button>
            {canWrite && <button className="icon-button" title="Editar" onClick={() => openEdit(artifact)}><Pencil size={15} /></button>}
            <button className="icon-button" title="Descargar" onClick={() => downloadArtifact(artifact)}><Download size={15} /></button>
            {canWrite && <button className="icon-button danger" title="Eliminar" onClick={() => setDeleteId(artifact.id)}><Trash2 size={15} /></button>}
          </div>
        </article>)}
        {!artifacts.isLoading && !artifacts.error && !filtered.length && <EmptyState icon={<Blocks />} title={query ? "Sin coincidencias" : "El workspace está listo"} description={query ? "Ajusta tu búsqueda." : canWrite ? "Crea un artefacto aquí o desde una respuesta de Studio." : "Tus artefactos aparecerán aquí al activar un plan compatible."} action={!query && canWrite && <button className="button button-primary" onClick={openCreate}><Plus size={16} /> Crear artefacto</button>} />}
      </div>
      <Modal open={Boolean(selectedId)} title={detail.data?.title || "Artefacto"} description={detail.data ? `${detail.data.type} · versión ${detail.data.version} · ${detail.data.auto_generated ? "generado por AK" : "creado manualmente"}` : "Cargando contenido…"} wide onClose={() => setSelectedId(null)} footer={detail.data && <><button className="button button-secondary" onClick={() => copyToClipboard(detail.data.id, toast, "ID copiado.")}><Copy size={15} /> Copiar ID</button><NavLink className="button button-secondary" to={`/studio?artifact=${encodeURIComponent(detail.data.id)}`}><WandSparkles size={15} /> Iterar en Studio</NavLink><button className="button button-primary" onClick={() => downloadArtifact(detail.data)}><Download size={15} /> Descargar</button></>}>
        {detail.error && <ErrorState error={detail.error} onRetry={() => detail.refetch()} />}
        {detail.isLoading && <LoadingBlock />}
        {detail.data && <div className="artifact-detail-layout"><aside><dl className="inspector-list"><div><dt>ID</dt><dd><code>{detail.data.id}</code></dd></div><div><dt>Tipo</dt><dd>{detail.data.type}</dd></div><div><dt>Versión</dt><dd>{detail.data.version}</dd></div><div><dt>Memoria</dt><dd>{detail.data.thread_id ? detail.data.thread_id.slice(0, 8) : "—"}</dd></div><div><dt>Origen</dt><dd>{detail.data.auto_generated ? "Asistente" : "Usuario"}</dd></div></dl></aside><article>{detail.data.type === "document" ? <MarkdownContent content={String(detail.data.content || "")} /> : <pre className="json-inspector">{String(detail.data.content || "")}</pre>}</article></div>}
      </Modal>
      <FormModal open={Boolean(editing)} title={editing?.isNew ? "Nuevo artefacto" : "Editar artefacto"} description="Guarda contenido reutilizable en tu workspace." submitLabel={editing?.isNew ? "Crear" : "Guardar"} busy={save.isPending} wide onClose={() => setEditing(null)} onSubmit={(event) => { event.preventDefault(); if (form.title.trim()) save.mutate(); }}>
        <div className="form-grid"><label className="field"><span>Título</span><input required maxLength={240} value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label><label className="field"><span>Tipo</span><select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}><option value="document">Documento</option><option value="code">Código</option><option value="diagram">Diagrama</option><option value="chart">Gráfico</option><option value="table">Tabla</option><option value="spreadsheet">Hoja de cálculo</option></select></label></div>
        <div className="artifact-builder"><label className="field"><span>Fuente editable</span><textarea className="mono" rows={18} value={form.content} onChange={(event) => setForm({ ...form, content: event.target.value })} placeholder="Escribe Markdown, Mermaid, código o datos estructurados…" /></label><section className="artifact-preview"><span>Vista previa</span>{form.type === "document" ? <MarkdownContent content={form.content || "_La vista previa aparecerá aquí._"} /> : <pre>{form.content || "La vista previa aparecerá aquí."}</pre>}</section></div>
      </FormModal>
      <ConfirmModal open={Boolean(deleteId)} title="Eliminar artefacto" description="Esta acción no se puede deshacer." confirmLabel="Eliminar" busy={remove.isPending} onClose={() => setDeleteId(null)} onConfirm={() => deleteId && remove.mutate(deleteId)} />
    </div>
  );
}

function Jobs({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [form, setForm] = useState({ type: "image", prompt: "" });
  const features = new Set(user.planFeatures);
  const allJobs = features.has("all");
  const allowedJobTypes = [
    ...(allJobs || features.has("studio") ? [{ value: "document", label: "Documento" }] : []),
    ...(allJobs || features.has("basic_tools") || features.has("all_tools") || features.has("priority") ? [{ value: "image", label: "Imagen" }] : []),
    ...(allJobs || features.has("all_tools") || features.has("priority") ? [{ value: "audio", label: "Audio TTS local" }] : [])
  ];
  const jobs = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api<any[]>("/api/v1/jobs"),
    refetchInterval: (query) => (query.state.data as any[] | undefined)?.some((job) => job.status === "queued" || job.status === "running") ? 5_000 : false
  });
  const create = useMutation({
    mutationFn: () => api<any>("/api/v1/jobs", { method: "POST", body: JSON.stringify({ ...form, options: {} }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      setCreateOpen(false);
      setForm({ type: "image", prompt: "" });
      toast("Generación enviada a la cola.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const retry = useMutation({
    mutationFn: (id: string) => api(`/api/v1/jobs/${id}/retry`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      toast("Generación reintentada.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/api/v1/jobs/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      setCancelId(null);
      toast("Generación cancelada.", "success");
    },
    onError: (error: Error) => toast(error.message, "error")
  });
  return (
    <div className="page">
      <PageHeader title="Generaciones" description="Imágenes, documentos y audio TTS local procesados en segundo plano; AK también puede crearlos desde una solicitud agentica." actions={<button className="button button-primary" disabled={!allowedJobTypes.length} onClick={() => { setForm((current) => ({ ...current, type: allowedJobTypes[0]?.value || current.type })); setCreateOpen(true); }}><Plus size={16} /> Nueva generación</button>} />
      {jobs.error && <ErrorState error={jobs.error} onRetry={() => jobs.refetch()} />}
      {jobs.isLoading && <LoadingBlock />}
      <section className="panel">
        <div className="thread-list">
          {jobs.data?.map((job) => <div className="thread-row" key={job.id}>
            <span className="thread-icon"><Play size={18} /></span>
            <div className="thread-main"><strong>{job.type}</strong><span>{job.payload?.prompt || "Generación AK"} · intento {job.attempts || 0}</span></div>
            <time>{formatDate(job.updated_at)}</time>
            <span className={`badge ${job.status === "succeeded" ? "badge-success" : ""}`}>{job.status}</span>
            <span className="inline-actions">
              {(job.status === "failed" || job.status === "cancelled") && <button className="icon-button" title="Reintentar" disabled={retry.isPending} onClick={() => retry.mutate(job.id)}><RotateCcw size={16} /></button>}
              {(job.status === "queued" || job.status === "failed") && <button className="icon-button danger" title="Cancelar" onClick={() => setCancelId(job.id)}><X size={16} /></button>}
              {job.status === "succeeded" && job.result && (/^\/media\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.(?:jpg|jpeg|png|webp|mp3|wav|mp4|md|txt|pdf)$/i.test(String(job.result.url || "")) ? <a className="icon-button" title="Abrir resultado" href={job.result.url} target="_blank" rel="noopener noreferrer"><Download size={16} /></a> : <button className="icon-button" title="Descargar resultado" onClick={() => downloadContent(`ak-${job.id}.json`, JSON.stringify(job.result, null, 2), "application/json")}><Download size={16} /></button>)}
            </span>
            {job.last_error && <small className="error-text">{job.last_error}</small>}
          </div>)}
        </div>
        {!jobs.isLoading && !jobs.error && !jobs.data?.length && <EmptyState icon={<Play />} title="Sin generaciones" description="Envía tu primer trabajo multimedia o documento a la cola." action={<button className="button button-primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> Crear generación</button>} />}
      </section>
      <FormModal open={createOpen} title="Nueva generación" description="El worker procesará el trabajo de forma asíncrona." submitLabel="Enviar a la cola" busy={create.isPending} onClose={() => setCreateOpen(false)} onSubmit={(event) => { event.preventDefault(); if (form.prompt.trim()) create.mutate(); }}>
        <label className="field"><span>Tipo</span><select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>{allowedJobTypes.map((type) => <option value={type.value} key={type.value}>{type.label}</option>)}</select></label>
        <label className="field"><span>Instrucción</span><textarea required rows={7} maxLength={10000} value={form.prompt} onChange={(event) => setForm({ ...form, prompt: event.target.value })} placeholder="Describe el resultado que necesitas…" /></label>
      </FormModal>
      <ConfirmModal open={Boolean(cancelId)} title="Cancelar generación" description="El trabajo dejará la cola. Los trabajos ya en ejecución no se pueden cancelar de forma segura." confirmLabel="Cancelar trabajo" busy={cancel.isPending} onClose={() => setCancelId(null)} onConfirm={() => cancelId && cancel.mutate(cancelId)} />
    </div>
  );
}

function AppShell({ user }: { user: User }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("ak-theme") === "dark");
  const [location] = useLocation();
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("ak-theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => {
    setMobileOpen(false);
    if (window.location.hash) {
      let targetId = window.location.hash.slice(1);
      try { targetId = decodeURIComponent(targetId); } catch { /* keep the literal hash */ }
      document.getElementById(targetId)?.scrollIntoView();
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location]);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const sync = () => { if (media.matches) setCollapsed(false); };
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);
  return (
    <div className={`app-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <Sidebar user={user} collapsed={collapsed} mobileOpen={mobileOpen} onCollapse={() => setCollapsed((value) => !value)} onClose={() => setMobileOpen(false)} />
      <Header user={user} onMobileMenu={() => setMobileOpen(true)} onTheme={() => setDark((value) => !value)} />
      <main className="app-main">
        <Switch>
          <Route path="/dashboard"><Overview /></Route>
          <Route path="/studio"><Studio user={user} /></Route>
          <Route path="/memory/:id"><ThreadDetail /></Route>
          <Route path="/memory"><Threads /></Route>
          <Route path="/threads/:id">{(params) => <Redirect to={`/memory/${params.id}`} />}</Route>
          <Route path="/threads"><Redirect to="/memory" /></Route>
          <Route path="/artifacts"><Artifacts user={user} /></Route>
          <Route path="/jobs"><Jobs user={user} /></Route>
          <Route path="/models"><Models user={user} /></Route>
          <Route path="/connectors"><Connectors user={user} /></Route>
          <Route path="/skills"><Skills user={user} /></Route>
          <Route path="/api-keys"><ApiKeys /></Route>
          <Route path="/settings"><ProviderSettings /></Route>
          <Route path="/operations"><Operations user={user} /></Route>
          <Route path="/usage"><Usage /></Route>
          <Route path="/plans"><Plans user={user} /></Route>
          <Route path="/admin">{user.role === "admin" ? <Admin currentUser={user} /> : <Redirect to="/dashboard" />}</Route>
          <Route><Redirect to="/dashboard" /></Route>
        </Switch>
      </main>
    </div>
  );
}

function AppContent() {
  const queryClient = useQueryClient();
  const [location] = useLocation();
  const isStatusPage = location === "/status";
  const isDocumentationPage = location === "/docs";
  const isLegacyDocumentationPage = location === "/documentation";
  const isPublicPage = isStatusPage || isDocumentationPage || isLegacyDocumentationPage;
  const me = useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/api/v1/auth/me"),
    retry: (failureCount, error) => (
      location !== "/" && error instanceof ApiError && error.status === 401 && failureCount < 1
    ),
    retryDelay: 350,
    staleTime: 30_000,
    enabled: !isPublicPage
  });
  useEffect(() => {
    const expire = () => {
      queryClient.setQueryData(["me"], null);
      queryClient.removeQueries({ predicate: (query) => String(query.queryKey[0]) !== "me" });
    };
    window.addEventListener("ak:auth-expired", expire);
    return () => window.removeEventListener("ak:auth-expired", expire);
  }, [queryClient]);
  if (isStatusPage) return <StatusPage />;
  if (isLegacyDocumentationPage) return <Redirect to="/docs" />;
  if (isDocumentationPage) return <PublicDocumentation />;
  if (me.isLoading) return <div className="boot-screen"><Brand /><div className="boot-loader"><span /></div></div>;
  if (me.error instanceof ApiError && me.error.status === 401) return <Landing />;
  if (me.error) return <div className="fatal-screen"><Brand /><h1>No se pudo abrir AK-v5.0</h1><p>{(me.error as Error).message}</p><button className="button button-primary" onClick={() => window.location.reload()}>Reintentar</button></div>;
  return me.data?.user ? <AppShell user={me.data.user} /> : <Landing />;
}

export default function App() {
  return <ToastProvider><AppContent /></ToastProvider>;
}
